/* Lune Studio 4: one designer for every Lune Girl photo product (sweatshirts, ornaments, mugs, blankets,
   canvases, totes, pillows, phone cases). Upload a photo, remove its background in the browser, add a name,
   see it on the real product, and attach print-ready layers + a Printify spec to the Shopify cart line.
   A product is described by a JSON config (see ls4/CONTRACT.md). No build step: plain ES module.
   ls6 (2026-10-06): the name + line start EMPTY. The config's sample words (text.name.default / text.line.default) are
   only drawn on the preview while no photo of the customer's own is in and they have typed nothing; the print files and
   the cart get exactly what the customer typed (empty fields are left out). A "Printing: ..." line confirms it. */

const CREW_W = 3852, CREW_H = 4398;   // the apparel layout below is authored in Gildan 18000 L+ front px
const MAX_MP = 16.0e6;                // iOS canvas cap (16.7 MP) with a little headroom
const INK = '#2b1419';
const FILES = 'https://lunegirl.com/cdn/shop/files/';

/* ---------- projective helpers (mockup warps) ---------- */
function solve8(A, b) {                 // Gaussian elimination, 8x8
  const n = 8; const M = A.map((r, i) => [...r, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c; for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    [M[c], M[p]] = [M[p], M[c]];
    const d = M[c][c] || 1e-12;
    for (let r = 0; r < n; r++) { if (r === c) continue; const f = M[r][c] / d; for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k]; }
  }
  return M.map((r, i) => r[n] / (r[i] || 1e-12));
}
function homography(src, dst) {         // 3x3 (row-major, h33 = 1) mapping src[i] -> dst[i]
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i], [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  return [...solve8(A, b), 1];
}
function applyH(H, x, y) { const w = H[6] * x + H[7] * y + H[8]; return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w]; }
function invertH(m) {
  const [a, b, c, d, e, f, g, h, i] = m;
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g;
  const det = a * A + b * B + c * C || 1e-12;
  return [A / det, -(b * i - c * h) / det, (b * f - c * e) / det, B / det, (a * i - c * g) / det, -(a * f - c * d) / det, C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
}
/* Draw triangle (s0,s1,s2) of img onto ctx at (d0,d1,d2), slightly inflated so neighbours leave no seams. */
function drawTri(ctx, img, s0, s1, s2, d0, d1, d2) {
  const cx = (d0[0] + d1[0] + d2[0]) / 3, cy = (d0[1] + d1[1] + d2[1]) / 3;
  const inf = (p) => { const dx = p[0] - cx, dy = p[1] - cy, l = Math.hypot(dx, dy) || 1; return [p[0] + dx / l * 0.7, p[1] + dy / l * 0.7]; };
  const D0 = inf(d0), D1 = inf(d1), D2 = inf(d2);
  ctx.save(); ctx.beginPath(); ctx.moveTo(D0[0], D0[1]); ctx.lineTo(D1[0], D1[1]); ctx.lineTo(D2[0], D2[1]); ctx.closePath(); ctx.clip();
  const [x0, y0] = s0, [x1, y1] = s1, [x2, y2] = s2;
  const den = (x0 * (y2 - y1) - x1 * y2 + x2 * y1 + (x1 - x2) * y0) || 1e-9;
  const a = -(y0 * (d2[0] - d1[0]) - y1 * d2[0] + y2 * d1[0] + (y1 - y2) * d0[0]) / den;
  const b = (y1 * d2[1] + y0 * (d1[1] - d2[1]) - y2 * d1[1] + (y2 - y1) * d0[1]) / den;
  const c = (x0 * (d2[0] - d1[0]) - x1 * d2[0] + x2 * d1[0] + (x1 - x2) * d0[0]) / den;
  const d = -(x1 * d2[1] + x0 * (d1[1] - d2[1]) - x2 * d1[1] + (x2 - x1) * d0[1]) / den;
  const e = (x0 * (y2 * d1[0] - y1 * d2[0]) + y0 * (x1 * d2[0] - x2 * d1[0]) + (x2 * y1 - x1 * y2) * d0[0]) / den;
  const f = (x0 * (y2 * d1[1] - y1 * d2[1]) + y0 * (x1 * d2[1] - x2 * d1[1]) + (x2 * y1 - x1 * y2) * d0[1]) / den;
  ctx.transform(a, b, c, d, e, f); ctx.drawImage(img, 0, 0); ctx.restore();
}
const reMatch = (re, v) => { try { return new RegExp(re, 'i').test(String(v ?? '')); } catch { return String(v ?? '').toLowerCase() === String(re).toLowerCase(); } };

const ICONS = {
  photo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3"/><circle cx="9" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/></svg>',
  magic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4V2M15 10V8M11 6h2M17 6h2"/><path d="m3 21 12-12 1.5 1.5L4.5 22.5z"/><path d="M19 13v2M18 14h2M7 3v2M6 4h2"/></svg>',
  zoomIn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2M11 8.5v5M8.5 11h5"/></svg>',
  zoomOut: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="6.5"/><path d="m20 20-4.2-4.2M8.5 11h5"/></svg>',
  rotate: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11a8 8 0 1 0-2.3 5.7"/><path d="M20 4v7h-7"/></svg>',
  heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/></svg>',
};

/* Layout of the print file, in print-area pixels (matches the live Printify design). */
const LAYOUT = {
  photo: { x: 576, y: 132, w: 2700, h: 2700 },
  nameGap: 470,         // photo bottom -> name baseline
  nameSize: 620, nameMaxW: 3150,
  subGap: 330,          // name baseline -> subtitle baseline
  subSize: 150, subMaxW: 2700, subTrack: 0.14,
};

const DEFAULTS = {
  color: 'White',
  name: '',               // the customer's own words only: never pre-filled (whatever is in the fields gets printed)
  subtitle: '',
  sampleName: 'Biscuit',  // preview-only sample words (a product config's text.*.default wins), see shownText()
  sampleSubtitle: 'DOG MOM SINCE 2024',
  confirm: 'auto',        // "Printing: ..." line at the end of the panel, next to Add to cart. 'auto': shown once attachToProductForm()
                          // owns a product form (the homepage phone has none, so it stays as it was); true: always; false: never
  nameMax: 12,
  subtitleMax: 24,
  maxPhotoSide: 2400,     // downscale uploads for speed/memory
  printArea: { x: 0.34521, y: 0.32129, w: 0.31348, h: 0.35791 },
  shirts: {},
  samples: [],
  workerUrl: './ls1-lune-studio-worker.js',
  canvasSize: 1100,
  corners: 0.05,          // photo corner rounding: 0 = square, 1 = fully round; 0.05 ≈ the Printify template (60 px)
  shirtsHD: {},           // optional sharper mockups (e.g. 2048 px), loaded on first close-up
  zoomMax: 3,             // full photo: zoom inside the fixed frame, 1 = fills the frame
  zoomMin: 0.5,           // overlay products: zoom out below 'cover' (or to the whole photo, if that is smaller); the gap is filled
  cutMin: 0.5,            // cut-out: smallest size inside the frame (1 = as big as the frame allows)
  closeUp: 1.4,          // close-up view: window side = print area side × this
  cfg: null,              // product config (ls4/CONTRACT.md); null = legacy crewneck behaviour
  variants: [],           // [{ id, options: { Color: 'White', Size: 'M' } }] from Liquid
  variantId: null,
  assetBase: '/cdn/shop/files/',
};

/* ---------- small helpers ---------- */
const $ = (root, sel) => root.querySelector(sel);
const escHTML = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const isPat = (d) => !!(d && (d.tile || d.color || d.custom));   // background designs: pattern tile, solid colour or the shopper's own colour
const loadImage = (src) => new Promise((res, rej) => {
  const im = new Image(); im.crossOrigin = 'anonymous';
  im.onload = () => res(im); im.onerror = () => rej(new Error('Image failed to load: ' + src)); im.src = src;
});
const canvasToBlob = (c, type = 'image/png', q) => new Promise((res, rej) => c.toBlob((b) => (b && b.size ? res(b) : rej(new Error('encode-failed'))), type, q));
function makeCanvas(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }

async function decodeFile(file, maxSide) {
  let bmp;
  try { bmp = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch (e) {
    // Safari < 17 / odd formats: fall back to <img>
    const url = URL.createObjectURL(file);
    try { bmp = await loadImage(url); } finally { setTimeout(() => URL.revokeObjectURL(url), 2000); }
  }
  const w = bmp.width, h = bmp.height;
  const s = Math.min(1, maxSide / Math.max(w, h));
  const c = makeCanvas(Math.round(w * s), Math.round(h * s));
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  if (bmp.close) bmp.close();
  return c;
}

/* Trim fully transparent borders so the cut-out sits nicely in the photo box. */
function trimAlpha(src) {
  const ctx = src.getContext('2d', { willReadFrequently: true });
  const { width: w, height: h } = src; const d = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (d[(y * w + x) * 4 + 3] > 12) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (x1 < 0) return src;
  const pad = Math.round(Math.max(w, h) * 0.01);
  x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w - 1, x1 + pad); y1 = Math.min(h - 1, y1 + pad);
  const c = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  c.getContext('2d').drawImage(src, x0, y0, c.width, c.height, 0, 0, c.width, c.height);
  return c;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}

function fitText(ctx, text, family, weight, size, maxW, track = 0) {
  let s = size;
  for (let i = 0; i < 40; i++) {
    ctx.font = `${weight} ${s}px ${family}`;
    const w = ctx.measureText(text).width + track * s * Math.max(0, text.length - 1);
    if (w <= maxW) break; s *= 0.94;
  }
  return s;
}

function drawTracked(ctx, text, cx, y, track) {
  if (!track) { ctx.textAlign = 'center'; ctx.fillText(text, cx, y); return; }
  // manual letter spacing (canvas letterSpacing is not in every browser yet)
  ctx.textAlign = 'left';
  const sizes = [...text].map((ch) => ctx.measureText(ch).width);
  const px = parseFloat(ctx.font.match(/(\d+(?:\.\d+)?)px/)[1]);
  const total = sizes.reduce((a, b) => a + b, 0) + track * px * (text.length - 1);
  let x = cx - total / 2;
  [...text].forEach((ch, i) => { ctx.fillText(ch, x, y); x += sizes[i] + track * px; });
}

/* ---------- die-cut silhouettes (shape groups, pw/CONTRACT-PW.md) ----------
   The cut line is computed on a small fixed grid (same grid for the preview and the print file, so what they see is
   what is cut): exact Euclidean distance transforms give round dilation/erosion, interior holes are filled, stray
   specks dropped, and a marching-squares contour of a lightly blurred mask gives one smooth vector outline. */
function dt1(f, n, d, v, z) {           // 1-D squared distance transform (Felzenszwalb & Huttenlocher)
  let k = 0; v[0] = 0; z[0] = -Infinity; z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let p = v[k], s = ((f[q] + q * q) - (f[p] + p * p)) / (2 * q - 2 * p);
    while (s <= z[k]) { k--; p = v[k]; s = ((f[q] + q * q) - (f[p] + p * p)) / (2 * q - 2 * p); }
    k++; v[k] = q; z[k] = s; z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; const r = q - v[k]; d[q] = r * r + f[v[k]]; }
}
function edtSq(on, W, H) {              // squared distance (grid px) from every cell to the nearest cell where on[i] is set
  const n = Math.max(W, H), f = new Float64Array(n), d = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1);
  const g = new Float64Array(W * H);
  for (let i = 0; i < W * H; i++) g[i] = on[i] ? 0 : 1e20;
  for (let x = 0; x < W; x++) { for (let y = 0; y < H; y++) f[y] = g[y * W + x]; dt1(f, H, d, v, z); for (let y = 0; y < H; y++) g[y * W + x] = d[y]; }
  for (let y = 0; y < H; y++) { const o = y * W; for (let x = 0; x < W; x++) f[x] = g[o + x]; dt1(f, W, d, v, z); for (let x = 0; x < W; x++) g[o + x] = d[x]; }
  return g;
}
function fillHoles(S, W, H) {           // background not reachable from the border becomes inside
  const seen = new Uint8Array(W * H), st = new Int32Array(W * H); let sp = 0;
  const push = (i) => { if (!S[i] && !seen[i]) { seen[i] = 1; st[sp++] = i; } };
  for (let x = 0; x < W; x++) { push(x); push((H - 1) * W + x); }
  for (let y = 0; y < H; y++) { push(y * W); push(y * W + W - 1); }
  while (sp) { const i = st[--sp], x = i % W; if (x > 0) push(i - 1); if (x < W - 1) push(i + 1); if (i >= W) push(i - W); if (i < W * (H - 1)) push(i + W); }
  for (let i = 0; i < W * H; i++) if (!S[i] && !seen[i]) S[i] = 1;
}
function keepBig(S, W, H, minFrac = 0) { // drop components smaller than minFrac x the largest (0 = keep only the largest)
  const lab = new Int32Array(W * H), st = new Int32Array(W * H), area = [0]; let n = 0;
  for (let i0 = 0; i0 < W * H; i0++) {
    if (!S[i0] || lab[i0]) continue;
    n++; let sp = 0, a = 0; lab[i0] = n; st[sp++] = i0;
    while (sp) {
      const i = st[--sp], x = i % W; a++;
      if (x > 0 && S[i - 1] && !lab[i - 1]) { lab[i - 1] = n; st[sp++] = i - 1; }
      if (x < W - 1 && S[i + 1] && !lab[i + 1]) { lab[i + 1] = n; st[sp++] = i + 1; }
      if (i >= W && S[i - W] && !lab[i - W]) { lab[i - W] = n; st[sp++] = i - W; }
      if (i < W * (H - 1) && S[i + W] && !lab[i + W]) { lab[i + W] = n; st[sp++] = i + W; }
    }
    area.push(a);
  }
  if (n <= 1) return n;
  let big = 1; for (let l = 2; l <= n; l++) if (area[l] > area[big]) big = l;
  const keep = area.map((a, l) => l === big || (minFrac > 0 && a >= minFrac * area[big]));
  for (let i = 0; i < W * H; i++) if (S[i] && !keep[lab[i]]) S[i] = 0;
  return keep.filter(Boolean).length;
}
function boxBlur(F, W, H, r, passes = 2) {
  const t = new Float32Array(W * H), n = 2 * r + 1;
  for (let p = 0; p < passes; p++) {
    for (let y = 0; y < H; y++) {
      const o = y * W; let s = 0;
      for (let x = -r; x <= r; x++) s += F[o + clamp(x, 0, W - 1)];
      for (let x = 0; x < W; x++) { t[o + x] = s / n; s += F[o + Math.min(W - 1, x + r + 1)] - F[o + Math.max(0, x - r)]; }
    }
    for (let x = 0; x < W; x++) {
      let s = 0;
      for (let y = -r; y <= r; y++) s += t[clamp(y, 0, H - 1) * W + x];
      for (let y = 0; y < H; y++) { F[y * W + x] = s / n; s += t[Math.min(H - 1, y + r + 1) * W + x] - t[Math.max(0, y - r) * W + x]; }
    }
  }
  return F;
}
/* Outer contour of field F (W x H, values 0..1, cell centres at x+0.5) at iso 0.5: the loop with the largest area,
   as a flat [x0, y0, x1, y1, ...] array in grid px (sub-pixel, linear interpolation). */
function contour(F, W, H, iso = 0.5) {
  const GW = W + 2, GH = H + 2, val = (i, j) => (i < 1 || j < 1 || i > W || j > H ? 0 : F[(j - 1) * W + (i - 1)]);
  const NH = GW * GH, link = new Int32Array(NH * 2 * 2).fill(-1), segA = [], segB = [];
  const add = (e1, e2) => {
    const s = segA.length; segA.push(e1); segB.push(e2);
    for (const e of [e1, e2]) { if (link[e * 2] < 0) link[e * 2] = s; else link[e * 2 + 1] = s; }
  };
  for (let j = 0; j < GH - 1; j++) for (let i = 0; i < GW - 1; i++) {
    const tl = val(i, j), tr = val(i + 1, j), br = val(i + 1, j + 1), bl = val(i, j + 1);
    const c = (tl > iso ? 8 : 0) | (tr > iso ? 4 : 0) | (br > iso ? 2 : 0) | (bl > iso ? 1 : 0);
    if (c === 0 || c === 15) continue;
    const T = j * GW + i, B = (j + 1) * GW + i, L = NH + j * GW + i, R = NH + j * GW + i + 1;
    const mid = (tl + tr + br + bl) / 4 > iso;
    switch (c) {
      case 1: case 14: add(L, B); break;
      case 2: case 13: add(B, R); break;
      case 3: case 12: add(L, R); break;
      case 4: case 11: add(T, R); break;
      case 6: case 9: add(T, B); break;
      case 7: case 8: add(L, T); break;
      case 5: if (mid) { add(L, T); add(B, R); } else { add(L, B); add(T, R); } break;
      case 10: if (mid) { add(T, R); add(L, B); } else { add(L, T); add(B, R); } break;
    }
  }
  const pt = (e) => {                   // crossing point on edge e, in grid px (cell centres at +0.5)
    let i, j, a, b, horiz = e < NH; const k = horiz ? e : e - NH; i = k % GW; j = (k / GW) | 0;
    a = val(i, j); b = horiz ? val(i + 1, j) : val(i, j + 1);
    const t = clamp((iso - a) / ((b - a) || 1e-9), 0, 1);
    return horiz ? [i + t - 0.5, j - 0.5] : [i - 0.5, j + t - 0.5];
  };
  const used = new Uint8Array(segA.length); let best = null, bestA = 0;
  for (let s0 = 0; s0 < segA.length; s0++) {
    if (used[s0]) continue;
    const pts = []; let s = s0, e = segA[s0];
    for (let guard = 0; guard < segA.length + 2; guard++) {
      used[s] = 1; const p = pt(e); pts.push(p[0], p[1]);
      e = segA[s] === e ? segB[s] : segA[s];
      const nx = link[e * 2] === s ? link[e * 2 + 1] : link[e * 2];
      if (nx < 0 || used[nx]) break; s = nx;
    }
    let a = 0; for (let q = 0, n = pts.length; q < n; q += 2) { const r = (q + 2) % n; a += pts[q] * pts[r + 1] - pts[r] * pts[q + 1]; }
    if (Math.abs(a) > bestA) { bestA = Math.abs(a); best = pts; }
  }
  return best;
}
let SHAPE_IDS = 0;
/* Preview looks per shape material (view.look overrides any field). thick = visible side wall in mm. */
const SHAPE_MATS = {
  acrylic: { thick: 0.65, wall: 'rgb(214,224,228)', wallA: 0.78, line: '#ffffff', lineA: 0.9, sheen: 1, reflect: 0.16, grain: 0.025, rimHi: 'rgba(255,255,255,0.9)', rimLo: 'rgba(60,70,74,0.32)' },
  glass:   { thick: 0.65, wall: 'rgb(205,222,226)', wallA: 0.75, line: '#ffffff', lineA: 0.95, sheen: 1.3, reflect: 0.26, grain: 0.02, rimHi: 'rgba(255,255,255,0.95)', rimLo: 'rgba(60,70,74,0.32)' },
  frosted: { thick: 0.65, wall: 'rgb(238,240,240)', wallA: 0.92, line: '#ffffff', lineA: 0.6, sheen: 0.35, reflect: 0.05, grain: 0.03, rimHi: 'rgba(255,255,255,0.7)', rimLo: 'rgba(60,70,74,0.22)' },
  holo:    { thick: 0.65, wall: 'rgb(214,224,228)', wallA: 0.78, line: '#ffffff', lineA: 0.9, sheen: 1, reflect: 0.16, grain: 0.02, rimHi: 'rgba(255,255,255,0.9)', rimLo: 'rgba(60,70,74,0.32)', pattern: 'holo', patternA: 0.42 },
  wavy:    { thick: 0.8, wall: 'rgb(214,224,228)', wallA: 0.78, line: '#ffffff', lineA: 0.9, sheen: 1, reflect: 0.2, grain: 0.02, rimHi: 'rgba(255,255,255,0.9)', rimLo: 'rgba(60,70,74,0.32)', pattern: 'wavy', patternA: 0.55 },
  led:     { thick: 0.65, wall: 'rgb(255,236,205)', wallA: 0.85, line: '#fff6e8', lineA: 0.95, sheen: 0.7, reflect: 0.08, grain: 0.02, rimHi: 'rgba(255,240,215,0.95)', rimLo: 'rgba(60,50,40,0.25)', glow: 'rgba(255,186,110,0.55)', led: 'rgba(255,196,120,0.30)' },
  wood:    { thick: 0.8, wall: '#5a3820', wallA: 1, line: '#2e1c10', lineA: 0.7, sheen: 0, reflect: 0, grain: 0.025, rimHi: 'rgba(255,236,210,0.5)', rimLo: 'rgba(60,35,18,0.45)' },
  block:   { thick: 4, wall: 'face', wallTint: 'rgba(232,242,245,0.62)', wallA: 0.95, line: '#ffffff', lineA: 0.95, sheen: 1.1, reflect: 0.18, grain: 0.02, rimHi: 'rgba(255,255,255,0.95)', rimLo: 'rgba(60,70,74,0.35)' },
  felt:    { thick: 0.9, wall: '#cfcac2', wallA: 1, line: null, sheen: 0, reflect: 0, grain: 0.09, inner: 0.22 },
  fabric:  { thick: 0, wall: null, sheen: 0, reflect: 0, grain: 0.03, inner: 0.55 },
};
const hexRGB = (h) => { const m = String(h || '').replace('#', ''); const s = m.length === 3 ? m.split('').map((c) => c + c).join('') : m.padEnd(6, '0'); return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16) || 0); };
const mixRGB = (h, t, to = [255, 255, 255], a = 1) => { const c = hexRGB(h).map((v, i) => Math.round(v + (to[i] - v) * t)); return `rgba(${c[0]},${c[1]},${c[2]},${a})`; };

/* ---------- background-removal engine (Web Workers) ----------
   Two workers: "fast" (U²-Netp, every device) and "hd" (BiRefNet-lite on capable desktops),
   so a slow HD refine never blocks a quick cut-out of the next photo. */
class BgEngine {
  constructor(workerUrl) { this.workerUrl = new URL(workerUrl, import.meta.url).href; this.workers = {}; this.ready = {}; this.seq = 0; this.pending = new Map(); this.onProgress = () => {}; }
  _worker(kind) {
    if (this.workers[kind]) return this.workers[kind];
    // Same-origin module worker when possible; otherwise (files on cdn.shopify.com) a tiny blob worker imports it via CORS.
    const sameOrigin = new URL(this.workerUrl).origin === location.origin;
    const w = sameOrigin ? new Worker(this.workerUrl, { type: 'module' })
      : new Worker(URL.createObjectURL(new Blob([`import ${JSON.stringify(this.workerUrl)};`], { type: 'text/javascript' })), { type: 'module' });
    w.onmessage = (e) => {
      const m = e.data;
      if (m.type === 'progress') { this.onProgress(m); return; }
      const p = this.pending.get(m.id); if (!p) return; this.pending.delete(m.id);
      m.error ? p.reject(new Error(m.error)) : p.resolve(m);
    };
    w.onerror = (e) => {
      if (this.workers[kind] === w) { w.terminate(); delete this.workers[kind]; this.ready[kind] = null; }
      this._fail(kind, new Error(e.message || 'worker error'));
    };
    return (this.workers[kind] = w);
  }
  _fail(kind, err) { for (const [id, p] of this.pending) if (p.kind === kind) { this.pending.delete(id); p.reject(err); } }
  _call(kind, msg, transfer) {
    const w = this._worker(kind); const id = ++this.seq;
    return new Promise((resolve, reject) => { this.pending.set(id, { resolve, reject, kind }); w.postMessage({ ...msg, id }, transfer || []); });
  }
  _load(kind) {
    if (!this.ready[kind]) this.ready[kind] = this._call(kind, { type: 'load', quality: kind }).catch((e) => { this.ready[kind] = null; throw e; });
    return this.ready[kind];
  }
  load() { return this._load('fast'); }
  async canHD() {
    try {
      const n = navigator;
      if (/Macintosh/.test(n.userAgent || '') && (n.maxTouchPoints || 0) > 1) return false;   // iPadOS desktop UA
      if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) return false;   // phones/tablets keep the fast model
      const r = await this.load(); return !!r.hd;
    } catch { return false; }
  }
  /* stop an HD refine that is no longer needed (the photo changed); the model reloads from cache next time */
  cancelHD() {
    const w = this.workers.hd; if (!w) return;
    w.terminate(); delete this.workers.hd; this.ready.hd = null; this._fail('hd', new Error('cancelled'));
  }
  async cutout(photoCanvas, quality = 'fast') {
    const kind = quality === 'hd' ? 'hd' : 'fast';
    await this._load(kind);
    this.onProgress({ type: 'progress', stage: 'run', progress: 0 });
    // the model never needs more than ~1600px; keep the full photo for compositing
    const s = Math.min(1, 1600 / Math.max(photoCanvas.width, photoCanvas.height));
    const src = s < 1 ? (() => { const c = makeCanvas(Math.round(photoCanvas.width * s), Math.round(photoCanvas.height * s)); c.getContext('2d').drawImage(photoCanvas, 0, 0, c.width, c.height); return c; })() : photoCanvas;
    const bmp = await createImageBitmap(src);
    const res = await this._call(kind, { type: 'run', bitmap: bmp, quality: kind }, [bmp]);
    if (src !== photoCanvas) src.width = src.height = 0;
    const cut = makeCanvas(res.w, res.h);
    cut.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(res.rgba.buffer || res.rgba), res.w, res.h), 0, 0);
    let out = cut;
    if (s < 1) {   // upscale the cut onto the full-resolution photo
      const W = photoCanvas.width, H = photoCanvas.height;
      out = makeCanvas(W, H); const o = out.getContext('2d', { willReadFrequently: true });
      o.imageSmoothingQuality = 'high'; o.drawImage(cut, 0, 0, W, H);
      // sharp photo detail inside the subject, cleaned (model-res) colours on the rim
      const up = o.getImageData(0, 0, W, H); const ph = photoCanvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H);
      const u = up.data, p = ph.data;
      for (let i = 0; i < u.length; i += 4) {
        const a = u[i + 3]; if (a < 200) continue;
        const k = Math.min(1, (a - 200) / 55);
        u[i] = u[i] * (1 - k) + p[i] * k; u[i + 1] = u[i + 1] * (1 - k) + p[i + 1] * k; u[i + 2] = u[i + 2] * (1 - k) + p[i + 2] * k;
      }
      o.putImageData(up, 0, 0);
    }
    const trimmed = trimAlpha(out);
    if (out !== cut) cut.width = cut.height = 0;
    if (trimmed !== out) out.width = out.height = 0;
    return { canvas: trimmed, model: res.model, ep: res.ep, ms: res.ms, T: res.T };
  }
}

/* ---------- the studio ---------- */
export class LuneStudio {
  static mount(root, options) { const s = new LuneStudio(root, options); s.init(); return s; }

  constructor(root, options = {}) {
    this.root = root;
    this.o = { ...DEFAULTS, ...options, printArea: { ...DEFAULTS.printArea, ...(options.printArea || {}) } };
    this.state = {
      color: this.o.color, name: this.o.name || '', subtitle: this.o.subtitle || '', typed: !!(this.o.name || this.o.subtitle),
      photo: null, photoName: '', cut: null, useCut: false, removing: false,
      showText: this.o.showText !== false, zoom: 1, ox: 0, oy: 0, cutScale: 1, corners: clamp(this.o.corners, 0, 1), view: 'full',
      rot: 0, pane: 'preview',     // rot: photo turned clockwise (0/90/180/270); pane: preview | layout | edge
    };
    this.sampleText = { name: this.o.sampleName || '', line: this.o.sampleSubtitle || '' };   // preview-only, never printed
    this.shirtImgs = {}; this.shades = {};
    this.cfg = this.o.cfg || null; this.mode = (this.cfg && this.cfg.mode) || 'apparel';
    this.pw = !!(this.cfg && this.cfg.supplier === 'printway');      // Printway: low-res guard, Printway print formats, no Printify layers
    this.cfgShape = !!(this.cfg && this.mode === 'overlay' && (this.cfg.shape || (this.cfg.groups || []).some((g) => g.shape)));
    this.cfgText = !!(this.cfg && this.mode === 'overlay' && (this.cfg.textOnly || (this.cfg.groups || []).some((g) => g.textOnly)));   // embroidered names, no photo
    this.img = {}; this.prep = {};          // image cache by file name; prepared shade/mask canvases by view id
    if (this.cfg) {
      const t = this.cfg.text || {};
      this.o.nameMax = (t.name && t.name.max) || this.o.nameMax; this.o.subtitleMax = (t.line && t.line.max) || this.o.subtitleMax;
      if (t.name && t.name.default != null && options.sampleName == null) this.sampleText.name = String(t.name.default);
      if (t.line && t.line.default != null && options.sampleSubtitle == null) this.sampleText.line = String(t.line.default);
      if (this.cfg.samples) this.o.samples = this.cfg.samples;
      if (this.cfg.designs && this.cfg.designs.length) this.state.design = this.cfg.designs[0].key;
    }
    this.sel = this.resolveGroup(this.o.variantId);
    if (this.cfg && this.mode === 'overlay') {      // keep enough photo pixels for the biggest print window (blankets, canvases)
      const big = Math.max(...this.cfg.groups.map((g) => g.window ? Math.max(g.window.w * g.px[0], g.window.h * g.px[1]) : this.shapeOf(g) ? Math.max(g.px[0], g.px[1]) : 0));
      this.o.maxPhotoSide = Math.round(clamp(big, 2400, 4096));
    }
    if (this.cfg && this.mode === 'apparel' && this.sel) this.state.color = this.sel.key;
    this.touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    this.engine = new BgEngine(this.o.workerUrl);
    this.listeners = [];
    this._raf = 0;
  }

  on(fn) { this.listeners.push(fn); }
  emit() { for (const fn of this.listeners) fn(this.snapshot()); }
  snapshot() { const s = this.state; return { color: s.color, name: s.name, subtitle: s.subtitle, hasPhoto: !!s.photo, backgroundRemoved: !!(s.useCut && s.cut), photoName: s.photoName, showText: s.showText, zoom: s.zoom, ox: s.ox, oy: s.oy, cutScale: s.cutScale, corners: s.corners, design: s.design || null, rot: s.rot || 0 }; }
  /* compact description of the design, saved (hidden) on the order so it can be re-rendered later */
  layout() {
    const s = this.state;
    const g = this.photoGeom(); const cut = this.isCut(); const F = this.frame();
    return { v: 5, text: s.showText, frame: { x: F.x, y: F.y, w: F.w, h: F.h }, radius: cut ? 0 : Math.round(clamp(s.corners, 0, 1) * F.w / 2), corners: Math.round(s.corners * 100),
      zoom: +s.zoom.toFixed(4), ox: Math.round(s.ox), oy: Math.round(s.oy), cutScale: +s.cutScale.toFixed(4),
      drawn: g ? { x: Math.round(g.x), y: Math.round(g.y), w: Math.round(g.dw), h: Math.round(g.dh) } : null, src: g ? { w: g.src.width, h: g.src.height } : null,
      bg: cut ? 'removed' : 'original', cut: cut ? s.cutQuality : null, color: s.color, print: [this.PW, this.PH],
      cfg: this.cfg ? this.cfg.id + '@' + this.cfg.rev : 'crew-legacy', group: this.sel ? this.sel.key : null, variant: this.o.variantId || null, design: this.design() ? this.design().key : null,
      ...(s.rot ? { rot: s.rot } : {}), ...(!cut && s.photo && s.zoom < 1 ? { fill: this.extFill() } : {}),
      ...(this.cfgShape && this.shapeSpec() ? { shape: this.shapeInfo() } : {}), ...(this.pw && s.photo ? { dpi: Math.round(this.effectiveDpi() || 0) } : {}),
      ...(this.cfgText && this.isTextOnly() ? { textOnly: true, font: (this.fontChoice() || {}).key || null, thread: (this.threadChoice() || {}).key || null } : {}) };
  }

  /* ---------- product config: which layout group applies to the selected variant ---------- */
  get PW() { return this.sel && this.sel.px ? this.sel.px[0] : CREW_W; }
  get PH() { return this.sel && this.sel.px ? this.sel.px[1] : CREW_H; }
  get view() { return this.cfg && this.sel && this.cfg.views ? this.cfg.views[this.sel.view] || null : null; }
  asset(name) { return !name ? null : /^(https?:)?\/\//.test(name) || name.startsWith('/') ? name : this.o.assetBase + name; }
  resolveGroup(variantId) {
    const cfg = this.cfg; if (!cfg || !cfg.groups || !cfg.groups.length) return null;
    const v = (this.o.variants || []).find((x) => String(x.id) === String(variantId)) || (this.o.variants || [])[0];
    const opts = (v && v.options) || {};
    return cfg.groups.find((g) => Object.entries(g.match || {}).every(([k, re]) => reMatch(re, opts[k]))) || cfg.groups[0];
  }
  /* Called when the customer picks another variant (colour, size, shape, phone model...). */
  setVariant(id) {
    if (id == null || String(id) === String(this.o.variantId)) return;
    this.o.variantId = id;
    const g = this.resolveGroup(id); if (!g) return;
    const changed = !this.sel || g.key !== this.sel.key; this.sel = g;
    if (this.mode === 'apparel' && this.cfg) this.state.color = g.key;
    if (changed) { this.syncDesignsUI(); this.syncCheckUI(); this.loadSelection().then(() => this.requestRender()); this.requestRender(); this.emitSoon(); }
  }
  /* Load the overlay and the preview view (base photo, shade, mask) for the current group. */
  async loadSelection() {
    const g = this.sel, v = this.view; if (!g) return this.loadAssets();
    const jobs = [];
    const want = (name) => { if (name && !this.img[name]) jobs.push(loadImage(this.asset(name)).then((im) => { this.img[name] = im; }).catch((e) => console.warn(e))); };
    const d = this.design();
    if (this.mode === 'overlay' && !(d && (d.layout || isPat(d)))) want(this.webName(g.overlay));
    if (d && d.tile) want(d.tile);
    if (v) { want(v.src); want(v.shade); want(v.mask); }
    if (v && this.shapeSpec(g)) { (v.extras || []).forEach((e) => want(e && e.src)); want(v.front); }
    if (this.cfgShape) this.syncShapeUI();
    if (this.cfgText) this.syncTextOnlyUI();
    await Promise.all(jobs);
    if (v) this.prepView(g.view, v);      // g.view, not this.sel.view: the customer may have switched variant while loading (would cache the wrong mask)
    if (this.mode === 'apparel' && v) { this.shirtImgs[g.key] = this.img[v.src]; }
  }
  webName(name) { return name ? name.replace(/\.png$/i, '-web.png') : null; }
  /* Mask (white = printed surface) -> alpha canvas; shade (value/200 = factor) -> multiply canvas (capped at 1). */
  prepView(id, v) {
    if (this.prep[id]) return this.prep[id];
    const out = {};
    const base = this.img[v.src]; const W = base ? base.naturalWidth : 1200, H = base ? base.naturalHeight : 1200;
    const toCanvas = (im, fn) => {
      const c = makeCanvas(W, H); const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0, W, H);
      const d = x.getImageData(0, 0, W, H); const a = d.data;
      for (let i = 0; i < a.length; i += 4) fn(a, i);
      x.putImageData(d, 0, 0); return c;
    };
    if (v.mask && this.img[v.mask]) out.mask = toCanvas(this.img[v.mask], (a, i) => { const g = a[i]; a[i] = a[i + 1] = a[i + 2] = 0; a[i + 3] = g; });
    if (v.shade && this.img[v.shade]) out.shade = toCanvas(this.img[v.shade], (a, i) => { const g = Math.min(255, Math.round(a[i] * 255 / 200)); a[i] = a[i + 1] = a[i + 2] = g; a[i + 3] = 255; });
    return (this.prep[id] = out);
  }
  /* Mesh of the view mapping: print-area fractions (u,v) -> view fractions (x,y). */
  viewMesh(v) {
    if (v._mesh) return v._mesh;
    const [x0, y0, x1, y1] = v.region || [0, 0, 1, 1];
    let cols, rows, P;
    if (v.grid && v.grid.pts && v.grid.pts.length) {
      cols = v.grid.cols; rows = v.grid.rows;
      if (v.grid.pts.length !== (cols + 1) * (rows + 1) && v.grid.pts.length === cols * rows) { cols -= 1; rows -= 1; }
      P = v.grid.pts;
    } else {
      const q = v.quad || [[0, 0], [1, 0], [1, 1], [0, 1]];
      const H = homography([[0, 0], [1, 0], [1, 1], [0, 1]], q);
      cols = 10; rows = 10; P = [];
      for (let r = 0; r <= rows; r++) for (let c = 0; c <= cols; c++) P.push(applyH(H, c / cols, r / rows));
      v._H = H; v._Hi = invertH(H);
    }
    const UV = []; for (let r = 0; r <= rows; r++) for (let c = 0; c <= cols; c++) UV.push([x0 + (x1 - x0) * c / cols, y0 + (y1 - y0) * r / rows]);
    if (!v._Hi) {                     // grid: inverse for gestures from its corner quad
      const q = [P[0], P[cols], P[P.length - 1], P[P.length - 1 - cols]];
      v._Hi = invertH(homography([[0, 0], [1, 0], [1, 1], [0, 1]], q));
    }
    return (v._mesh = { cols, rows, P, UV, region: [x0, y0, x1, y1] });
  }

  init() {
    this.root.classList.add('ls');
    if (this.o.split) this.root.classList.add('ls--split');
    this.root.innerHTML = this.template();
    this.canvas = $(this.root, '.ls-canvas');
    this.stage = $(this.root, '.ls-stage');
    this.bindUI(); this.syncShapeControls();
    (this.cfg ? this.loadSelection() : this.loadAssets()).then(() => this.requestRender());
    if (document.fonts && document.fonts.load) {
      Promise.all([document.fonts.load('100px "Pinyon Script"'), document.fonts.load('600 100px Montserrat')]).then(() => this.requestRender());
    }
  }

  template() {
    const o = this.o;
    const samples = o.samples.length ? `<div class="ls-samples"><span class="ls-samples-label">No photo handy? Try:</span>${o.samples.map((s, i) => `<button type="button" class="ls-chip" data-sample="${i}">${s.label}</button>`).join('')}</div>` : '';
    return `
      <div class="ls-stage">
        <canvas class="ls-canvas" aria-label="${(this.cfg && this.cfg.previewLabel) || 'Live preview of your sweatshirt'}"></canvas>
        <span class="ls-badge">Live preview</span>
        <button type="button" class="ls-zoom"><span class="ls-zoom-ico" aria-hidden="true">${ICONS.zoomIn}</span><span class="ls-zoom-text">Close-up</span></button>
        <span class="ls-hint">${this.hintText()}</span>
        <div class="ls-busy"><div class="ls-busy-card"><div class="ls-busy-text">Removing background…</div><div class="ls-bar"><i></i></div></div></div>
        ${this.checkHTML()}${this.lowResHTML()}
      </div>
      <div class="ls-panel">
        <div class="ls-head"><h3 class="ls-title">Make it yours</h3><span class="ls-kicker">Lune Studio</span></div>
        <div class="ls-step">
          <div class="ls-label">1. Your photo <small>JPG or PNG</small></div>
          <label class="ls-drop">
            <input type="file" accept="image/*" class="ls-file">
            <span class="ls-thumb">${ICONS.photo}</span>
            <span class="ls-drop-text"><b class="ls-drop-title">Upload a photo</b><span class="ls-drop-sub">Pets, people, couples. A bright, sharp photo works best.</span></span>
          </label>
          ${samples}
          <div class="ls-error" role="alert"></div>
        </div>
        <div class="ls-step">
          <div class="ls-label">2. Style your photo</div>
          ${this.designsHTML()}
          <button type="button" class="ls-toggle ls-bg" aria-pressed="false" disabled>
            <span class="ls-toggle-ico">${ICONS.magic}</span>
            <span class="ls-toggle-text"><b>Remove background</b><span class="ls-bg-sub">Upload a photo first</span></span>
            <span class="ls-switch" aria-hidden="true"></span>
          </button>
          <div class="ls-range"><span class="ls-range-label ls-size-label">Zoom</span><input type="range" class="ls-scale" min="100" max="${Math.round(o.zoomMax * 100)}" value="100" aria-label="Zoom" disabled><span class="ls-scale-val">100%</span></div>
          ${this.mode === 'overlay' ? `<div class="ls-fitrow" role="group" aria-label="Place your photo"><button type="button" class="ls-fitbtn" data-fit="fit" title="Show the whole photo" disabled>Fit</button><button type="button" class="ls-fitbtn" data-fit="fill" title="Fill the whole area" disabled>Fill</button><button type="button" class="ls-fitbtn" data-fit="rotate" title="Turn the photo 90°" disabled>${ICONS.rotate}<span>Rotate</span></button></div>` : ''}
          <div class="ls-range ls-corners"${this.mode === 'overlay' ? ' hidden style="display:none"' : ''}><span class="ls-range-label">Corners</span><span class="ls-shape ls-shape--sq" aria-hidden="true"></span><input type="range" class="ls-radius" min="0" max="100" value="${Math.round(this.state.corners * 100)}" aria-label="Corner rounding" aria-describedby="ls-corners-note" disabled><span class="ls-shape ls-shape--round" aria-hidden="true"></span><span class="ls-radius-val">${Math.round(this.state.corners * 100)}%</span></div>
          <small class="ls-corners-note" id="ls-corners-note" hidden>Corners apply when you keep the full photo.</small>
        </div>
        <div class="ls-step">
          <div class="ls-label">3. Name &amp; little line
            <button type="button" class="ls-mini-switch ls-text-toggle" aria-pressed="${this.state.showText}" aria-label="Text under the photo"><span class="ls-mini-switch-text">${this.state.showText ? 'On' : 'Off'}</span><span class="ls-switch" aria-hidden="true"></span></button></div>
          <div class="ls-text-fields"${this.state.showText ? '' : ' hidden'}>
            <div class="ls-field"><input class="ls-input ls-name" maxlength="${o.nameMax}" placeholder="${escHTML(this.placeholder('name', 'Name, e.g. Biscuit'))}" value="${escHTML(this.state.name)}" aria-label="Name" autocomplete="off"></div>
            <div class="ls-field"><input class="ls-input ls-sub" maxlength="${o.subtitleMax}" placeholder="${escHTML(this.placeholder('line', 'e.g. DOG MOM SINCE 2024'))}" value="${escHTML(this.state.subtitle)}" aria-label="Line under the name (optional)" autocomplete="off"></div>
          </div>
          <small class="ls-text-note"${this.state.showText ? ' hidden' : ''}>${this.mode === 'overlay' ? 'No text: just your photo.' : 'No text: your photo gets the whole print area.'}</small>
        </div>${this.textOnlyHTML()}
        <p class="ls-note">${ICONS.heart}<span>We check every photo by hand before printing and touch up the edges if needed. What you see is what we print.</span></p>
        ${this.confirmHTML()}
        <div class="ls-ready">Your design is saved with this item.</div>
      </div>`;
  }

  bindUI() {
    const r = this.root;
    const file = $(r, '.ls-file');
    file.addEventListener('change', () => { if (file.files[0]) this.setFile(file.files[0]); file.value = ''; });
    const drop = $(r, '.ls-drop');
    ['dragenter', 'dragover'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
    drop.addEventListener('drop', (e) => { const f = e.dataTransfer.files[0]; if (f) this.setFile(f); });
    r.querySelectorAll('[data-sample]').forEach((b) => b.addEventListener('click', () => this.setSample(+b.dataset.sample)));
    $(r, '.ls-bg').addEventListener('click', () => this.toggleBackground());
    this.scaleInput = $(r, '.ls-scale'); this.scaleVal = $(r, '.ls-scale-val');
    this.scaleInput.addEventListener('input', () => { this.setSize(this.scaleInput.value / 100, { fromSlider: true }); this.emitSoon(); });
    this.radiusInput = $(r, '.ls-radius'); this.radiusVal = $(r, '.ls-radius-val'); this.cornersRow = $(r, '.ls-corners');
    this.radiusInput.addEventListener('input', () => {
      this.state.corners = clamp(this.radiusInput.value / 100, 0, 1); this.radiusVal.textContent = this.radiusInput.value + '%';
      this.radiusInput.setAttribute('aria-valuetext', this.radiusInput.value + '%');
      this.requestRender(); this.emitSoon();
    });
    $(r, '.ls-text-toggle').addEventListener('click', () => this.setShowText(!this.state.showText));
    this.zoomBtn = $(this.stage, '.ls-zoom');
    this.zoomBtn.addEventListener('click', () => { this._viewChosen = true; this.toggleView(); });
    $(r, '.ls-name').addEventListener('input', (e) => { const v = e.target.value; if (v !== this.state.name) this.state.typed = true; this.state.name = v; this.requestRender(); this.emitSoon(); });
    $(r, '.ls-sub').addEventListener('input', (e) => { const v = e.target.value.toUpperCase(); if (v !== this.state.subtitle) this.state.typed = true; this.state.subtitle = v; this.requestRender(); this.emitSoon(); });
    this.bindConfirm();
    r.querySelectorAll('.ls-design').forEach((b) => b.addEventListener('click', () => this.setDesign(b.dataset.key)));
    r.querySelectorAll('.ls-color-input').forEach((inp) => {
      const key = inp.closest('.ls-design').dataset.key;
      const apply = () => { this.state.customColor = inp.value; if (this.state.design !== key) this.setDesign(key); else { this.syncDesignsUI(); this.requestRender(); this.emitSoon(); } };
      inp.addEventListener('input', apply); inp.addEventListener('change', () => { apply(); this.emit(); });
    });
    r.querySelectorAll('.ls-layout').forEach((b) => b.addEventListener('click', () => {
      const ds = this.designs() || [], full = ds.find((x) => x.layout === 'full'), framed = ds.filter((x) => x.layout !== 'full');
      const pick = b.dataset.layout === 'full' ? full : (framed.find((x) => x.key === this._lastFramed) || framed[0]);
      if (pick) this.setDesign(pick.key);
    }));
    this.syncDesignsUI();
    this.bindCheck();
    this.bindGestures();
    window.addEventListener('resize', () => this.requestRender());
    this.bindPinnedPreview();
  }

  /* The one size control: zoom inside the frame (full photo) or size of the cut-out.
     Slider, two-finger pinch and trackpad pinch (Ctrl+wheel / Safari gesture events) all land here. */
  setSize(v, { fromSlider = false, gesture = false } = {}) {
    const st = this.state; const cut = this.isCut();
    if (cut) st.cutScale = clamp(v, this.o.cutMin, 1); else st.zoom = clamp(v, this.zoomMin(), this.o.zoomMax);
    const pct = Math.round((cut ? st.cutScale : st.zoom) * 100);
    if (!fromSlider || Math.abs(this.scaleInput.value - pct) > 0.5) this.scaleInput.value = pct;
    this.scaleVal.textContent = pct + '%'; this.scaleInput.setAttribute('aria-valuetext', pct + '%');
    if (gesture) this.flashHint(`${cut ? 'Size' : 'Zoom'} ${pct}%`);
    this.requestRender();
  }
  sizeValue() { return this.isCut() ? this.state.cutScale : this.state.zoom; }
  /* point the slider at whichever size applies (zoom for the full photo, size for a cut-out) */
  syncSizeSlider() {
    const cut = this.isCut(); const el = this.scaleInput;
    el.min = cut ? Math.round(this.o.cutMin * 100) : Math.floor(this.zoomMin() * 100); el.max = cut ? 100 : Math.round(this.o.zoomMax * 100);
    el.setAttribute('aria-label', cut ? 'Size' : 'Zoom'); $(this.root, '.ls-size-label').textContent = cut ? 'Size' : 'Zoom';
    this.setSize(this.sizeValue());
    $(this.stage, '.ls-hint').textContent = this.hintText();
  }

  /* Corners only shape the full photo; a cut-out has no frame. */
  syncShapeControls() {
    const st = this.state; const off = !st.photo || (st.useCut && !!st.cut);
    this.radiusInput.disabled = off; this.cornersRow.classList.toggle('is-off', off);
    $(this.root, '.ls-corners-note').hidden = this.mode === 'overlay' || !(st.photo && off);
    this.scaleInput.disabled = !st.photo;
    this.root.querySelectorAll('.ls-fitbtn').forEach((b) => { b.disabled = !st.photo || (b.dataset.fit !== 'rotate' && this.isCut()); });
    this.stage.classList.toggle('has-photo', !!st.photo);
  }

  /* Short message on the preview (e.g. "Photo size 120%" while pinching). */
  flashHint(text, ms = 1200) {
    const h = $(this.stage, '.ls-hint'); h.textContent = text; this.stage.classList.add('is-draggable');
    clearTimeout(this._hintT);
    this._hintT = setTimeout(() => { this.stage.classList.remove('is-draggable'); setTimeout(() => { h.textContent = this.hintText(); }, 300); }, ms);
  }
  hintText() {
    if (this.state.pane === 'edge') return 'Drag on the case to adjust';
    if (this.isCut()) return this.touch ? 'Pinch to resize' : 'Ctrl + scroll to resize';
    return this.touch ? 'Drag to adjust · pinch to zoom' : 'Drag to adjust · Ctrl + scroll to zoom';
  }

  toggleView(force) {
    const close = typeof force === 'boolean' ? force : this.state.view !== 'close';
    if (close && this.cfg && this.view && this.view.srcHD && !this.img[this.view.srcHD]) {
      const n = this.view.srcHD; loadImage(this.asset(n)).then((im) => { this.img[n] = im; if (this.mode === 'apparel') (this.shirtImgsHD = this.shirtImgsHD || {})[this.sel.key] = im; this.requestRender(); }).catch(() => {});
    } else if (close && !this.cfg && !this._hdShirts) this._hdShirts = this.loadAssets(this.o.shirtsHD, this.shirtImgsHD = {}).then(() => this.requestRender());
    this.state.view = close ? 'close' : 'full';
    this.stage.classList.toggle('is-close', close);
    $(this.zoomBtn, '.ls-zoom-ico').innerHTML = close ? ICONS.zoomOut : ICONS.zoomIn;
    $(this.zoomBtn, '.ls-zoom-text').textContent = close ? 'Full view' : 'Close-up';
    this.requestRender();
  }

  /* Text on: the name and line sit under the fixed photo frame. Text off: the frame grows to the whole print area. */
  setShowText(on) {
    const st = this.state; st.showText = !!on;
    const t = $(this.root, '.ls-text-toggle'); t.setAttribute('aria-pressed', String(st.showText));
    $(t, '.ls-mini-switch-text').textContent = st.showText ? 'On' : 'Off';
    $(this.root, '.ls-text-fields').hidden = !st.showText; $(this.root, '.ls-text-note').hidden = st.showText;
    if (st.photo) this.syncSizeSlider();              // the frame changed: the zoom-out limit may too
    this.requestRender(); this.emit(); if (st.photo) this.revealStage();
  }

  /* ---------- the words on the product (ls6) ----------
     Drawn = exactly what the customer typed. Only while no photo of their own is in (none yet, or our sample photo) and
     they have typed nothing does the preview borrow the product's sample name + line, so the demo mockup looks finished.
     Print files (and the order's preview image) never borrow: see printing(). */
  demoText() {
    const st = this.state, S = this.sampleText || {};
    return !this._printing && !st.typed && !(st.name || '').trim() && !(st.subtitle || '').trim() && (!st.photo || !!st.sample) && !!((S.name || '').trim() || (S.line || '').trim());
  }
  shownText() {
    const st = this.state;
    if (this.demoText()) return { name: (this.sampleText.name || '').trim(), line: (this.sampleText.line || '').trim().toUpperCase(), sample: true };
    return { name: (st.name || '').trim(), line: (st.subtitle || '').trim().toUpperCase(), sample: false };
  }
  /* run an export with the customer's own words only (nested calls fine) */
  async printing(fn) { this._printing = (this._printing || 0) + 1; try { return await fn(); } finally { this._printing--; } }
  /* empty fields must not look filled in: a bare example ("DOG MOM SINCE 2023") reads "e.g. DOG MOM SINCE 2023" */
  placeholder(key, fallback) {
    const t = (this.cfg && this.cfg.text && this.cfg.text[key]) || {}, p = String(t.placeholder || fallback);
    return /e\.g\.|:|^name\b/i.test(p) ? p : 'e.g. ' + p;
  }
  /* "Printing: “Mochi” · “BEST GIRL SINCE 2024”" / "No name will be printed · Add one", next to Add to cart */
  confirmHTML() {
    if (this.o.confirm === false) return '';
    if (typeof document !== 'undefined' && !document.getElementById('ls-confirm-css')) {
      const css = document.createElement('style'); css.id = 'ls-confirm-css';
      css.textContent = '.ls-confirm{margin:0;padding:10px 12px;border:1px solid #E3DBD3;border-radius:12px;background:#FFFDFB;color:#4E4447;font:inherit;font-size:13px;line-height:1.45;overflow-wrap:anywhere}'
        + '.ls-confirm[hidden]{display:none}.ls-confirm.is-none{background:#ECE6DF}.ls-confirm-k{color:#6F6466}.ls-confirm b{color:#1E1418;font-weight:600}'
        + '.ls-confirm-edit{display:inline;position:relative;margin:0;padding:0 2px;border:0;background:none;font:inherit;font-weight:600;color:#1E1418;text-decoration:underline;text-underline-offset:3px;cursor:pointer;-webkit-tap-highlight-color:transparent}'
        + '.ls-confirm-edit::after{content:"";position:absolute;inset:-12px -6px}.ls-confirm-edit:focus-visible{outline:2px solid #1E1418;outline-offset:2px;border-radius:4px}';
      document.head.appendChild(css);
    }
    return `<p class="ls-confirm"${this.o.confirm === true ? '' : ' hidden'}></p>`;
  }
  bindConfirm() {
    const el = $(this.root, '.ls-confirm'); if (!el) return;
    el.addEventListener('click', (e) => {
      const b = e.target.closest && e.target.closest('[data-ls-edit]'); if (!b) return;
      e.preventDefault();
      if (!this.state.showText) this.setShowText(true);
      const inp = $(this.root, b.dataset.lsEdit === 'line' ? '.ls-sub' : '.ls-name'); if (!inp) return;
      try { inp.focus({ preventScroll: true }); } catch (err) { inp.focus(); }   // focus inside the tap: phones open the keyboard
      const n = inp.value.length; try { inp.setSelectionRange(n, n); } catch (err) { /* not a text input */ }
      inp.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    this.syncConfirm();
  }
  syncConfirm() {
    const el = this.root && $(this.root, '.ls-confirm'); if (!el) return;
    const st = this.state, name = (st.name || '').trim(), line = (st.subtitle || '').trim().toUpperCase();
    const btn = (label, key = 'name') => `<button type="button" class="ls-confirm-edit" data-ls-edit="${key}">${label}</button>`;
    let html, none = false;
    if (!st.showText) { html = '<span>Photo only: no name will be printed</span> · ' + btn('Add one'); none = true; }
    else if (this.demoText()) html = '<span class="ls-confirm-k">The name in the preview is a sample</span> · ' + btn('Add yours');
    else if (!name && !line) { html = '<span>No name will be printed</span> · ' + btn('Add one'); none = true; }
    else {
      html = '<span class="ls-confirm-k">Printing:</span> ' + [name, line].filter(Boolean).map((t) => `<b>“${escHTML(t)}”</b>`).join(' · ')
        + (name ? ' · ' + btn('Edit') : ' · ' + btn('Add a name'));
    }
    if (html === this._confirmSig) return;
    this._confirmSig = html; el.innerHTML = html; el.classList.toggle('is-none', none);
  }

  /* The photo frame is fixed: the Printify template box with text, the whole print area without. */
  frame() {
    const PW = this.PW, PH = this.PH;
    if (this.cfgShape && this.shapeSpec()) return this.shapeLayout().F;     // die-cut: the photo area above the name
    if (this.cfgText && this.isTextOnly()) return { x: 0, y: 0, w: PW, h: PH, cx: PW / 2, cy: PH / 2, shape: 'rect', r: 0 };   // text only: no photo window
    if (this.mode === 'overlay' && this.sel) {
      const d = this.design();
      if (d && (d.layout === 'full' || isPat(d)) && this.isCut()) {     // sticker: the cut-out stands large between the camera and the name
        const w0 = this.sel.window, nb = this.sel.name || { y: 0.76 };
        const y0 = Math.max(0.05, w0.y - 0.06) * PH, y1 = (this.state.showText ? nb.y - 0.012 : 0.95) * PH, x0 = 0.1 * PW;
        return { x: x0, y: y0, w: PW - 2 * x0, h: y1 - y0, cx: PW / 2, cy: (y0 + y1) / 2, shape: 'rect', r: 0 };
      }
      if (d && d.layout === 'full') return { x: 0, y: 0, w: PW, h: PH, cx: PW / 2, cy: PH / 2, shape: 'rect', r: 0 };
      const w = (!this.state.showText && this.sel.textOff && this.sel.textOff.window) || this.sel.window;
      const x = w.x * PW, y = w.y * PH, ww = w.w * PW, hh = w.h * PH;
      return { x, y, w: ww, h: hh, cx: x + ww / 2, cy: y + hh / 2, shape: w.shape || 'rect', r: (w.r || 0) * ww };
    }
    const k = PW / CREW_W;
    const L = this.state.showText ? { x: LAYOUT.photo.x * k, y: LAYOUT.photo.y * k, w: LAYOUT.photo.w * k, h: LAYOUT.photo.h * k } : { x: 0, y: 0, w: PW, h: PH };
    return { x: L.x, y: L.y, w: L.w, h: L.h, cx: L.x + L.w / 2, cy: L.y + L.h / 2 };
  }
  /* Path of the photo window (overlay products: the overlay's hole; apparel: the corner-rounded frame). */
  framePath(ctx, F = this.frame()) {
    if (this.mode === 'overlay') {
      if (F.shape === 'circle' || F.shape === 'ellipse') { ctx.beginPath(); ctx.ellipse(F.cx, F.cy, F.w / 2, F.h / 2, 0, 0, Math.PI * 2); return; }
      if (F.shape === 'heart') { this.heartPath(ctx, F); return; }
      if (F.r > 0.5) { roundRect(ctx, F.x, F.y, F.w, F.h, Math.min(F.r, F.w / 2, F.h / 2)); return; }
      ctx.beginPath(); ctx.rect(F.x, F.y, F.w, F.h); return;
    }
    const r = clamp(this.state.corners, 0, 1) * Math.min(F.w, F.h) / 2;
    if (r < 1) { ctx.beginPath(); ctx.rect(F.x, F.y, F.w, F.h); } else roundRect(ctx, F.x, F.y, F.w, F.h, r);
  }
  heartPath(ctx, F) {
    const { x, y, w, h } = F; ctx.beginPath();
    ctx.moveTo(x + w / 2, y + h * 0.28);
    ctx.bezierCurveTo(x + w / 2, y, x, y, x, y + h * 0.32);
    ctx.bezierCurveTo(x, y + h * 0.62, x + w * 0.4, y + h * 0.8, x + w / 2, y + h);
    ctx.bezierCurveTo(x + w * 0.6, y + h * 0.8, x + w, y + h * 0.62, x + w, y + h * 0.32);
    ctx.bezierCurveTo(x + w, y, x + w / 2, y, x + w / 2, y + h * 0.28); ctx.closePath();
  }
  isCut() { const st = this.state; return !!(st.useCut && st.cut); }

  /* Where the photo is drawn, in print px.
     Full photo: always covers the fixed frame; zoom/pan only choose which part shows (pan is kept in range here).
     Cut-out: fits inside the frame, centred, standing on the frame's bottom edge; it never moves. */
  photoGeom() {
    const st = this.state; if (!st.photo) return null;
    if (this.cfgShape && this.shapeSpec()) return this.shapeLayout().geo;
    const F = this.frame();
    if (this.isCut()) {
      const src = this.rotSrc(st.cut); const fit = Math.min(F.w / src.width, F.h / src.height) * st.cutScale;
      const dw = src.width * fit, dh = src.height * fit;
      const bottom = this.mode === 'overlay' || st.showText;
      return { src, cut: true, x: F.cx - dw / 2, y: bottom ? F.y + F.h - dh : F.cy - dh / 2, dw, dh };
    }
    const src = this.rotSrc(st.photo); st.zoom = Math.max(st.zoom, this.zoomMin());
    const fit = Math.max(F.w / src.width, F.h / src.height) * st.zoom;
    const dw = src.width * fit, dh = src.height * fit, mx = Math.abs(dw - F.w) / 2, my = Math.abs(dh - F.h) / 2;   // zoomed out: the photo slides inside the frame
    st.ox = clamp(st.ox, -mx, mx); st.oy = clamp(st.oy, -my, my);
    return { src, cut: false, x: F.cx - dw / 2 + st.ox, y: F.cy - dh / 2 + st.oy, dw, dh };
  }

  /* Full photo: zoom so the photo point at relative (u, v) sits under print point P (zoom around the fingers). */
  zoomAround(u, v, P, zoom) {
    const st = this.state; const F = this.frame();
    this.setSize(zoom, { gesture: true });
    if (this.isCut()) return;
    const src = this.rotSrc(st.photo); const fit = Math.max(F.w / src.width, F.h / src.height) * st.zoom;
    const dw = src.width * fit, dh = src.height * fit;
    st.ox = P.x - u * dw - (F.cx - dw / 2); st.oy = P.y - v * dh - (F.cy - dh / 2);
    this.photoGeom();                                   // clamps the pan so the frame stays filled
    this.requestRender();
  }

  bindGestures() {
    const c = this.canvas; const pts = new Map(); let g = null; let gs = null;
    const local = (e) => { const b = c.getBoundingClientRect(); return { x: e.clientX - b.left, y: e.clientY - b.top }; };
    const toPrint = (p) => this.cssToPrint(p);
    // photo point under P (clamped onto the photo); null for a cut-out, which only changes size
    const anchor = (P) => {
      if (!P) return null;
      const geo = this.photoGeom(); if (!geo || geo.cut) return null;
      return { u: clamp((P.x - geo.x) / geo.dw, 0, 1), v: clamp((P.y - geo.y) / geo.dh, 0, 1) };
    };
    const onFrame = (p) => {
      const P = this.cssToPrint(p); if (!P) return false; const F = this.frame(); const m = 12 * this.printPerCss(p);
      return P.x >= F.x - m && P.x <= F.x + F.w + m && P.y >= F.y - m && P.y <= F.y + F.h + m;
    };
    // one finger: slide the photo inside the frame (full photo only; a cut-out stays put)
    const startDrag = (id, onlyOnFrame) => {
      const p = pts.get(id);
      g = this.isCut() || (onlyOnFrame && !onFrame(p)) ? { mode: 'idle' }
        : { mode: 'drag', id, x: p.x, y: p.y, ox: this.state.ox, oy: this.state.oy, k: this.printPerCss(p), J: this.state.pane === 'edge' ? this.cssJac(p) : null };
    };
    const startPinch = () => {
      const [p1, p2] = [...pts.values()];
      const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }; const P0 = toPrint(mid);
      g = { mode: 'pinch', d0: Math.max(10, Math.hypot(p2.x - p1.x, p2.y - p1.y)), s0: this.sizeValue(), a: anchor(P0), P0 };
      this.stage.classList.remove('is-draggable');
    };
    // touch-action is pan-y: a swipe that does not grab the photo still scrolls the page;
    // touches that grab it (or pinch) cancel scrolling here (pointerdown runs before touchstart)
    c.addEventListener('touchstart', (e) => { if (g && g.mode !== 'idle') e.preventDefault(); }, { passive: false });
    c.addEventListener('touchmove', (e) => { if (g && g.mode !== 'idle') e.preventDefault(); }, { passive: false });
    c.addEventListener('pointerdown', (e) => {
      if (!this.state.photo || pts.size >= 2) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pts.set(e.pointerId, local(e));
      try { c.setPointerCapture(e.pointerId); } catch (err) { /* not capturable: fine */ }
      if (pts.size === 1) startDrag(e.pointerId, true); else startPinch();
      if (g.mode !== 'idle') e.preventDefault();
    });
    c.addEventListener('pointermove', (e) => {
      if (!pts.has(e.pointerId) || !g) return;
      if (e.pointerType === 'mouse' && !(e.buttons & 1)) { try { c.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ } end(e); return; }  // missed mouseup
      pts.set(e.pointerId, local(e));
      if (g.mode === 'drag' && e.pointerId === g.id) {
        const p = pts.get(g.id);
        const dx = p.x - g.x, dy = p.y - g.y;            // Edge view: the surface is tilted, so map the finger through the local UV Jacobian
        if (g.J) { this.state.ox = g.ox + g.J[0] * dx + g.J[1] * dy; this.state.oy = g.oy + g.J[2] * dx + g.J[3] * dy; }
        else { this.state.ox = g.ox + dx * g.k; this.state.oy = g.oy + dy * g.k; }
        this.photoGeom(); this.requestRender();
      } else if (g.mode === 'pinch' && pts.size === 2) {
        const [p1, p2] = [...pts.values()];
        const d = Math.hypot(p2.x - p1.x, p2.y - p1.y); const want = g.s0 * d / g.d0;
        const Pm = g.a && toPrint({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
        if (Pm) this.zoomAround(g.a.u, g.a.v, Pm, want);
        else this.setSize(want, { gesture: true });
        if (Math.abs(this.sizeValue() - want) > 1e-4) { g.s0 = this.sizeValue(); g.d0 = Math.max(10, d); }   // at a limit: reversing responds at once
      }
    });
    function end(e) {
      if (!pts.delete(e.pointerId)) return;
      if (pts.size === 1) startDrag(pts.keys().next().value, false);   // one finger left: keep sliding, no jump
      else if (pts.size === 0) { const moved = g && g.mode !== 'idle'; g = null; if (moved) self.emitSoon(); }
    }
    const self = this;
    this._regrab = () => { if (g && g.mode === 'drag' && pts.has(g.id)) startDrag(g.id, false); };   // pane changed mid-drag: re-read the mapping, no jump
    c.addEventListener('pointerup', end); c.addEventListener('pointercancel', end); c.addEventListener('lostpointercapture', end);
    c.addEventListener('contextmenu', () => { pts.clear(); g = null; });
    const zoomBy = (P, factor) => {
      const a = anchor(P); const want = this.sizeValue() * factor;
      if (a) this.zoomAround(a.u, a.v, P, want); else this.setSize(want, { gesture: true });
    };
    // desktop: trackpad pinch arrives as Ctrl+wheel (Chrome, Edge, Firefox); a plain wheel still scrolls the page
    c.addEventListener('wheel', (e) => {
      if (!this.state.photo || !e.ctrlKey) return;
      e.preventDefault();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      zoomBy(toPrint(local(e)), Math.exp(-clamp(dy, -30, 30) * 0.01));
      this.emitSoon();
    }, { passive: false });
    // Safari: trackpad pinch arrives as gesture events (on iPhone the pointer handlers above do the pinch)
    c.addEventListener('gesturestart', (e) => {
      if (!this.state.photo) return; e.preventDefault();
      if (pts.size >= 2) return;
      const P = toPrint(local(e)); gs = { s0: this.sizeValue(), a: anchor(P), P };
    });
    c.addEventListener('gesturechange', (e) => {
      if (!this.state.photo) return; e.preventDefault();
      if (!gs || pts.size >= 2) return;
      const want = gs.s0 * e.scale;
      if (gs.a) this.zoomAround(gs.a.u, gs.a.v, gs.P, want); else this.setSize(want, { gesture: true });
      if (Math.abs(this.sizeValue() - want) > 1e-4) gs.s0 = this.sizeValue() / e.scale;
    });
    c.addEventListener('gestureend', () => { if (gs) { gs = null; this.emitSoon(); } });
  }

  async loadAssets(map = this.o.shirts, into = this.shirtImgs) {
    await Promise.all(Object.entries(map || {}).map(async ([color, src]) => {
      try { into[color] = await loadImage(src); } catch (e) { console.warn(e); }
    }));
  }

  setColor(color) { if (this.cfg) return; if (this.state.color === color) return; this.state.color = color; this.requestRender(); this.emitSoon(); }

  async setSample(i) {
    const s = this.o.samples[i]; if (!s) return;
    try { const blob = await (await fetch(s.src)).blob(); await this.setFile(new File([blob], s.file || (s.label + '.jpg'), { type: blob.type || 'image/jpeg' }), s.label); }
    catch (e) { this.showError('Could not load the sample photo.'); }
  }

  async setFile(f, sample = '') {
    this.showError('');
    if (!/^image\//.test(f.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(f.name)) { this.showError('Please choose an image file (JPG or PNG).'); return; }
    if (f.size > 25 * 1024 * 1024) { this.showError('That file is over 25 MB. Please choose a smaller photo.'); return; }
    let canvas;
    try { canvas = await decodeFile(f, this.o.maxPhotoSide); }
    catch (e) { this.showError('We could not read that photo. If it is an iPhone HEIC file, please choose “Most compatible” or upload a JPG.'); return; }
    const st = this.state;
    if (this._hdFor) { this.engine.cancelHD(); this._hdFor = null; }   // free the CPU from a refine nobody needs
    st.photo = canvas; st.sample = sample; st.originalFile = f; st.photoName = f.name; st.cut = null; st.useCut = false; st.zoom = 1; st.ox = 0; st.oy = 0; st.cutScale = 1; st.rot = 0;
    st.lowRes = Math.max(canvas.width, canvas.height) < 900;
    this.fitForDesign();
    const thumb = $(this.root, '.ls-thumb'), tc = makeCanvas(104, 104), tx = tc.getContext('2d'), ts = Math.max(104 / canvas.width, 104 / canvas.height);
    tx.imageSmoothingQuality = 'high'; tx.drawImage(canvas, (104 - canvas.width * ts) / 2, (104 - canvas.height * ts) / 2, canvas.width * ts, canvas.height * ts);
    thumb.style.backgroundImage = `url(${tc.toDataURL('image/jpeg', 0.8)})`; thumb.innerHTML = '';
    $(this.root, '.ls-drop-title').textContent = 'Change photo';
    $(this.root, '.ls-drop-sub').textContent = sample ? 'Sample photo for preview only. Upload your own to order.' : f.name + (st.lowRes ? ' — small photo, may print soft' : '');
    const bg = $(this.root, '.ls-bg'); bg.disabled = false; bg.setAttribute('aria-pressed', 'false');
    $(this.root, '.ls-bg-sub').textContent = 'Cut out your pet or person in one tap';
    this.syncShapeControls(); this.syncSizeSlider();
    this.flashHint(this.hintText(), 6000);
    if (this.touch && !this._viewChosen && this.state.view !== 'close') this.toggleView(true);   // phones: start close-up so the photo and text are readable
    this.requestRender(); this.emit(); this.revealStage();
    this.engine.load().catch(() => {}); // warm up the model while they type
    if (this.cfgShape && this.shapeSpec()) { st.shapeFallback = false; this.toggleBackground(); }   // die-cut: the outline comes from the cut-out, start it now
  }

  /* Phones: the preview is pinned (CSS sticky). Re-render when its size changes, shrink it while typing
     (the keyboard takes half the screen) and add a shadow once it is stuck to the top. */
  bindPinnedPreview() {
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => this.requestRender()).observe(this.canvas);
    let blurT = 0;
    this.root.addEventListener('focusin', (e) => {
      if (!e.target.matches('.ls-input')) return;
      clearTimeout(blurT); this.stage.classList.add('is-typing');
    });
    this.root.addEventListener('focusout', (e) => {
      if (!e.target.matches('.ls-input')) return;
      blurT = setTimeout(() => this.stage.classList.remove('is-typing'), 150);   // moving between the two inputs: no flicker
    });
    let raf = 0;
    const check = () => {
      raf = 0;
      const pinned = getComputedStyle(this.stage).position === 'sticky';
      const top = parseFloat(getComputedStyle(this.stage).top) || 0;
      const stuck = pinned && this.root.contains(this.stage) && this.root.getBoundingClientRect().top < top - 1;
      this.stage.classList.toggle('is-stuck', stuck);
    };
    window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(check); }, { passive: true });
    check();
  }

  /* On phones the preview sits above the controls: bring it into view so the customer sees what changed. */
  revealStage() {
    const r = this.stage.getBoundingClientRect(); const vh = window.innerHeight || 0;
    const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    if (r.height && visible < r.height * 0.6) this.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  async toggleBackground() {
    const st = this.state; if (!st.photo || st.removing) return;
    const btn = $(this.root, '.ls-bg');
    const shape = this.cfgShape && !!this.shapeSpec();
    if (shape && st.useCut && st.cut) return;            // die-cut products always use the cut-out (the tap only retries after a failure)
    this.revealStage();
    if (st.useCut) { st.useCut = false; btn.setAttribute('aria-pressed', 'false'); $(this.root, '.ls-bg-sub').textContent = 'Showing your original photo'; this.syncShapeControls(); this.syncSizeSlider(); this.requestRender(); this.emit(); return; }
    if (!st.cut) {
      st.removing = true; this.busy(true, 'Getting the magic ready…', 0.08);
      this.engine.onProgress = (m) => {
        if (m.stage === 'download' && st.removing) this.busy(true, `Loading background remover… ${Math.round(m.progress * 100)}%`, 0.08 + m.progress * 0.5);
        if (m.stage === 'run' && st.removing) this.busy(true, 'Removing background…', 0.8);
      };
      const photoRef = st.photo;
      try {
        const res = await this.engine.cutout(photoRef, 'fast');
        if (photoRef !== st.photo) { st.removing = false; this.busy(false); return; } // photo changed meanwhile
        st.cut = res.canvas; st.cutInfo = { model: res.model, ep: res.ep, ms: res.ms, T: res.T }; st.cutQuality = 'fast';
      } catch (e) {
        console.error(e);
        if (shape) {
          if (photoRef !== st.photo) { st.removing = false; this.busy(false); return; }
          st.shapeFallback = true; btn.setAttribute('aria-pressed', 'false');
          $(this.root, '.ls-bg-sub').textContent = 'Tap to try the cut-out again';
          this.showError('We could not cut out the outline of this photo automatically, so it is shown as a rounded rectangle with your full photo. Try a photo with a plainer background, or order it like this.');
          st.removing = false; this.busy(false); this.requestRender(); this.emit(); return;
        }
        this.showError('Background removal is not available on this device right now. You can still order with your original photo — we can remove it for you before printing.');
        st.removing = false; this.busy(false); return;
      }
      st.removing = false; this.busy(false);
      this.refineHD(photoRef);
    }
    st.useCut = true; btn.setAttribute('aria-pressed', 'true'); this.syncShapeControls(); this.syncSizeSlider();
    if (shape) { st.shapeFallback = false; this.showError(''); }
    $(this.root, '.ls-bg-sub').textContent = shape ? 'Cut to the outline of your photo' : st.cutQuality === 'hd' ? 'Background removed (HD edges). Tap again to compare.' : 'Background removed. Tap again to compare.';
    this.requestRender(); this.emit();
  }

  /* Upgrade the quick cut-out to the HD model in the background, where the device supports it. */
  async refineHD(photoRef) {
    const st = this.state;
    if (!(await this.engine.canHD())) return;
    if (this._hdFor && this._hdFor !== photoRef) this.engine.cancelHD();
    this._hdFor = photoRef;
    const sub = $(this.root, '.ls-bg-sub');
    if (st.useCut && st.photo === photoRef) sub.textContent = 'Background removed · refining edges in HD…';
    this.engine.onProgress = () => {};
    try {
      const res = await this.engine.cutout(photoRef, 'hd');
      if (this._hdFor === photoRef) this._hdFor = null;
      if (photoRef !== st.photo) return;
      st.cut = res.canvas; st.cutQuality = 'hd'; st.cutInfo = { model: res.model, ep: res.ep, ms: res.ms, T: res.T };
      if (st.useCut) { sub.textContent = this.cfgShape && this.shapeSpec() ? 'Cut to the outline of your photo (HD edges)' : 'Background removed (HD edges). Tap again to compare.'; this.requestRender(); this.emit(); }
    } catch (e) {
      if (this._hdFor === photoRef) this._hdFor = null;
      if (e.message === 'cancelled') return;
      console.warn('HD refine skipped:', e.message);
      if (st.useCut && st.photo === photoRef) sub.textContent = this.cfgShape && this.shapeSpec() ? 'Cut to the outline of your photo' : 'Background removed. Tap again to compare.';
    }
  }

  busy(on, text, p) {
    this.stage.classList.toggle('is-busy', !!on);
    if (text) $(this.stage, '.ls-busy-text').textContent = text;
    if (p != null) $(this.stage, '.ls-bar > i').style.width = Math.round(p * 100) + '%';
  }

  showError(msg) { const el = $(this.root, '.ls-error'); el.textContent = msg; el.classList.toggle('is-on', !!msg); }

  emitSoon() { clearTimeout(this._emitT); this._emitT = setTimeout(() => { this._emitT = 0; this.emit(); }, 250); }
  flushEmit() { if (this._emitT) { clearTimeout(this._emitT); this._emitT = 0; this.emit(); } }

  /* --------- rendering --------- */
  requestRender() { cancelAnimationFrame(this._raf); this._raf = requestAnimationFrame(() => this.render()); }

  /* The part of the preview photo shown, as fractions: the whole photo, or a close-up. */
  viewWindow(mode = this.state.view) {
    if (mode !== 'close') return { x: 0, y: 0, s: 1 };
    const v = this.view;
    if (v && v.closeUp) { const c = v.closeUp; return { x: clamp(c.x, 0, 1 - c.s), y: clamp(c.y, 0, 1 - c.s), s: c.s }; }
    const a = this.printAreaFrac(); const side = Math.min(1, Math.max(a.w, a.h) * this.o.closeUp);
    return { x: clamp(a.x + a.w / 2 - side / 2, 0, 1 - side), y: clamp(a.y + a.h / 2 - side / 2, 0, 1 - side), s: side };
  }
  /* Apparel: the print area as an axis-aligned rect on the shirt photo (the view quad's bounding box). */
  printAreaFrac() {
    const v = this.view;
    if (v && v.quad) { const xs = v.quad.map((p) => p[0]), ys = v.quad.map((p) => p[1]); const x = Math.min(...xs), y = Math.min(...ys); return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y }; }
    return this.o.printArea;
  }
  printRectCss() {
    const b = this.canvas.getBoundingClientRect(); const a = this.printAreaFrac(); const v = this.viewWindow();
    return { x: (a.x - v.x) / v.s * b.width, y: (a.y - v.y) / v.s * b.height, w: a.w / v.s * b.width, h: a.h / v.s * b.height };
  }
  /* Canvas CSS point -> print px (inverse of the preview mapping). */
  cssToPrint(p) {
    const b = this.canvas.getBoundingClientRect(); const vw = this.viewWindow();
    const pane = this.state.pane;
    if (pane === 'layout' && this._lay && this.paneOK('layout')) {   // Print layout: the flat sheet, fitted into the canvas
      const L = this._lay, k = L.size / (b.width || 1);
      return { x: (p.x * k - L.x) / L.w * this.PW, y: (p.y * k - L.y) / L.h * this.PH };
    }
    if (pane === 'edge' && this.paneOK('edge')) {                     // Edge view: read the print point from the UV map; null off the case
      const E = this._edgeView; if (!E) return null; const R = E.R, k = E.size / (b.width || 1);
      const ex = Math.floor((p.x * k - E.x) / E.s), ey = Math.floor((p.y * k - E.y) / E.s);
      if (ex < 0 || ey < 0 || ex >= R.W || ey >= R.H) return null;
      const q = R.kOf[ey * R.W + ex]; if (q < 0 || R.a[q] < 128) return null;
      return { x: R.u[q] * this.PW, y: R.v[q] * this.PH };
    }
    if (this.mode === 'overlay' && this.view) {
      const v = this.view; const m = this.viewMesh(v);
      const fx = vw.x + p.x / b.width * vw.s, fy = vw.y + p.y / b.height * vw.s;
      const [u, w] = applyH(v._Hi, fx, fy);             // 0..1 across the mapped region
      const [x0, y0, x1, y1] = m.region;
      return { x: (x0 + (x1 - x0) * u) * this.PW, y: (y0 + (y1 - y0) * w) * this.PH };
    }
    const r = this.printRectCss(); return { x: (p.x - r.x) * this.PW / r.w, y: (p.y - r.y) * this.PH / r.h };
  }
  printPerCss(p) {
    const a = this.cssToPrint(p), b = this.cssToPrint({ x: p.x + 4, y: p.y });
    if (!a || !b) return this.PH / Math.max(1, this.canvas.getBoundingClientRect().height * 0.8);
    return Math.max(0.01, Math.hypot(b.x - a.x, b.y - a.y) / 4);
  }
  /* d(print)/d(css) at p as [dxdx, dxdy, dydx, dydy]: drag on a tilted surface (Edge view). */
  cssJac(p) {
    const P = this.cssToPrint(p), d = 5, k = this.printPerCss(p);
    if (!P) return [k, 0, 0, k];
    const at = (dx, dy) => { const Q = this.cssToPrint({ x: p.x + dx, y: p.y + dy }); return Q ? [(Q.x - P.x), (Q.y - P.y)] : null; };
    let ax = at(d, 0), sx = d; if (!ax) { ax = at(-d, 0); sx = -d; }
    let ay = at(0, d), sy = d; if (!ay) { ay = at(0, -d); sy = -d; }
    if (!ax || !ay) return [k, 0, 0, k];
    return [ax[0] / sx, ay[0] / sy, ax[1] / sx, ay[1] / sy];
  }

  /* Draw the design in print-area coordinates onto ctx scaled to (w,h) for the whole print area.
     opts.only: 'photo' | 'text' | undefined (everything). opts.placeholder: dashed frame when there is no photo. */
  drawDesign(ctx, w, h, { placeholder = true, only = null, overlayImg, cut = true } = {}) {
    if (this.mode === 'overlay') {
      if (this.cfgText && this.isTextOnly()) return this.drawTextOnlyDesign(ctx, w, h, { only, stitch: cut });
      return this.cfgShape && this.shapeSpec() ? this.drawShapeDesign(ctx, w, h, { placeholder, only, cut }) : this.drawOverlayDesign(ctx, w, h, { placeholder, only, overlayImg });
    }
    const st = this.state; const PW = this.PW, PH = this.PH; const k = PW / CREW_W;
    ctx.save(); ctx.scale(w / PW, h / PH);
    const F = this.frame();
    const photoBottom = F.y + F.h;                      // fixed: the name and line never move
    const geo = this.photoGeom();
    if (only !== 'text') {
      if (geo) {
        if (geo.cut) ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
        else { ctx.save(); this.framePath(ctx, F); ctx.clip(); ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh); ctx.restore(); }
      } else if (placeholder) this.drawPlaceholder(ctx, F);
    }
    if (!st.showText || only === 'photo') { ctx.restore(); return; }
    ctx.fillStyle = (this.sel && this.sel.ink) || (this.cfg && this.cfg.ink) || INK;
    let base = photoBottom + LAYOUT.nameGap * k;
    const T = this.shownText(), name = T.name;
    if (name) {
      const size = fitText(ctx, name, '"Pinyon Script", cursive', '400', LAYOUT.nameSize * k, LAYOUT.nameMaxW * k);
      ctx.font = `400 ${size}px "Pinyon Script", cursive`; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      ctx.fillText(name, PW / 2, base);
    } else { base -= LAYOUT.nameGap * k * 0.55; }
    const sub = T.line;
    if (sub) {
      const size = fitText(ctx, sub, 'Montserrat, sans-serif', '600', LAYOUT.subSize * k, LAYOUT.subMaxW * k, LAYOUT.subTrack);
      ctx.font = `600 ${size}px Montserrat, sans-serif`;
      drawTracked(ctx, sub, PW / 2, base + LAYOUT.subGap * k, LAYOUT.subTrack);
    }
    ctx.restore();
  }
  drawPlaceholder(ctx, F) {
    const u = Math.max(F.w, F.h);
    ctx.save();
    ctx.fillStyle = 'rgba(178,78,108,.07)'; this.framePath(ctx, F); ctx.fill();
    ctx.setLineDash([u * 0.022, u * 0.015]); ctx.lineWidth = Math.max(2, u * 0.005); ctx.strokeStyle = 'rgba(178,78,108,.55)'; ctx.stroke();
    ctx.fillStyle = 'rgba(178,78,108,.8)'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = `600 ${Math.round(Math.min(F.w * 0.075, F.h * 0.09))}px Montserrat, sans-serif`; ctx.fillText('YOUR PHOTO', F.cx, F.cy);
    ctx.restore();
  }
  /* Overlay products: background -> window fill -> photo (clipped to the window) -> overlay art -> name + line. */
  drawOverlayDesign(ctx, w, h, { placeholder = true, only = null, overlayImg } = {}) {
    const st = this.state, g = this.sel, PW = this.PW, PH = this.PH;
    const d = this.design(), full = !!(d && d.layout === 'full'), tile = isPat(d), classic = !(full || tile), sticker = !classic && this.isCut();
    ctx.save(); ctx.scale(w / PW, h / PH);
    const F = this.frame();
    if (only !== 'text') {
      if (!only) {
        ctx.fillStyle = (d && d.bg) || g.bg || '#ffffff'; ctx.fillRect(0, 0, PW, PH);
        if (full && sticker) { ctx.fillStyle = d.stickerBg || '#f2e4d6'; ctx.fillRect(0, 0, PW, PH); }
        if (tile) this.drawTile(ctx, d);
        if (tile && d.border !== false && !sticker) {               // a mat around the photo so it reads on a busy pattern
          const b = d.borderW ? d.borderW * PW : (d.borderMM || 2.4) / 25.4 * (g.dpi || 300);
          ctx.save(); ctx.fillStyle = d.border || '#ffffff'; this.framePath(ctx, { ...F, x: F.x - b, y: F.y - b, w: F.w + 2 * b, h: F.h + 2 * b, r: F.r > 0 ? F.r + b : 0 }); ctx.fill(); ctx.restore();
        }
      }
      const geo = this.photoGeom();
      if (sticker && geo) this.drawSticker(ctx, geo, d);
      else {
        ctx.save(); this.framePath(ctx, F); ctx.clip();
        ctx.fillStyle = (d && d.windowFill) || g.windowFill || g.bg || '#ffffff'; ctx.fillRect(F.x, F.y, F.w, F.h);
        if (geo && !geo.cut && full && (geo.x > F.x + 0.5 || geo.y > F.y + 0.5 || geo.x + geo.dw < F.x + F.w - 0.5 || geo.y + geo.dh < F.y + F.h - 0.5)) this.drawExtension(ctx, geo.src, F, d);   // zoomed out: soft extension of the same photo
        if (geo) ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
        ctx.restore();
      }
      if (!geo && placeholder) this.drawPlaceholder(ctx, F);
      if (!only && classic) { const ov = overlayImg || this.img[this.webName(g.overlay)]; if (ov) ctx.drawImage(ov, 0, 0, PW, PH); }
      if (!only && full && !sticker && st.showText && this.textSpecs().length) {      // soft shade under the name so white text reads on any photo
        const y0 = Math.min(...this.textSpecs().map((t) => t.box.y)) * PH - 0.12 * PH;
        const gr = ctx.createLinearGradient(0, y0, 0, PH); gr.addColorStop(0, 'rgba(0,0,0,0)'); gr.addColorStop(1, 'rgba(0,0,0,' + (d.scrim != null ? d.scrim : 0.38) + ')');
        ctx.fillStyle = gr; ctx.fillRect(0, y0, PW, PH - y0);
      }
    }
    if (st.showText && only !== 'photo') {
      if (tile && d.plate !== false) this.drawPlate(ctx, d);
      this.drawTextBoxes(ctx, sticker && full ? { ...d, ink: d.stickerInk || INK, shadow: false } : d);
    }
    ctx.restore();
  }
  /* Optional designs (cfg.designs, first = default): no layout/tile = the classic overlay art; layout "full" = the photo
     edge to edge with the name on it; tile = a repeating pattern (JPG tile) behind a matted photo window. A group can opt
     out with "designs": false (e.g. clear cases). */
  /* Cut-out as a sticker: white outline (the silhouette grown by r) + soft shadow, then the cut-out itself. */
  drawSticker(ctx, geo, d) {
    const PW = this.PW, r = (d.stickerLine || 0.016) * PW, w = Math.ceil(geo.dw + 2 * r), h = Math.ceil(geo.dh + 2 * r);
    const k = Math.min(1, 1400 / Math.max(w, h));       // outline canvas capped (fine at print size, cheap while dragging)
    const key = [geo.src.width, geo.src.height, Math.round(geo.dw), Math.round(r)].join('x');
    let S = this._stk && this._stk.src === geo.src && this._stk.key === key ? this._stk.c : null;
    if (!S) {
      S = makeCanvas(Math.max(1, Math.round(w * k)), Math.max(1, Math.round(h * k))); const x = S.getContext('2d');
      const n = 24; for (let i = 0; i < n; i++) { const a = i / n * Math.PI * 2; x.drawImage(geo.src, (r + Math.cos(a) * r) * k, (r + Math.sin(a) * r) * k, geo.dw * k, geo.dh * k); }
      x.drawImage(geo.src, r * k, r * k, geo.dw * k, geo.dh * k);
      x.globalCompositeOperation = 'source-in'; x.fillStyle = d.stickerEdge || '#ffffff'; x.fillRect(0, 0, S.width, S.height);
      this._stk = { src: geo.src, key, c: S };
    }
    const t = ctx.getTransform ? Math.abs(ctx.getTransform().a) : 1;
    ctx.save(); ctx.shadowColor = 'rgba(40,20,10,.28)'; ctx.shadowBlur = 0.02 * PW * t; ctx.shadowOffsetY = 0.008 * PW * t;
    ctx.drawImage(S, geo.x - r, geo.y - r, w, h); ctx.restore();
    ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
  }
  designs() { const ds = this.cfg && this.cfg.designs; return ds && ds.length && this.mode === 'overlay' && this.sel && this.sel.designs !== false ? ds : null; }
  design() { const ds = this.designs(); return ds ? ds.find((x) => x.key === this.state.design) || ds[0] : null; }
  setDesign(key) {
    const ds = this.designs(); const d = ds && ds.find((x) => x.key === key); if (!d) return;
    const was = this.design(); this.state.design = d.key; if (d.layout !== 'full') this._lastFramed = d.key; this.syncDesignsUI();
    if (was !== d) { this.fitForDesign(); if (this.scaleInput) this.syncSizeSlider(); }
    const done = () => { this.requestRender(); this.emit(); };
    if (d.tile && !this.img[d.tile]) loadImage(this.asset(d.tile)).then((im) => { this.img[d.tile] = im; done(); }).catch((e) => { console.warn(e); done(); });
    else if (!isPat(d) && !d.layout && this.sel && this.sel.overlay && !this.img[this.webName(this.sel.overlay)]) this.loadSelection().then(done);
    else done();
    if (this.state.photo) this.revealStage();
  }
  /* Edge-to-edge photo: start a little zoomed in and lowered, so faces sit below the camera bump (the customer can still drag). */
  fitForDesign() {
    const st = this.state; if (!st.photo || this.isCut()) return;
    const d = this.design(), F = this.frame();
    if (d && d.layout === 'full') {
      const asp = st.photo.width / st.photo.height;      // wide group photos keep more of the frame; tall pet shots zoom in past the camera
      st.zoom = clamp(d.zoom || (asp >= 1 ? 1 : asp > 0.85 ? 1.2 : 1.45), 1, this.o.zoomMax); st.ox = 0;
      const fit = Math.max(F.w / st.photo.width, F.h / st.photo.height) * st.zoom;
      st.oy = (st.photo.height * fit - F.h) / 2 * (d.drop != null ? d.drop : 1);
    } else { st.zoom = 1; st.ox = 0; st.oy = 0; }
  }
  designsHTML() {
    const ds = this.cfg && this.mode === 'overlay' && this.cfg.designs; if (!ds || !ds.length) return '';
    if (typeof document !== 'undefined' && !document.getElementById('ls-designs-css')) {
      const css = document.createElement('style'); css.id = 'ls-designs-css';
      css.textContent = '.ls-designs{margin:2px 0 16px}.ls-designs-head{display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin:0 0 8px}'
        + '.ls-design-name{font-size:12px;letter-spacing:.02em;opacity:.72}.ls-designs-row{display:flex;flex-wrap:wrap;gap:8px}'
        + '.ls-design{position:relative;width:46px;height:46px;flex:0 0 46px;border-radius:12px;border:1px solid rgba(43,20,25,.16);padding:0;cursor:pointer;background:#faf6f2 center/cover no-repeat;box-shadow:inset 0 0 0 2px rgba(255,255,255,.85);transition:transform .15s ease}'
        + '.ls-design:hover{transform:translateY(-1px)}.ls-design[aria-checked="true"]{outline:2px solid #2b1419;outline-offset:2px}'
        + '.ls-design--classic::after{content:"";position:absolute;left:14px;right:14px;top:10px;bottom:16px;border:1.5px solid #b24e6c;border-radius:5px}'
        + '.ls-design--full{background:linear-gradient(150deg,#e8c9b0,#8a6352)}.ls-design--full svg{position:absolute;inset:12px;width:22px;height:22px;color:#fff}'
        + '.ls-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}'
        + '.ls-layouts{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:0 0 10px}'
        + '.ls-layout{display:flex;align-items:center;gap:10px;min-height:56px;padding:8px 10px;border-radius:14px;border:1px solid rgba(43,20,25,.16);background:#fff;cursor:pointer;text-align:left;font:inherit;color:inherit}'
        + '.ls-layout[aria-pressed="true"]{border-color:#2b1419;box-shadow:inset 0 0 0 1px #2b1419}'
        + '.ls-layout-ico{flex:0 0 26px;width:26px;height:40px;border-radius:7px;position:relative;overflow:hidden}'
        + '.ls-layout-ico--full{background:linear-gradient(160deg,#e8c9b0,#8a6352)}'
        + '.ls-layout-ico--framed{background:repeating-linear-gradient(45deg,#d9b48a 0 4px,#7a4f2a 4px 6px)}.ls-layout-ico--framed::after{content:"";position:absolute;left:5px;right:5px;top:9px;bottom:12px;background:#9cb88a;border:2px solid #fff;border-radius:3px}'
        + '.ls-layout b{display:block;font-size:13px;line-height:1.2}.ls-layout small{display:block;font-size:11px;opacity:.65;line-height:1.25}'
        + '.ls-designs-row[hidden]{display:none}.ls-designs-row--colors{margin-bottom:8px}'
        + '.ls-design--color,.ls-design--custom{border-radius:50%}.ls-design--custom::after{content:"+";position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font:600 20px/1 sans-serif;color:#2b1419;background:conic-gradient(from 0deg,rgba(255,120,120,.55),rgba(255,220,120,.55),rgba(140,220,160,.55),rgba(120,170,255,.55),rgba(220,140,255,.55),rgba(255,120,120,.55));border-radius:50%;opacity:.9}'
        + '.ls-design--custom[aria-checked="true"]::after{background:none;content:""}.ls-color-input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;border:0;padding:0}';
      document.head.appendChild(css);
    }
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const btn = (x) => {
      const kind = x.layout === 'full' ? 'full' : x.custom ? 'custom' : x.color ? 'color' : x.tile ? 'tile' : 'classic';
      const style = kind === 'tile' ? ` style="background-image:url('${this.asset(x.thumb || x.tile)}')"` : kind === 'color' ? ` style="background:${esc(x.color)}"` : kind === 'custom' ? ` style="background:${esc(this.state.customColor || x.color || '#f2c9cf')}"` : '';
      return `<button type="button" class="ls-design ls-design--${kind}" role="radio" aria-checked="${x.key === this.state.design}" data-key="${esc(x.key)}" title="${esc(x.label)}"${style}>${kind === 'full' ? ICONS.photo : ''}${kind === 'custom' ? `<input type="color" class="ls-color-input" value="${esc(this.state.customColor || x.color || '#f2c9cf')}" aria-label="${esc(x.label)}">` : ''}<span class="ls-sr">${esc(x.label)}</span></button>`;
    };
    const full = ds.filter((x) => x.layout === 'full'), framed = ds.filter((x) => x.layout !== 'full');
    const cols = framed.filter((x) => x.color || x.custom), pats = framed.filter((x) => !(x.color || x.custom));
    if (full.length && framed.length) {          // two big choices; the framed one opens its background swatches
      const lay = (k, t, sub) => `<button type="button" class="ls-layout" data-layout="${k}" aria-pressed="false"><span class="ls-layout-ico ls-layout-ico--${k}" aria-hidden="true"></span><span><b>${t}</b><small>${sub}</small></span></button>`;
      return `<div class="ls-designs" aria-label="Design"><div class="ls-designs-head"><span class="ls-range-label">Design</span><span class="ls-design-name"></span></div>`
        + `<div class="ls-layouts">${lay('full', esc(full[0].label || 'Full photo'), 'Your photo edge to edge')}${lay('framed', 'Framed photo', cols.length ? 'Pick a colour or pattern' : 'Pick a pattern around it')}</div>`
        + (cols.length ? `<div class="ls-designs-row ls-designs-row--colors" role="radiogroup" aria-label="Colour">${cols.map(btn).join('')}</div>` : '')
        + `<div class="ls-designs-row" role="radiogroup" aria-label="Pattern">${pats.map(btn).join('')}</div></div>`;
    }
    return `<div class="ls-designs" role="radiogroup" aria-label="Design"><div class="ls-designs-head"><span class="ls-range-label">Design</span><span class="ls-design-name"></span></div>`
      + (cols.length ? `<div class="ls-designs-row ls-designs-row--colors">${cols.map(btn).join('')}</div>` : '') + `<div class="ls-designs-row">${(cols.length ? pats : ds).map(btn).join('')}</div></div>`;
  }
  syncDesignsUI() {
    const box = this.root && this.root.querySelector('.ls-designs'); if (!box) return;
    const d = this.design(); box.hidden = !d; box.style.display = d ? '' : 'none'; if (!d) return;
    box.querySelectorAll('.ls-design').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.key === d.key)));
    const isFull = d.layout === 'full', lays = box.querySelectorAll('.ls-layout');
    lays.forEach((b) => b.setAttribute('aria-pressed', String((b.dataset.layout === 'full') === isFull)));
    if (lays.length) box.querySelectorAll('.ls-designs-row').forEach((row) => { row.hidden = isFull; });
    const cs = box.querySelector('.ls-design--custom'); if (cs && this.state.customColor) cs.style.background = this.state.customColor;
    const n = box.querySelector('.ls-design-name'); if (n) n.textContent = d.label || '';
  }
  drawTile(ctx, d) {
    if (d.color || d.custom) { ctx.fillStyle = d.custom ? (this.state.customColor || d.color || '#f2c9cf') : d.color; ctx.fillRect(0, 0, this.PW, this.PH); return; }
    const im = this.img[d.tile]; if (!im || !im.naturalWidth) return;
    const PW = this.PW, PH = this.PH, tw = d.tileMM ? d.tileMM / 25.4 * ((this.sel && this.sel.dpi) || 300) : (d.tileW || 0.55) * PW, k = tw / im.naturalWidth, th = im.naturalHeight * k;
    const pat = ctx.createPattern(im, 'repeat');
    if (pat && pat.setTransform && typeof DOMMatrix !== 'undefined') {
      pat.setTransform(new DOMMatrix([k, 0, 0, k, ((PW - tw) / 2) % tw, 0])); ctx.fillStyle = pat; ctx.fillRect(0, 0, PW, PH);
    } else for (let y = 0; y < PH; y += th) for (let x = 0; x < PW; x += tw) ctx.drawImage(im, x, y, tw + 1, th + 1);
  }
  /* Label behind the name + line on patterned designs, sized to the text (lines sharing a centre share one label). */
  drawPlate(ctx, d) {
    const L = this.textLayout(ctx); if (!L.length) return;
    const PW = this.PW, PH = this.PH, groups = [];
    for (const t of L) { const g = groups.find((x) => Math.abs(x[0].x - t.x) < 0.04 * PW); if (g) g.push(t); else groups.push([t]); }
    ctx.save(); ctx.fillStyle = d.plate || '#fffdf9'; ctx.globalAlpha = d.plateAlpha != null ? d.plateAlpha : 1;
    for (const g of groups) {
      const wMax = Math.max(...g.map((t) => t.width)), top = Math.min(...g.map((t) => t.y - t.asc)), bot = Math.max(...g.map((t) => t.y + t.desc));
      const pp = this.sel.platePad || d.platePad || [0.9, 0.33];   // optional [x, y] label padding in name-box heights (small faces, e.g. AirPods)
      const hb = ((this.sel.name && this.sel.name.h) || 0.07) * PH, px = pp[0] * hb, py = pp[1] * hb, w = Math.min(PW * 0.9, wMax + 2 * px), h = bot - top + 2 * py;
      roundRect(ctx, g[0].x - w / 2, top - py, w, h, Math.min(0.6 * hb, h / 2)); ctx.fill();
    }
    ctx.restore();
  }
  textSpecs() {
    const st = this.state, g = this.sel, f = (this.cfg && this.cfg.fonts) || {};
    const out = [];
    const T = this.shownText(), name = T.name, line = T.line;
    const SB = this.cfgShape && this.shapeSpec() ? this.shapeLayout().boxes : null;     // die-cut: the text follows the photo
    if (SB && !SB.on) return out;
    const FC = this.cfgText && this.isTextOnly() ? this.fontChoice() : null;          // text only: the shopper's font for the name
    if (name && g.name) out.push(FC ? { box: g.name, text: name, family: FC.family, weight: FC.weight || 400, track: FC.track || 0, script: this.isScript(FC) }
      : { box: SB ? SB.name : g.name, text: name, family: (f.name && f.name.family) || 'Pinyon Script', weight: (f.name && f.name.weight) || 400, track: 0, script: true });
    if (line && g.line) out.push({ box: SB ? SB.line : g.line, text: line, family: (f.line && f.line.family) || 'Montserrat', weight: (f.line && f.line.weight) || 600, track: (f.line && f.line.track != null) ? f.line.track : 0.14 });
    return out;
  }
  /* Text centred in its box, as large as fits (script names a little taller than caps lines). */
  textLayout(ctx) {
    const PW = this.PW, PH = this.PH, out = [];
    ctx.save();
    for (const t of this.textSpecs()) {
      const bx = t.box.x * PW, by = t.box.y * PH, bw = t.box.w * PW, bh = t.box.h * PH;
      const fam = `"${t.family}", ${t.script ? 'cursive' : 'sans-serif'}`;
      let size = fitText(ctx, t.text, fam, t.weight, bh * (t.script ? 0.92 : 0.86), bw * 0.98, t.track);
      ctx.font = `${t.weight} ${size}px ${fam}`; ctx.textBaseline = 'alphabetic';
      const m = ctx.measureText(t.text);
      let asc = m.actualBoundingBoxAscent || size * 0.7, desc = m.actualBoundingBoxDescent || size * 0.2;
      if (asc + desc > bh) { size *= bh / (asc + desc); ctx.font = `${t.weight} ${size}px ${fam}`; const m2 = ctx.measureText(t.text); asc = m2.actualBoundingBoxAscent || size * 0.7; desc = m2.actualBoundingBoxDescent || size * 0.2; }
      const width = ctx.measureText(t.text).width + Math.max(0, t.text.length - 1) * (t.track || 0) * size;
      out.push({ t, fam, size, asc, desc, width, x: bx + bw / 2, y: by + bh / 2 + (asc - desc) / 2 });
    }
    ctx.restore();
    return out;
  }
  drawTextBoxes(ctx, d = this.design()) {
    ctx.save();
    ctx.fillStyle = (d && d.ink) || (this.sel && this.sel.ink) || (this.cfg && this.cfg.ink) || INK;
    if (d && d.shadow) {                                  // shadowBlur ignores the transform: convert print px to device px
      const k = ctx.getTransform ? Math.abs(ctx.getTransform().a) : 1;
      ctx.shadowColor = 'rgba(0,0,0,.38)'; ctx.shadowBlur = 0.012 * this.PW * k; ctx.shadowOffsetY = 0.002 * this.PW * k;
    }
    for (const L of this.textLayout(ctx)) {
      ctx.font = `${L.t.weight} ${L.size}px ${L.fam}`; ctx.textBaseline = 'alphabetic';
      drawTracked(ctx, L.t.text, L.x, L.y, L.t.track);
    }
    ctx.restore();
  }

  shadeFor(img, id, w, h) {
    const key = id + w + 'x' + h; if (this.shades[key]) return this.shades[key];
    if (!img) return null;
    if (Object.keys(this.shades).length > 8) this.shades = {};      // sizes change with resize / close-up
    const a = this.printAreaFrac();
    const sx = a.x * img.naturalWidth, sy = a.y * img.naturalHeight, sw = a.w * img.naturalWidth, sh = a.h * img.naturalHeight;
    const c = makeCanvas(w, h); const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
    const lum = ctx.getImageData(0, 0, w, h).data;
    const small = makeCanvas(Math.max(4, w >> 4), Math.max(4, h >> 4)); const sctx = small.getContext('2d', { willReadFrequently: true });
    sctx.imageSmoothingQuality = 'high'; sctx.drawImage(c, 0, 0, small.width, small.height);
    const blur = makeCanvas(w, h); const bctx = blur.getContext('2d', { willReadFrequently: true });
    bctx.imageSmoothingQuality = 'high'; bctx.drawImage(small, 0, 0, w, h);
    const bl = bctx.getImageData(0, 0, w, h).data;
    const shade = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const L = 0.299 * lum[i * 4] + 0.587 * lum[i * 4 + 1] + 0.114 * lum[i * 4 + 2];
      const B = 0.299 * bl[i * 4] + 0.587 * bl[i * 4 + 1] + 0.114 * bl[i * 4 + 2];
      shade[i] = clamp(1 + (L - B) / 255 * 1.9, 0.78, 1.12);
    }
    c.width = c.height = small.width = small.height = blur.width = blur.height = 0;   // free canvas memory (iOS)
    return (this.shades[key] = shade);
  }

  render() {
    const p = this._printing; this._printing = 0;      // a frame drawn while an export awaits is still the on-screen preview
    try { this.syncConfirm(); this._render(); if (this.pw) this.syncLowRes(); } finally { this._printing = p; }
  }
  _render() {
    const c = this.canvas; const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cssW = c.clientWidth || this.o.canvasSize; const size = Math.round(Math.min(this.o.canvasSize, cssW) * dpr);
    if (c.width !== size) { c.width = size; c.height = size; }
    if (!this._layer) this._layer = makeCanvas(1, 1);        // reused every frame (no per-frame allocations while pinching)
    const pane = this.state.pane;
    if (pane !== 'preview' && this.paneOK(pane)) {
      const ctx = c.getContext('2d'); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, size, size); ctx.imageSmoothingQuality = 'high';
      if (pane === 'layout') this.renderLayout(ctx, size); else this.renderEdge(ctx, size);
      return;
    }
    this.placeLabels(null);
    if (this.mode === 'overlay') this.renderOverlay(c, size, this.viewWindow());
    else this.renderTo(c, size, this.viewWindow(), this._layer);
  }

  /* Overlay products: the real product photo, with the design warped onto its print surface,
     shaded with the photo's own light and clipped to the visible surface (handle, camera hole, ribbon stay in front). */
  renderOverlay(c, size, vw) {
    if (this.cfgShape && this.shapeSpec()) return this.renderShape(c, size, vw);     // die-cut: the cut product in a scene
    if (this.cfgText && this.isTextOnly()) return this.renderTextOnly(c, size, vw);   // text only: thread on the product photo
    const ctx = c.getContext('2d'); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, size, size); ctx.imageSmoothingQuality = 'high';
    const v = this.view;
    const base = v && ((vw.s < 1 && v.srcHD && this.img[v.srcHD]) || this.img[v.src]);
    if (!v || !base) { this.renderFlat(ctx, size); return; }
    const nw = base.naturalWidth, nh = base.naturalHeight;
    ctx.drawImage(base, vw.x * nw, vw.y * nh, vw.s * nw, vw.s * nh, 0, 0, size, size);
    const m = this.viewMesh(v); const [x0, y0, x1, y1] = m.region;
    const toC = (p) => [(p[0] - vw.x) / vw.s * size, (p[1] - vw.y) / vw.s * size];
    const Pc = m.P.map(toC);
    const xs = Pc.map((p) => p[0]), ys = Pc.map((p) => p[1]);
    const bw = Math.max(...xs) - Math.min(...xs), bh = Math.max(...ys) - Math.min(...ys);
    // design layer: just the mapped region, at about on-screen resolution
    const rw = (x1 - x0) * this.PW, rh = (y1 - y0) * this.PH;
    const s = Math.min(1600 / Math.max(rw, rh), Math.max(bw, bh) * 1.15 / Math.max(rw, rh));
    const lw = Math.max(2, Math.round(rw * s)), lh = Math.max(2, Math.round(rh * s));
    const layer = this._layer; if (layer.width !== lw || layer.height !== lh) { layer.width = lw; layer.height = lh; }
    const lctx = layer.getContext('2d'); lctx.setTransform(1, 0, 0, 1, 0, 0); lctx.clearRect(0, 0, lw, lh); lctx.imageSmoothingQuality = 'high';
    const fullW = this.PW * s, fullH = this.PH * s;
    lctx.translate(-x0 * fullW, -y0 * fullH);
    this.drawDesign(lctx, fullW, fullH);
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    // warp onto a scratch canvas, then shade + clip, then onto the photo
    const T = this._warp || (this._warp = makeCanvas(1, 1)); if (T.width !== size) { T.width = size; T.height = size; }
    const t = T.getContext('2d'); t.setTransform(1, 0, 0, 1, 0, 0); t.globalCompositeOperation = 'source-over'; t.clearRect(0, 0, size, size); t.imageSmoothingQuality = 'high';
    const { cols, rows } = m; const L = (c2, r2) => [c2 / cols * lw, r2 / rows * lh];
    for (let r = 0; r < rows; r++) for (let q = 0; q < cols; q++) {
      const i0 = r * (cols + 1) + q, i1 = i0 + 1, i2 = i0 + cols + 1, i3 = i2 + 1;
      drawTri(t, layer, L(q, r), L(q + 1, r), L(q, r + 1), Pc[i0], Pc[i1], Pc[i2]);
      drawTri(t, layer, L(q + 1, r), L(q + 1, r + 1), L(q, r + 1), Pc[i1], Pc[i3], Pc[i2]);
    }
    const prep = this.prep[this.sel.view] || {};
    if (prep.shade) { t.globalCompositeOperation = 'multiply'; t.drawImage(prep.shade, vw.x * prep.shade.width, vw.y * prep.shade.height, vw.s * prep.shade.width, vw.s * prep.shade.height, 0, 0, size, size); }
    // keep only the mapped surface: the warped outline, then the visible-surface mask
    t.globalCompositeOperation = 'destination-in'; t.beginPath();
    const ring = []; for (let q = 0; q <= cols; q++) ring.push(Pc[q]); for (let r = 1; r <= rows; r++) ring.push(Pc[r * (cols + 1) + cols]);
    for (let q = cols - 1; q >= 0; q--) ring.push(Pc[rows * (cols + 1) + q]); for (let r = rows - 1; r >= 1; r--) ring.push(Pc[r * (cols + 1)]);
    ring.forEach((p, i) => (i ? t.lineTo(p[0], p[1]) : t.moveTo(p[0], p[1]))); t.closePath(); t.fillStyle = '#000'; t.fill();
    if (prep.mask) t.drawImage(prep.mask, vw.x * prep.mask.width, vw.y * prep.mask.height, vw.s * prep.mask.width, vw.s * prep.mask.height, 0, 0, size, size);
    t.globalCompositeOperation = 'source-over';
    ctx.drawImage(T, 0, 0);
  }
  /* No mockup yet: the print itself on a soft card. */
  renderFlat(ctx, size) {
    ctx.fillStyle = '#f4efec'; ctx.fillRect(0, 0, size, size);
    const PW = this.PW, PH = this.PH; const s = Math.min(size * 0.86 / PW, size * 0.86 / PH);
    const w = PW * s, h = PH * s, x = (size - w) / 2, y = (size - h) / 2;
    ctx.save(); ctx.shadowColor = 'rgba(43,20,25,.18)'; ctx.shadowBlur = size * 0.03; ctx.shadowOffsetY = size * 0.01; ctx.fillStyle = '#fff'; ctx.fillRect(x, y, w, h); ctx.restore();
    ctx.save(); ctx.translate(x, y); this.drawDesign(ctx, w, h); ctx.restore();
  }

  /* Apparel: shirt photo (or a window of it) + the design with fold shading, onto a square canvas. */
  renderTo(c, size, v, layer = null) {
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingQuality = 'high';
    const color = this.state.color;
    const hd = v.s < 1 && this.shirtImgsHD && this.shirtImgsHD[color];
    const shirt = hd || this.shirtImgs[color];
    if (shirt) {
      const nw = shirt.naturalWidth, nh = shirt.naturalHeight;
      ctx.drawImage(shirt, v.x * nw, v.y * nh, v.s * nw, v.s * nh, 0, 0, size, size);
    } else { ctx.fillStyle = '#f1ece9'; ctx.fillRect(0, 0, size, size); }
    const a = this.printAreaFrac();
    const rx = Math.round((a.x - v.x) / v.s * size), ry = Math.round((a.y - v.y) / v.s * size);
    const rw = Math.round(a.w / v.s * size), rh = Math.round(a.h / v.s * size);
    const own = !layer; if (own) layer = makeCanvas(rw, rh);
    if (layer.width !== rw || layer.height !== rh) { layer.width = rw; layer.height = rh; }
    const lctx = layer.getContext('2d', { willReadFrequently: true });
    lctx.setTransform(1, 0, 0, 1, 0, 0); lctx.clearRect(0, 0, rw, rh); lctx.imageSmoothingQuality = 'high';
    this.drawDesign(lctx, rw, rh);
    // fabric realism: fold shading + a hint of ink absorption
    const shade = shirt ? this.shadeFor(shirt, color + (hd ? '@hd' : ''), rw, rh) : null;
    if (shade) {
      const id = lctx.getImageData(0, 0, rw, rh); const d = id.data;
      for (let i = 0, p = 0; i < d.length; i += 4, p++) {
        if (d[i + 3] === 0) continue;
        const s = shade[p];
        d[i] = Math.min(255, d[i] * s * 0.985); d[i + 1] = Math.min(255, d[i + 1] * s * 0.985); d[i + 2] = Math.min(255, d[i + 2] * s * 0.985);
        d[i + 3] = d[i + 3] * 0.97;
      }
      lctx.putImageData(id, 0, 0);
    }
    ctx.drawImage(layer, rx, ry);
    if (own) layer.width = layer.height = 0;
  }

  /* --------- placement: rotate, zoom out below 'cover', Fit / Fill ---------
     The rotated photo is a cached canvas, so every drawing path (preview, print layout, edge view, exports) uses the
     same pixels: what they see is what prints. */
  rotSrc(src) {
    const r = (((this.state.rot || 0) % 360) + 360) % 360; if (!r || !src) return src;
    const st = this.state, keep = (this._rot || []).filter((e) => (e.src === st.photo || e.src === st.cut) && e.r === r);
    for (const e of this._rot || []) if (!keep.includes(e)) e.c.width = e.c.height = 0;   // free old turns (iOS memory)
    this._rot = keep;
    let e = keep.find((x) => x.src === src);
    if (!e) {
      const w = r % 180 ? src.height : src.width, h = r % 180 ? src.width : src.height;
      const c = makeCanvas(w, h), x = c.getContext('2d');
      x.translate(w / 2, h / 2); x.rotate(r * Math.PI / 180); x.drawImage(src, -src.width / 2, -src.height / 2);
      e = { src, r, c }; keep.push(e);
    }
    return e.c;
  }
  zoomOutOK() { return this.mode === 'overlay' && !!this.state.photo && !this.isCut() && !(this.cfg && this.cfg.zoomOut === false); }
  /* zoom (1 = the photo covers the frame) at which the whole photo just fits inside the frame */
  containZoom() {
    const st = this.state; if (!st.photo) return 1; const F = this.frame(), s = this.rotSrc(st.photo);
    return Math.min(F.w / s.width, F.h / s.height) / Math.max(F.w / s.width, F.h / s.height);
  }
  zoomMin() { return this.zoomOutOK() ? clamp(Math.min(this.o.zoomMin, this.containZoom()), 0.05, 1) : 1; }   // 0.05: Fit shows even a 6:1+ panorama whole
  /* what fills the frame around a zoomed-out photo: 'blur' (Full photo design) or 'window' (the window fill colour) */
  extFill() { const d = this.design(); return d && d.layout === 'full' && !this.isCut() ? 'blur' : 'window'; }
  fitPhoto(kind) {
    const st = this.state; if (!st.photo) return;
    if (kind === 'rotate') {
      st.rot = ((st.rot || 0) + 90) % 360; st.ox = 0; st.oy = 0;
      this.syncSizeSlider(); this.flashHint(`Rotated ${st.rot}°`);
    } else {
      if (this.isCut()) return;
      st.ox = 0; st.oy = 0; this.setSize(kind === 'fit' ? this.containZoom() : 1);
      this.flashHint(kind === 'fit' ? 'Whole photo · drag to place' : 'Filled · drag to place');
    }
    this.requestRender(); this.emit(); this.revealStage();
  }
  /* Soft extension behind a zoomed-out photo: a tiny, box-blurred copy of the same photo scaled up and darkened a little.
     Built from a ~28 px thumbnail, so it looks identical at screen size and at print size. */
  extCanvas(src) {
    if (this._ext && this._ext.src === src && this._ext.c.width) return this._ext.c;
    let c = src, w = src.width, h = src.height;
    while (Math.max(w, h) > 112) {
      w = Math.max(1, Math.round(w / 2)); h = Math.max(1, Math.round(h / 2));
      const n = makeCanvas(w, h), x = n.getContext('2d'); x.imageSmoothingQuality = 'high'; x.drawImage(c, 0, 0, w, h);
      if (c !== src) c.width = c.height = 0; c = n;
    }
    const k = 28 / Math.max(w, h), tw = Math.max(2, Math.round(w * k)), th = Math.max(2, Math.round(h * k));
    const t = makeCanvas(tw, th), tx = t.getContext('2d', { willReadFrequently: true }); tx.imageSmoothingQuality = 'high'; tx.drawImage(c, 0, 0, tw, th);
    if (c !== src) c.width = c.height = 0;
    const id = tx.getImageData(0, 0, tw, th), d = id.data;
    for (let pass = 0; pass < 2; pass++) {             // two 3x3 box blurs (edge pixels clamp)
      const s = new Uint8ClampedArray(d);
      for (let y = 0; y < th; y++) for (let x = 0; x < tw; x++) {
        let r = 0, g = 0, b = 0;
        for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) { const q = (clamp(y + j, 0, th - 1) * tw + clamp(x + i, 0, tw - 1)) * 4; r += s[q]; g += s[q + 1]; b += s[q + 2]; }
        const q = (y * tw + x) * 4; d[q] = r / 9; d[q + 1] = g / 9; d[q + 2] = b / 9; d[q + 3] = 255;
      }
    }
    tx.putImageData(id, 0, 0);
    this._ext = { src, c: t }; return t;
  }
  drawExtension(ctx, src, F, d) {
    const e = this.extCanvas(src), s = Math.max(F.w / e.width, F.h / e.height) * 1.08;
    ctx.save(); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(e, F.cx - e.width * s / 2, F.cy - e.height * s / 2, e.width * s, e.height * s);
    ctx.fillStyle = `rgba(0,0,0,${d && d.extDim != null ? d.extDim : 0.16})`; ctx.fillRect(F.x, F.y, F.w, F.h);
    ctx.restore();
  }

  /* --------- print-check views: Preview | Print layout | Edge view (CONTRACT.md "Print-check views") --------- */
  guideSpec(g = this.sel) {
    if (!g || this.mode !== 'overlay' || !g.guide) return null;
    const o = typeof g.guide === 'string' ? { src: g.guide } : g.guide, src = o && (o.src || o.guide); if (!src) return null;
    return { src, web: o.web || this.webName(src), wrap: o.wrap != null ? !!o.wrap : !!g.wrap, labels: o.labels || g.labels || {} };
  }
  edgeSpec(g = this.sel) {
    if (!g || this.mode !== 'overlay' || !g.edge) return null;
    let e = typeof g.edge === 'string' ? (this.cfg && this.cfg.edges && this.cfg.edges[g.edge]) : g.edge;
    if (e && e.files) e = { ...e, ...e.files };                       // work/checkview/edge.json shape: { files: {base, uvhi, ...} }
    return e && e.base && e.uvhi && e.uvlo ? e : null;
  }
  paneOK(p) { return p === 'layout' ? !!this.guideSpec() : p === 'edge' ? !!this.edgeSpec() : true; }
  checkHTML() {
    if (typeof document !== 'undefined' && !document.getElementById('ls-check-css')) {
      const css = document.createElement('style'); css.id = 'ls-check-css';
      css.textContent = '.ls-checkbar{display:none}.ls-checkbar[hidden]{display:none!important}'
        + '.ls-stage.has-check{aspect-ratio:auto;display:flex;flex-direction:column}'
        + '.ls-stage.has-check>.ls-canvas{width:100%;height:auto;aspect-ratio:1/1;flex:none}'
        + '.ls-stage.has-check .ls-checkbar{display:flex;flex:none;gap:6px;justify-content:center;align-items:center;width:100%;height:56px;padding:6px 8px;background:#f4f1ef;border-top:1px solid rgba(43,20,25,.07)}'
        + '.ls-pane{flex:1 1 0;max-width:150px;min-width:0;min-height:44px;padding:0 8px;border:1px solid rgba(43,20,25,.16);border-radius:999px;background:#fff;color:#2b1419;font:inherit;font-size:12.5px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;cursor:pointer;-webkit-tap-highlight-color:transparent}'
        + '.ls-pane[aria-pressed="true"]{background:#2b1419;border-color:#2b1419;color:#fff}.ls-pane[hidden]{display:none}.ls-pane:focus-visible{outline:2px solid #b24e6c;outline-offset:2px}'
        + '.ls-stage.has-check .ls-hint{bottom:68px}'
        + '.ls-stage.is-pane-layout .ls-hint,.ls-stage.is-pane-edge .ls-hint{top:12px;bottom:auto}'
        + '.ls-stage.is-pane-layout .ls-zoom,.ls-stage.is-pane-edge .ls-zoom,.ls-stage.is-pane-layout .ls-badge,.ls-stage.is-pane-edge .ls-badge{display:none}'
        + '.ls-glabels{position:absolute;left:0;top:0;width:0;height:0;pointer-events:none}.ls-glabels[hidden]{display:none}'
        + '.ls-glabel{position:absolute;transform:translateY(-50%);background:rgba(43,20,25,.88);color:#fff;font-size:11px;line-height:1.2;padding:4px 8px;border-radius:999px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.18)}'
        + '.ls-fitrow{display:flex;gap:8px;margin:6px 0 8px}'
        + '.ls-fitbtn{flex:1 1 0;min-width:0;min-height:44px;display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:0 10px;border:1px solid rgba(43,20,25,.16);border-radius:999px;background:#fff;color:#2b1419;font:inherit;font-size:12.5px;font-weight:500;cursor:pointer;-webkit-tap-highlight-color:transparent}'
        + '.ls-fitbtn svg{width:16px;height:16px;flex:none}.ls-fitbtn:hover:not(:disabled){border-color:#b24e6c}.ls-fitbtn:disabled{opacity:.45;cursor:default}.ls-fitbtn:focus-visible{outline:2px solid #b24e6c;outline-offset:2px}'
        + '@media (max-width:989px){.ls>.ls-stage.has-check{flex-direction:column;align-items:center;justify-content:flex-start;height:calc(min(calc(100vw - 32px),46vh) + 56px)}'
        + '.ls>.ls-stage.has-check>.ls-canvas{width:auto;height:calc(100% - 56px);aspect-ratio:1/1}'
        + '.ls>.ls-stage.has-check.is-typing{height:min(calc(100vw - 32px),30vh)}.ls>.ls-stage.has-check.is-typing>.ls-canvas{height:100%}.ls>.ls-stage.has-check.is-typing .ls-checkbar{display:none}}'
        + '@supports (height:1svh){@media (max-width:989px){.ls>.ls-stage.has-check{height:calc(min(calc(100vw - 32px),46svh) + 56px)}.ls>.ls-stage.has-check.is-typing{height:min(calc(100vw - 32px),30svh)}}}';
      document.head.appendChild(css);
    }
    const b = (k, t) => `<button type="button" class="ls-pane" data-pane="${k}" aria-pressed="${k === 'preview'}">${t}</button>`;
    return `<div class="ls-glabels" hidden></div><div class="ls-checkbar" role="group" aria-label="Check your print" hidden>${b('preview', 'Preview')}${b('layout', 'Print layout')}${b('edge', 'Edge view')}</div>`;
  }
  bindCheck() {
    this.stage.querySelectorAll('.ls-pane').forEach((b) => b.addEventListener('click', () => this.setPane(b.dataset.pane)));
    this.root.querySelectorAll('.ls-fitbtn').forEach((b) => b.addEventListener('click', () => this.fitPhoto(b.dataset.fit)));
    this.syncCheckUI();
  }
  /* show the buttons the selected group has assets for; fall back to Preview when the current one is gone */
  syncCheckUI() {
    const bar = this.stage && $(this.stage, '.ls-checkbar'); if (!bar) return;
    const lay = this.paneOK('layout'), edge = this.paneOK('edge'), on = lay || edge;
    $(bar, '[data-pane="layout"]').hidden = !lay; $(bar, '[data-pane="edge"]').hidden = !edge;
    bar.hidden = !on; this.stage.classList.toggle('has-check', on);
    this.setPane(this.state.pane, true);
  }
  setPane(p, quiet = false) {
    if (!this.paneOK(p)) p = 'preview';
    const st = this.state; st.pane = p; this._labSig = '';
    const lb = $(this.stage, '.ls-glabels'); if (lb && p !== 'layout') lb.hidden = true;
    this.stage.classList.toggle('is-pane-layout', p === 'layout'); this.stage.classList.toggle('is-pane-edge', p === 'edge');
    this.stage.querySelectorAll('.ls-pane').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.pane === p)));
    const base = (this.cfg && this.cfg.previewLabel) || 'Live preview';
    this.canvas.setAttribute('aria-label', p === 'layout' ? 'Print layout: the full print sheet with the cut lines' : p === 'edge' ? 'Edge view: the print wrapping over the edge of the case' : base);
    if (p === 'layout') {
      const G = this.guideSpec();
      const busy = this._guideLoad || (this._guideLoad = new Set());
      if (G && !this.img[G.src] && !busy.has(G.src)) {               // screen copy (-web.png) first, the full guide if there is none
        busy.add(G.src); const done = (im) => { this.img[G.src] = im; this.requestRender(); };
        loadImage(this.asset(G.web)).then(done).catch(() => loadImage(this.asset(G.src)).then(done).catch((e) => { busy.delete(G.src); console.warn(e); }));
      }
    }
    if (p === 'edge') this.loadEdge().then(() => this.requestRender()).catch((e) => { console.warn('Edge view failed:', e); });
    $(this.stage, '.ls-hint').textContent = this.hintText();
    if (!quiet) this.flashHint(p === 'layout' ? 'Exactly what we print · drag to place' : p === 'edge' ? (st.photo ? 'How it wraps over the edge · drag to adjust' : 'How it wraps over the edge') : this.hintText(), 1800);
    this.requestRender();
    if (this._regrab && typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(() => this._regrab()));   // a drag in progress continues with the new view's mapping
  }
  /* Print layout: the whole print sheet (template px) fitted into the canvas, the guide on top, HTML labels. */
  renderLayout(ctx, size) {
    ctx.fillStyle = '#f4efec'; ctx.fillRect(0, 0, size, size);
    const PW = this.PW, PH = this.PH, m = size * 0.035, s = Math.min((size - 2 * m) / PW, (size - 2 * m) / PH);
    const w = PW * s, h = PH * s, x = (size - w) / 2, y = (size - h) / 2;
    ctx.save(); ctx.shadowColor = 'rgba(43,20,25,.16)'; ctx.shadowBlur = size * 0.02; ctx.shadowOffsetY = size * 0.006; ctx.fillStyle = '#fff'; ctx.fillRect(x, y, w, h); ctx.restore();
    ctx.save(); ctx.translate(x, y); ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.clip(); this.drawDesign(ctx, w, h); ctx.restore();
    const G = this.guideSpec(), gi = G && this.img[G.src];
    if (gi) ctx.drawImage(gi, x, y, w, h);
    this._lay = { x, y, w, h, size };
    this.placeLabels(G);
  }
  placeLabels(G) {
    const box = this.stage && $(this.stage, '.ls-glabels'); if (!box) return;
    const L = this._lay, c = this.canvas;
    if (!G || !L || this.state.pane !== 'layout') { if (!box.hidden) { box.hidden = true; this._labSig = ''; } return; }
    const k = c.clientWidth / L.size, ox = c.offsetLeft, oy = c.offsetTop;
    const defs = { wrap: G.wrap ? 'wraps onto the side' : null, camera: 'camera cut-out', safe: 'keep faces & text inside' };
    const want = [];
    for (const key of Object.keys(defs)) {
      let a = G.labels[key]; if (!a) continue; if (Array.isArray(a)) a = { x: a[0], y: a[1] };
      const text = a.text || defs[key]; if (!text) continue;
      want.push({ key, text, x: +a.x || 0, y: +a.y || 0, align: a.align || 'center' });
    }
    const sig = JSON.stringify([want, k, ox, oy, c.clientWidth, L.x, L.y, L.w, L.h]); if (sig === this._labSig) return; this._labSig = sig;
    box.hidden = false;
    box.innerHTML = want.map((w) => `<span class="ls-glabel ls-glabel--${w.key}">${escHTML(w.text)}</span>`).join('');
    [...box.children].forEach((el, i) => {
      const w = want[i], ew = el.offsetWidth, ax = ox + (L.x + w.x * L.w) * k, ay = oy + (L.y + w.y * L.h) * k;
      const left = w.align === 'left' ? ax : w.align === 'right' ? ax - ew : ax - ew / 2;
      el.style.left = clamp(left, ox + 4, ox + c.clientWidth - ew - 4) + 'px'; el.style.top = clamp(ay, oy + 14, oy + c.clientHeight - 14) + 'px';
    });
  }
  /* Edge view assets -> flat arrays, once per edge set: masked pixel offsets, their print (u,v), coverage, shade, gloss. */
  async loadEdge(E = this.edgeSpec()) {
    if (!E) return null; const key = E.base + '|' + E.uvhi;
    this._edges = this._edges || {}; this._edgeDone = this._edgeDone || {};
    if (!this._edges[key]) this._edges[key] = this.decodeEdge(E).then((r) => {
      r.key = key; this._edgeDone[key] = r;
      const cur = this.edgeSpec(), keep = [key, cur && cur.base + '|' + cur.uvhi, this._edgeView && this._edgeView.R.key];   // ~20 MB per set: keep only what is (or was just) on screen (iOS memory)
      for (const k of Object.keys(this._edgeDone)) if (!keep.includes(k)) { const o = this._edgeDone[k]; if (o.out) o.out.width = o.out.height = 0; delete this._edgeDone[k]; delete this._edges[k]; }
      return r;
    }).catch((e) => { delete this._edges[key]; throw e; });
    return this._edges[key];
  }
  async decodeEdge(E) {
    const raw = async (name, exact) => {      // exact: data maps, decoded without colour management or premultiplication
      if (!name) return null; const url = this.asset(name);
      if (exact && typeof createImageBitmap === 'function') {
        try { const b = await (await fetch(url, { mode: 'cors' })).blob(); return await createImageBitmap(b, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' }); } catch (e) { /* fall back to <img> */ }
      }
      return loadImage(url);
    };
    const [base, hi, lo, mask, shade, gloss] = await Promise.all([raw(E.base), raw(E.uvhi, true), raw(E.uvlo, true), raw(E.mask, true), raw(E.shade, true), raw(E.gloss, true)]);
    const W = E.w || base.naturalWidth || base.width, H = E.h || base.naturalHeight || base.height, N = W * H;
    const px = (im) => {
      if (!im) return null; const c = makeCanvas(W, H), x = c.getContext('2d', { willReadFrequently: true });
      x.imageSmoothingEnabled = false; x.drawImage(im, 0, 0, W, H); const d = x.getImageData(0, 0, W, H).data;
      c.width = c.height = 0; if (im.close) im.close(); return d;
    };
    const B = px(base), Hi = px(hi), Lo = px(lo), M = px(mask), S = px(shade), G = px(gloss);
    const cov = M ? (i) => M[i * 4] : (i) => Hi[i * 4 + 2];        // no mask: blue channel of uvhi = coverage
    let n = 0; for (let i = 0; i < N; i++) if (cov(i)) n++;
    const idx = new Int32Array(n), u = new Float32Array(n), v = new Float32Array(n), a = new Uint8Array(n), sh = new Uint16Array(n), gl = G ? new Uint8Array(n) : null;
    const kOf = new Int32Array(N).fill(-1), sc = E.shadeScale || 200;
    for (let i = 0, k = 0; i < N; i++) {
      const cv = cov(i); if (!cv) continue; const q = i * 4;
      idx[k] = q; u[k] = (Hi[q] * 256 + Lo[q]) / 65535; v[k] = (Hi[q + 1] * 256 + Lo[q + 1]) / 65535; a[k] = cv;
      sh[k] = S ? Math.min(65535, Math.round(S[q] * 256 / sc)) : 256; if (gl) gl[k] = G[q]; kOf[i] = k; k++;
    }
    const out = makeCanvas(W, H), img = out.getContext('2d').createImageData(W, H);
    return { W, H, base: B, n, idx, u, v, a, sh, gl, kOf, out, img, bg: E.bg || `rgb(${B[0]},${B[1]},${B[2]})`, samp: null };
  }
  /* Edge view: per-pixel remap of the CURRENT design (the same drawDesign as the print file) through the UV maps,
     times the render's light, over the blank render. Runs once per animation frame while dragging. */
  renderEdge(ctx, size) {
    const E = this.edgeSpec(), key = E && E.base + '|' + E.uvhi, R = this._edgeDone && this._edgeDone[key];
    if (!R) {
      this._edgeView = null; this.renderFlat(ctx, size);
      ctx.fillStyle = 'rgba(43,20,25,.7)'; ctx.font = `600 ${Math.round(size * 0.032)}px Montserrat, sans-serif`; ctx.textAlign = 'center'; ctx.fillText('Loading the edge view…', size / 2, size * 0.96);
      return;
    }
    const PW = this.PW, PH = this.PH, dh = Math.round(E.designH || (this.touch ? 720 : 900)), dw = Math.max(2, Math.round(dh * PW / PH));
    const D = this._edgeD || (this._edgeD = makeCanvas(1, 1)); if (D.width !== dw || D.height !== dh) { D.width = dw; D.height = dh; this._edgeDx = null; }
    const dx = this._edgeDx || (this._edgeDx = D.getContext('2d', { willReadFrequently: true }));
    dx.setTransform(1, 0, 0, 1, 0, 0); dx.clearRect(0, 0, dw, dh); dx.imageSmoothingQuality = 'high';
    this.drawDesign(dx, dw, dh);
    const src = dx.getImageData(0, 0, dw, dh).data;
    if (!R.samp || R.samp.dw !== dw || R.samp.dh !== dh) {        // bilinear sample positions, precomputed once per design size
      const n = R.n, i00 = new Int32Array(n), fx = new Uint8Array(n), fy = new Uint8Array(n);
      for (let k = 0; k < n; k++) {
        const x = clamp(R.u[k] * dw - 0.5, 0, dw - 1.001), y = clamp(R.v[k] * dh - 0.5, 0, dh - 1.001), xi = x | 0, yi = y | 0;
        i00[k] = (yi * dw + xi) * 4; fx[k] = Math.round((x - xi) * 255); fy[k] = Math.round((y - yi) * 255);
      }
      R.samp = { dw, dh, i00, fx, fy };
    }
    const O = R.img.data; O.set(R.base);
    const { i00, fx, fy } = R.samp, row = dw * 4, idx = R.idx, A = R.a, SH = R.sh, GL = R.gl;
    for (let k = 0, n = R.n; k < n; k++) {
      const i = i00[k], j = i + row, wx = fx[k], wy = fy[k], ax = 255 - wx, ay = 255 - wy;
      const s = SH[k] / (256 * 65025), w00 = ax * ay * s, w10 = wx * ay * s, w01 = ax * wy * s, w11 = wx * wy * s, al = A[k] / 255, q = idx[k], g = GL ? GL[k] / 255 : 0;
      let r = src[i] * w00 + src[i + 4] * w10 + src[j] * w01 + src[j + 4] * w11;
      let gg = src[i + 1] * w00 + src[i + 5] * w10 + src[j + 1] * w01 + src[j + 5] * w11;
      let b = src[i + 2] * w00 + src[i + 6] * w10 + src[j + 2] * w01 + src[j + 6] * w11;
      if (g) { r += (255 - Math.min(255, r)) * g; gg += (255 - Math.min(255, gg)) * g; b += (255 - Math.min(255, b)) * g; }
      O[q] += (r - O[q]) * al; O[q + 1] += (gg - O[q + 1]) * al; O[q + 2] += (b - O[q + 2]) * al;
    }
    R.out.getContext('2d').putImageData(R.img, 0, 0);
    ctx.fillStyle = R.bg; ctx.fillRect(0, 0, size, size);
    const s = Math.min(size / R.W, size / R.H), w = R.W * s, h = R.H * s, x = (size - w) / 2, y = (size - h) / 2;
    ctx.drawImage(R.out, x, y, w, h);
    this._edgeView = { R, x, y, s, size };
  }

  /* =========== Printway products (pw/CONTRACT-PW.md): die-cut shapes, text-only embroidery, low-res guard ===========
     Everything below is opt-in: configs without `shape`, `textOnly` or `supplier: "printway"` never reach it. */
  shapeOf(g) {
    if (!this.cfg || this.mode !== 'overlay' || !g) return null;
    const s = Object.prototype.hasOwnProperty.call(g, 'shape') ? g.shape : this.cfg.shape;
    return s && s.kind === 'diecut' ? s : null;
  }
  shapeSpec(g = this.sel) { return this.cfgShape ? this.shapeOf(g) : null; }
  /* Geometry of the die-cut group in print px (border, bleed, smoothing, hole). */
  shapeParams(g = this.sel) {
    if (this._shpP && this._shpP.g === g) return this._shpP.P;
    const s = this.shapeOf(g); if (!s) return null;
    const PW = g.px[0], dpi = g.dpi || 300, mm = dpi / 25.4, material = s.material || 'acrylic';
    const b = s.border != null ? s.border * PW : 3 * mm, bleed = s.bleed != null ? s.bleed * PW : 1 * mm;
    const h = s.hole && typeof s.hole === 'object' ? s.hole : null;
    const hole = h ? { d: h.d != null ? h.d * PW : 4 * mm, ring: h.ring != null ? h.ring * PW : b, at: h.at || 'top' } : null;
    const gap = 0.5 * b, PH = g.px[1], safe = g.printway && g.printway.safe_px;            // Printway safe box: the whole cut stays inside it
    const mx = safe ? Math.max(2, (PW - safe[0]) / 2) : bleed + Math.max(2, 0.004 * PW), my = safe ? Math.max(2, (PH - safe[1]) / 2) : bleed + Math.max(2, 0.004 * PH);
    const base = s.base && s.base.w ? { w: s.base.w * PW, h: s.base.h * PH } : null;   // straight bar / plug at the bottom (LED base, plaque stand)
    const P = { s, PW, PH, dpi, mm, material, b, bleed, c: s.smooth != null ? s.smooth * PW : 1.5 * b, hole, gap, m: Math.min(mx, my), mx, my, base,
      tres: hole ? Math.max(0, gap + hole.d + hole.ring - b) : 0,
      R: s.res || 512,
      edge: s.edge || (material === 'wood' ? '#d6ad7f' : '#ffffff'),
      baseInk: s.baseInk != null ? !!s.baseInk : material !== 'wood',
      text: s.text || 'below', alpha: s.alphaCut != null ? s.alphaCut : 0.5, fallbackR: s.fallbackR != null ? s.fallbackR : 0.14 };
    this._shpP = { g, P }; return P;
  }
  /* Layout inside the square: [hole tab] + photo (cut-out fitted, or the full photo in a rounded rectangle when the
     cut-out failed) + name/line below, centred as one block. Text boxes keep the config's w/h; their y follows the photo. */
  shapeLayout() {
    const g = this.sel, st = this.state, P = this.shapeParams(g), PW = this.PW, PH = this.PH;
    const T = this.shownText(), on = P.text !== 'none' && st.showText;
    const hasName = on && !!T.name && !!g.name, hasLine = on && !!T.line && !!g.line;
    const nH = hasName ? g.name.h * PH : 0, lH = hasLine ? g.line.h * PH : 0;
    const lg = hasName && hasLine ? Math.max(0, g.line.y - g.name.y - g.name.h) * PH : 0;
    const Ht = nH + lg + lH, tg = Ht ? (P.s.textGap != null ? P.s.textGap * PH : 0.006 * PH) : 0;
    const top = P.my + P.b, left = P.mx + P.b, right = PW - P.mx - P.b;
    const bot = (P.base ? PH - P.my - P.base.h - 0.8 * P.b : PH - P.my - P.b) - (Ht ? 0.35 * P.b : 0);   // the name plate's padding + border stay inside the safe box
    const fy = top + P.tres, fh = Math.max(1, bot - fy - Ht - tg), cut = this.isCut();
    const F = { x: left, y: fy, w: right - left, h: fh, cx: PW / 2, cy: fy + fh / 2, shape: 'rect', r: cut ? 0 : P.fallbackR * Math.min(right - left, fh) };
    let geo = null, ph = fh;
    if (st.photo && cut) {
      const src = this.rotSrc(st.cut), fit = Math.min(F.w / src.width, F.h / src.height) * st.cutScale;
      const dw = src.width * fit, dh = src.height * fit; ph = dh;
      geo = { src, cut: true, x: F.cx - dw / 2, y: 0, dw, dh };
    } else if (st.photo) {                       // full photo (cut-out failed / still running): covers the rounded frame, zoom + pan as usual
      const src = this.rotSrc(st.photo), sw = src.width, sh = src.height;
      const cz = Math.min(F.w / sw, F.h / sh) / Math.max(F.w / sw, F.h / sh);
      st.zoom = Math.max(st.zoom, this.cfg.zoomOut === false ? 1 : clamp(Math.min(this.o.zoomMin, cz), 0.05, 1));
      const fit = Math.max(F.w / sw, F.h / sh) * st.zoom, dw = sw * fit, dh = sh * fit, mx = Math.abs(dw - F.w) / 2, my = Math.abs(dh - F.h) / 2;
      st.ox = clamp(st.ox, -mx, mx); st.oy = clamp(st.oy, -my, my);
      geo = { src, cut: false, x: F.cx - dw / 2 + st.ox, y: F.cy - dh / 2 + st.oy, dw, dh };
    }
    const py = P.base ? Math.max(fy, bot - (ph + tg + Ht)) : top + Math.max(0, (bot - top - (P.tres + ph + tg + Ht)) / 2) + P.tres;   // on a base: stands on it
    if (geo && geo.cut) geo.y = py;
    const ty = py + ph + tg, bx = (b) => 0.5 - b.w / 2;
    const name = hasName ? { x: bx(g.name), y: ty / PH, w: g.name.w, h: g.name.h } : null;
    const line = hasLine ? { x: bx(g.line), y: (ty + nH + lg) / PH, w: g.line.w, h: g.line.h } : null;
    return { P, F, geo, py, ph, boxes: { on: !!Ht, name, line } };
  }
  /* The cut line: dilate(alpha(photo) ∪ text plate, border) + hole tab, closed (smooth concave corners), holes filled,
     specks dropped, contoured. Cached until the photo, its size or the words change. */
  shapeGeom() {
    const g = this.sel, L = this.shapeLayout(), P = L.P, PW = this.PW, PH = this.PH, geo = L.geo;
    const now = typeof performance !== 'undefined' ? performance.now() : 0, specs = this.textSpecs();
    const base = [g.key, P.R, geo && geo.cut ? [geo.src._lsid || (geo.src._lsid = ++SHAPE_IDS), geo.x, geo.y, geo.dw, geo.dh].map((v) => Math.round(v * 8) / 8).join(',') : 'frame',
      L.F.x, L.F.y, L.F.w, L.F.h, L.F.r, JSON.stringify(specs.map((t) => [t.text, t.family, t.weight, t.box])), typeof document !== 'undefined' && document.fonts ? document.fonts.status : ''].join('|');
    const c0 = this._shp;
    if (c0 && c0.base === base) {
      if (!c0.fast) return c0;
      const age = now - this._shpT; if (!this._printing && age < 200) { this.shapeUpgradeSoon(200 - age + 20); return c0; }
    }
    // pinching / typing (another change within 200 ms of the last outline): a half-resolution outline now, the full one right after (exports: always full)
    const fast = !(c0 && c0.base === base) && !this._printing && this._shpT != null && now - this._shpT < 200;
    const R = fast ? Math.round(P.R / 2) : P.R, k = R / PW, pad = Math.ceil((P.b + P.c) * k) + 3, GW = R + 2 * pad, GH = Math.round(PH * k) + 2 * pad, N = GW * GH;
    const c = this._shpC || (this._shpC = makeCanvas(1, 1)); if (c.width !== GW || c.height !== GH) { c.width = GW; c.height = GH; }
    const x = c.getContext('2d', { willReadFrequently: true }), U = new Uint8Array(N), th = Math.round(P.alpha * 255);
    const begin = () => { x.setTransform(1, 0, 0, 1, 0, 0); x.clearRect(0, 0, GW, GH); x.setTransform(k, 0, 0, k, pad, pad); x.fillStyle = '#000'; };
    begin();
    if (geo && geo.cut) x.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
    else { roundRect(x, L.F.x, L.F.y, L.F.w, L.F.h, Math.min(L.F.r, L.F.w / 2, L.F.h / 2)); x.fill(); }
    let d = x.getImageData(0, 0, GW, GH).data;
    for (let i = 0; i < N; i++) U[i] = d[i * 4 + 3] >= th ? 1 : 0;
    if (geo && geo.cut) keepBig(U, GW, GH, 0.04);           // specks the background removal left behind are not cut
    let hole = null, crownX = pad + PW / 2 * k;
    {                                                        // hole above the photo, at the top centre (or the top point nearest it)
      const colTop = new Int32Array(GW).fill(GH);
      for (let xx = 0; xx < GW; xx++) for (let yy = 0; yy < GH; yy++) if (U[yy * GW + xx]) { colTop[xx] = yy; break; }
      let gTop = GH; for (let xx = 0; xx < GW; xx++) gTop = Math.min(gTop, colTop[xx]);
      const hd = P.hole ? P.hole.d : 0.04 * PW;
      if (gTop < GH) {
        const half = Math.ceil((hd / 2 + P.gap) * k), span = (geo ? geo.dh : L.F.h) * k;
        let cs = 0, cn = 0; for (let xx = 0; xx < GW; xx++) if (colTop[xx] <= gTop + 0.08 * span) { cs += xx; cn++; }
        const cx = cn ? cs / cn : pad + PW / 2 * k;           // centre of the crown (a pet's head), where a hanger looks natural
        const band = (xc) => { let t = GH; for (let xx = Math.max(0, Math.round(xc) - half); xx <= Math.min(GW - 1, Math.round(xc) + half); xx++) t = Math.min(t, colTop[xx]); return t; };
        let hx = cx, top = band(cx);
        if (top - gTop > 0.3 * span) {                       // deep dip at the centre (between two pets): hang from the nearest high point
          let best = -1; for (let xx = 0; xx < GW; xx++) if (colTop[xx] <= gTop + 0.04 * span && (best < 0 || Math.abs(xx - cx) < Math.abs(best - cx))) best = xx;
          if (best >= 0) { hx = best; top = band(best); }
        }
        crownX = hx;
        if (P.hole) { const r = P.hole.d / 2, hy = Math.max((top - pad) / k - P.gap - r, P.my + r + P.hole.ring); hole = { x: (hx - pad) / k, y: hy, r, R: r + P.hole.ring }; }
      }
    }
    if (specs.length) {                                      // the name plate: the text's box + the glyphs themselves (script swashes)
      begin(); const TL = this.textLayout(x);
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const t of TL) { x0 = Math.min(x0, t.x - t.width / 2); x1 = Math.max(x1, t.x + t.width / 2); y0 = Math.min(y0, t.y - t.asc); y1 = Math.max(y1, t.y + t.desc); }
      const pp = 0.3 * P.b; roundRect(x, x0 - pp, y0 - pp, x1 - x0 + 2 * pp, y1 - y0 + 2 * pp, Math.min(P.b, (y1 - y0) / 2 + pp)); x.fill();
      this.drawTextBoxes(x, null);
      d = x.getImageData(0, 0, GW, GH).data;
      for (let i = 0; i < N; i++) if (d[i * 4 + 3] >= 128) U[i] = 1;
    }
    const bar = P.base ? { x0: pad + (PW - P.base.w) / 2 * k, x1: pad + (PW + P.base.w) / 2 * k, y0: pad + (PH - P.my - P.base.h) * k, y1: pad + (PH - P.my) * k } : null;
    if (bar) {                                               // inset by the border so the dilation lands exactly on the bar, then squared off below
      const ins = P.b * k;
      for (let yy = Math.ceil(bar.y0 + ins); yy < Math.floor(bar.y1 - ins); yy++) for (let xx = Math.ceil(bar.x0 + ins); xx < Math.floor(bar.x1 - ins); xx++) U[yy * GW + xx] = 1;
    }
    const D = edtSq(U, GW, GH), r1 = (P.b + P.c) * k, S = new Uint8Array(N);
    for (let i = 0; i < N; i++) S[i] = D[i] <= r1 * r1 ? 1 : 0;
    if (hole) {
      const tx = pad + hole.x * k, ty = pad + hole.y * k, tr = (hole.R + P.c) * k;
      for (let yy = Math.max(0, Math.floor(ty - tr)); yy <= Math.min(GH - 1, Math.ceil(ty + tr)); yy++)
        for (let xx = Math.max(0, Math.floor(tx - tr)); xx <= Math.min(GW - 1, Math.ceil(tx + tr)); xx++) if ((xx - tx) ** 2 + (yy - ty) ** 2 <= tr * tr) S[yy * GW + xx] = 1;
    }
    fillHoles(S, GW, GH);
    const inv = new Uint8Array(N); for (let i = 0; i < N; i++) inv[i] = S[i] ? 0 : 1;
    const E = edtSq(inv, GW, GH), cc = (P.c * k) ** 2;
    for (let i = 0; i < N; i++) S[i] = E[i] > cc ? 1 : 0;
    if (bar) for (let yy = Math.ceil(bar.y0); yy < Math.floor(bar.y1); yy++) for (let xx = Math.ceil(bar.x0); xx < Math.floor(bar.x1); xx++) S[yy * GW + xx] = 1;   // rounded inwards: never past the safe line
    if (P.s.flatBottom) {                                    // free-standing: square off the bottom so it stands flat
      let yb = -1; for (let yy = GH - 1; yy >= 0 && yb < 0; yy--) for (let xx = 0; xx < GW; xx++) if (S[yy * GW + xx]) { yb = yy; break; }
      const y0 = Math.max(0, yb - Math.max(2, Math.round(1.2 * P.b * k))); let xa = GW, xb = -1;
      for (let xx = 0; xx < GW; xx++) if (S[y0 * GW + xx]) { xa = Math.min(xa, xx); xb = Math.max(xb, xx); }
      for (let yy = y0; yy <= yb; yy++) for (let xx = xa; xx <= xb; xx++) S[yy * GW + xx] = 1;
    }
    keepBig(S, GW, GH, 0); fillHoles(S, GW, GH);
    let crown = null; { const cx = Math.round(crownX); for (let yy = 0; yy < GH; yy++) if (S[yy * GW + cx]) { crown = { x: (cx - pad) / k, y: (yy - pad) / k }; break; } }
    const Fd = new Float32Array(N); for (let i = 0; i < N; i++) Fd[i] = S[i];
    boxBlur(Fd, GW, GH, Math.max(1, Math.round(R / 300)), 2);
    const pts = contour(Fd, GW, GH, 0.5) || [];
    const path = new Path2D(), out = new Float32Array(pts.length); let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      const X = (pts[i] - pad) / k, Y = (pts[i + 1] - pad) / k; out[i] = X; out[i + 1] = Y;
      if (i) path.lineTo(X, Y); else path.moveTo(X, Y);
      bx0 = Math.min(bx0, X); bx1 = Math.max(bx1, X); by0 = Math.min(by0, Y); by1 = Math.max(by1, Y);
    }
    path.closePath();
    const cutPath = new Path2D(path);
    if (hole) { cutPath.moveTo(hole.x + hole.r, hole.y); cutPath.arc(hole.x, hole.y, hole.r, 0, Math.PI * 2); cutPath.closePath(); }
    this._shpT = typeof performance !== 'undefined' ? performance.now() : 0;      // measured from the END of this outline
    if (fast) this.shapeUpgradeSoon(220);
    return (this._shp = { base, fast, path, cutPath, hole, crown, pts: out, bbox: { x0: bx0, y0: by0, x1: bx1, y1: by1 } });
  }
  shapeUpgradeSoon(ms) { clearTimeout(this._shpFull); this._shpFull = setTimeout(() => this.requestRender(), ms); }
  shapeInfo() {
    const SG = this.shapeGeom(), P = this.shapeParams(), b = SG.bbox;
    return { material: P.material, border: Math.round(P.b), bleed: Math.round(P.bleed), hole: SG.hole ? [Math.round(SG.hole.x), Math.round(SG.hole.y), Math.round(SG.hole.r * 2)] : null,
      bbox: [b.x0, b.y0, b.x1, b.y1].map(Math.round), outline: this.isCut() ? 'cutout' : 'rounded-rect' };
  }
  /* The design, die-cut: cut = true is the product as cut (transparent outside the outline and in the hole: the preview);
     cut = false is the Printway artwork half (base colour bleeds past the cut line, the hole is not punched). */
  drawShapeDesign(ctx, w, h, { placeholder = true, only = null, cut = true, noText = false, mirrorClip = false } = {}) {
    const st = this.state, PW = this.PW, PH = this.PH, L = this.shapeLayout(), P = L.P, SG = this.shapeGeom(), geo = L.geo;
    ctx.save(); ctx.scale(w / PW, h / PH);
    const clip = () => { if (mirrorClip) ctx.clip(this.mirrorPath()); else if (cut && SG.hole) ctx.clip(SG.cutPath, 'evenodd'); else ctx.clip(SG.path); };
    if (only !== 'text') {
      if (!only) {
        ctx.save(); ctx.fillStyle = P.edge;
        if (cut) { clip(); ctx.fillRect(0, 0, PW, PH); }
        else if (P.baseInk) { ctx.fill(SG.path); if (P.bleed > 0) { ctx.strokeStyle = P.edge; ctx.lineWidth = 2 * P.bleed; ctx.lineJoin = 'round'; ctx.stroke(SG.path); } }
        ctx.restore();
      }
      ctx.save(); clip();
      if (geo && geo.cut) ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
      else if (geo) {
        const F = L.F; ctx.save(); roundRect(ctx, F.x, F.y, F.w, F.h, Math.min(F.r, F.w / 2, F.h / 2)); ctx.clip();
        ctx.fillStyle = P.edge; ctx.fillRect(F.x, F.y, F.w, F.h); ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh); ctx.restore();
      } else if (placeholder) this.drawPlaceholder(ctx, L.F);
      ctx.restore();
    }
    if (st.showText && only !== 'photo' && !noText && L.boxes.on) { ctx.save(); clip(); this.drawTextBoxes(ctx, null); ctx.restore(); }
    ctx.restore();
  }
  mirrorPath() {                       // the outline flipped left-right (the back cell of 3-part templates)
    const SG = this.shapeGeom(); if (SG._mirror) return SG._mirror;
    const p = new Path2D(), q = SG.pts, PW = this.PW;
    for (let i = 0; i < q.length; i += 2) { if (i) p.lineTo(PW - q[i], q[i + 1]); else p.moveTo(PW - q[i], q[i + 1]); }
    p.closePath(); return (SG._mirror = p);
  }
  /* Printway cut cell: SOLID black outline on transparency (binary alpha), white dot = hanging hole. */
  cutMaskCanvas(w, h) {
    const SG = this.shapeGeom(), c = makeCanvas(w, h), x = c.getContext('2d', { willReadFrequently: true });
    x.scale(w / this.PW, h / this.PH); x.fillStyle = '#000'; x.fill(SG.path);
    if (SG.hole) { x.fillStyle = '#fff'; x.beginPath(); x.arc(SG.hole.x, SG.hole.y, SG.hole.r, 0, Math.PI * 2); x.fill(); }
    const id = x.getImageData(0, 0, w, h), d = id.data;
    for (let i = 0; i < d.length; i += 4) { if (d[i + 3] < 128) { d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 0; } else { const v = d[i] * d[i + 3] / 255 >= 128 ? 255 : 0; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255; } }
    x.putImageData(id, 0, 0); return c;
  }
  /* Printway custom-shape file, laid out from group.printway (print_specs.json: px + cells) or, without it, W x 2H:
     cut cell = solid black outline on transparency (white dot = hole); front cell = the artwork at exactly the cut's x/y
     (transparent outside the bleed, or s.artworkBg); back cell (3-part templates) = printway.back "mirror" (mirrored art,
     text re-drawn readable) or "empty"; any other cell (printed base / stand strip) is left empty. Capped at 16 MP. */
  pwLayout(g = this.sel) {
    const pw = g.printway || {}, PW = this.PW, PH = this.PH;
    return { px: pw.px || [PW, 2 * PH], cells: pw.cells || { cut: [0, 0, PW, PH], front: [0, PH, PW, PH] }, back: pw.back || 'empty' };
  }
  async _exportShapePrint(maxBytes = Infinity) {
    if (!(await this.fontsReady()) && this.needsFonts()) throw new Error('font-not-loaded');
    const P = this.shapeParams(), Lp = this.pwLayout(), [W, H] = Lp.px, cap = Math.min(1, Math.sqrt(MAX_MP / (W * H)));
    let blob = null;
    for (const k of [1, 0.85, 0.7]) {
      const sc = cap * k, c = makeCanvas(Math.round(W * sc), Math.round(H * sc)), ctx = c.getContext('2d'); ctx.imageSmoothingQuality = 'high';
      const R = (r) => [Math.round(r[0] * sc), Math.round(r[1] * sc), Math.round(r[2] * sc), Math.round(r[3] * sc)];
      const cell = (r, fn) => { const [x, y, w, h] = R(r); ctx.save(); ctx.translate(x, y); ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.clip(); fn(w, h); ctx.restore(); };
      const art = (w, h, o) => { if (P.s.artworkBg) { ctx.fillStyle = P.s.artworkBg; ctx.fillRect(0, 0, w, h); } this.drawShapeDesign(ctx, w, h, { placeholder: false, cut: false, ...o }); };
      if (Lp.cells.cut) { const [x, y, w, h] = R(Lp.cells.cut); ctx.drawImage(this.cutMaskCanvas(w, h), x, y); }
      if (Lp.cells.front) cell(Lp.cells.front, (w, h) => art(w, h));
      if (Lp.cells.back && Lp.back === 'mirror') cell(Lp.cells.back, (w, h) => {
        ctx.save(); ctx.translate(w, 0); ctx.scale(-1, 1); art(w, h, { noText: true }); ctx.restore();
        this.drawShapeDesign(ctx, w, h, { only: 'text', cut: false, mirrorClip: true });
      });
      blob = await canvasToBlob(c, 'image/png'); this._printPx = [c.width, c.height]; this._printScale = sc; c.width = c.height = 0;
      if (blob.size <= maxBytes) break;
    }
    return blob;
  }
  /* print px (of the design square) -> preview canvas px, through the view mapping */
  viewPoint(v, vw, size, X, Y) {
    const m = this.viewMesh(v), [x0, y0, x1, y1] = m.region, u = (X / this.PW - x0) / (x1 - x0), w = (Y / this.PH - y0) / (y1 - y0);
    const H = v._H || homography([[0, 0], [1, 0], [1, 1], [0, 1]], [m.P[0], m.P[m.cols], m.P[m.P.length - 1], m.P[m.P.length - 1 - m.cols]]);
    const [fx, fy] = applyH(H, u, w); return [(fx - vw.x) / vw.s * size, (fy - vw.y) / vw.s * size];
  }
  scratch(name, w, h) {
    const c = (this._scr = this._scr || {})[name] || (this._scr[name] = makeCanvas(1, 1));
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    const x = c.getContext('2d'); x.setTransform(1, 0, 0, 1, 0, 0); x.globalCompositeOperation = 'source-over'; x.globalAlpha = 1; x.clearRect(0, 0, w, h);
    return [c, x];
  }
  /* Die-cut preview: scene photo (no product) + the cut design warped into view.quad, with a soft shadow on the scene,
     the material's edge (thickness), light + sheen, the hanger through the hole and optional sprites in front. */
  renderShape(c, size, vw) {
    const ctx = c.getContext('2d'); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, size, size); ctx.imageSmoothingQuality = 'high';
    const v = this.view, base = v && ((vw.s < 1 && v.srcHD && this.img[v.srcHD]) || this.img[v.src]);
    if (!v || !base) { this.renderFlat(ctx, size); return; }
    const nw = base.naturalWidth, nh = base.naturalHeight;
    ctx.drawImage(base, vw.x * nw, vw.y * nh, vw.s * nw, vw.s * nh, 0, 0, size, size);
    const m = this.viewMesh(v), [x0, y0, x1, y1] = m.region, Pc = m.P.map((p) => [(p[0] - vw.x) / vw.s * size, (p[1] - vw.y) / vw.s * size]);
    const xs = Pc.map((p) => p[0]), ys = Pc.map((p) => p[1]);
    const bx0 = Math.min(...xs), by0 = Math.min(...ys), bw = Math.max(...xs) - bx0, bh = Math.max(...ys) - by0;
    const rw = (x1 - x0) * this.PW, rh = (y1 - y0) * this.PH, s = Math.min(1600 / Math.max(rw, rh), Math.max(bw, bh) * 1.15 / Math.max(rw, rh));
    const lw = Math.max(2, Math.round(rw * s)), lh = Math.max(2, Math.round(rh * s));
    const layer = this._layer; if (layer.width !== lw || layer.height !== lh) { layer.width = lw; layer.height = lh; }
    const lctx = layer.getContext('2d'); lctx.setTransform(1, 0, 0, 1, 0, 0); lctx.clearRect(0, 0, lw, lh); lctx.imageSmoothingQuality = 'high';
    const fullW = this.PW * s, fullH = this.PH * s; lctx.translate(-x0 * fullW, -y0 * fullH); this.drawDesign(lctx, fullW, fullH); lctx.setTransform(1, 0, 0, 1, 0, 0);
    const mg = Math.ceil(Math.max(bw, bh) * 0.05) + 6, ox = Math.floor(bx0) - mg, oy = Math.floor(by0) - mg, tw = Math.ceil(bw) + 2 * mg, th = Math.ceil(bh) + 2 * mg;
    const [T, t] = this.scratch('face', tw, th); t.imageSmoothingQuality = 'high'; t.setTransform(1, 0, 0, 1, -ox, -oy);
    const { cols, rows } = m, Lp = (c2, r2) => [c2 / cols * lw, r2 / rows * lh];
    for (let r = 0; r < rows; r++) for (let q = 0; q < cols; q++) {
      const i0 = r * (cols + 1) + q, i1 = i0 + 1, i2 = i0 + cols + 1, i3 = i2 + 1;
      drawTri(t, layer, Lp(q, r), Lp(q + 1, r), Lp(q, r + 1), Pc[i0], Pc[i1], Pc[i2]);
      drawTri(t, layer, Lp(q + 1, r), Lp(q + 1, r + 1), Lp(q, r + 1), Pc[i1], Pc[i3], Pc[i2]);
    }
    t.setTransform(1, 0, 0, 1, 0, 0);
    const P = this.shapeParams(), SG = this.shapeGeom(), mat = P.material, mmC = P.mm * bw / ((x1 - x0) * this.PW);   // canvas px per mm
    const tint = (name, color, src = T) => { const [C, x] = this.scratch(name, tw, th); x.drawImage(src, 0, 0); x.globalCompositeOperation = 'source-in'; x.fillStyle = color; x.fillRect(0, 0, tw, th); x.globalCompositeOperation = 'source-over'; return C; };
    // material look (pw/CONTRACT-PW.md): side-wall colours, gloss, reflection, rim, extras
    const M = { ...(SHAPE_MATS[mat] || SHAPE_MATS.acrylic), ...(v.look || {}) };
    if (mat === 'felt') { M.wall = mixRGB(P.edge, 0.22, [0, 0, 0]); }
    // 1. shadow on the scene (two soft passes, drawn far off-canvas so only the shadow lands) + optional contact shadow
    const sd = { dx: 1.2, dy: 2.6, blur: 3.2, alpha: 0.34, ...(v.shadow || {}) }, OFF = 4 * size + tw;
    if (sd.alpha > 0) for (const [f, a] of [[1, sd.alpha], [2.4, sd.alpha * 0.45]]) {
      ctx.save(); ctx.shadowColor = `rgba(18,10,4,${a})`; ctx.shadowBlur = sd.blur * f * mmC; ctx.shadowOffsetX = sd.dx * f * mmC + OFF; ctx.shadowOffsetY = sd.dy * f * mmC;
      ctx.drawImage(T, ox - OFF, oy); ctx.restore();
    }
    if (sd.contact) {                              // standing products: a dark soft line where the bottom meets the surface
      const b = SG.bbox, [cx, cy] = this.viewPoint(v, vw, size, (b.x0 + b.x1) / 2, b.y1), [ex] = this.viewPoint(v, vw, size, P.base ? (this.PW + P.base.w) / 2 : b.x1, b.y1);
      const rx = Math.max(4, Math.abs(ex - cx) * 1.05), ry = Math.max(2, (sd.contact.h || 1.6) * mmC);
      ctx.save(); ctx.translate(cx, cy); ctx.scale(1, ry / rx); const rg = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
      rg.addColorStop(0, `rgba(20,12,6,${sd.contact.alpha || 0.45})`); rg.addColorStop(0.7, `rgba(20,12,6,${(sd.contact.alpha || 0.45) * 0.35})`); rg.addColorStop(1, 'rgba(20,12,6,0)');
      ctx.fillStyle = rg; ctx.fillRect(-rx, -rx, 2 * rx, 2 * rx); ctx.restore();
    }
    if (M.glow) {                                  // LED: light from the base spills around the edges
      ctx.save(); ctx.shadowColor = M.glow; ctx.shadowBlur = 5 * mmC; ctx.shadowOffsetX = OFF; ctx.drawImage(T, ox - OFF, oy); ctx.shadowBlur = 14 * mmC; ctx.drawImage(T, ox - OFF, oy); ctx.restore();
    }
    const H = v.hanger && (SG.hole || SG.crown) ? this.hangerGeom(v, vw, size, SG, mmC) : null;
    if (H) this.drawHanger(ctx, H, 'back');
    // 2. material thickness: the side wall seen along edgeDir (acrylic: clear, light, a bright polished corner line;
    //    wood: dark laser-burnt edge; felt: the felt itself), then the face
    const thick = Math.max(1.2, (v.thickness != null ? v.thickness : M.thick) * mmC), dir = v.edgeDir || [0.55, 0.85];
    const dl = Math.hypot(dir[0], dir[1]) || 1, ux = dir[0] / dl, uy = dir[1] / dl;
    if (M.wall) {
      const n = Math.max(2, Math.ceil(thick * 1.5)), [E, ex] = this.scratch('edge', tw, th);
      for (let i = n; i >= 1; i--) ex.drawImage(T, ux * thick * i / n, uy * thick * i / n);
      if (M.wall === 'face') { ex.globalCompositeOperation = 'source-atop'; ex.fillStyle = M.wallTint; ex.fillRect(0, 0, tw, th); }
      else { ex.globalCompositeOperation = 'source-in'; ex.fillStyle = M.wall; ex.fillRect(0, 0, tw, th); }
      ex.globalCompositeOperation = 'destination-out'; ex.drawImage(T, 0, 0);
      ctx.save(); ctx.globalAlpha = M.wallA; ctx.drawImage(E, ox, oy);
      if (M.line) {
        const lw = Math.max(0.7, thick * 0.28), [Fl, fx] = this.scratch('edgeLine', tw, th);
        fx.drawImage(T, ux * thick, uy * thick); fx.globalCompositeOperation = 'destination-out'; fx.drawImage(T, ux * (thick - lw), uy * (thick - lw)); fx.drawImage(T, 0, 0);
        fx.globalCompositeOperation = 'source-in'; fx.fillStyle = M.line; fx.fillRect(0, 0, tw, th); ctx.globalAlpha = M.lineA; ctx.drawImage(Fl, ox, oy);
      }
      ctx.restore();
    }
    ctx.drawImage(T, ox, oy);
    // 3. the scene's light on the face: ambient colour + level (from the scene photo), gentle falloff, gloss, grain
    const amb = this.sceneAmbient(v, base), am = { tint: 0.10, level: 0.95, ...(v.ambient || {}) };
    const tc = amb.map((c) => Math.round(255 * am.level * (1 - am.tint * (1 - c))));
    ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(tint('amb', `rgb(${tc[0]},${tc[1]},${tc[2]})`), ox, oy); ctx.restore();
    if (M.pattern) {                               // hologram / wavy acrylic: the pattern is in the material itself
      const [Hp, hpx] = this.scratch('pattern', tw, th), tile = this.patternTile(M.pattern === 'holo' ? (P.s.holo || 'starburst') : M.pattern);
      hpx.drawImage(tile, mg, mg, tw - 2 * mg, th - 2 * mg); hpx.globalCompositeOperation = 'destination-in';
      if (M.pattern === 'holo') {                  // the foil shows on the light (white-base) areas, hardly over the photo
        const [Lm, lmx] = this.scratch('holoMask', tw, th); lmx.drawImage(T, 0, 0);
        const id = lmx.getImageData(0, 0, tw, th), d = id.data;
        for (let i = 0; i < d.length; i += 4) { const l = (0.3 * d[i] + 0.59 * d[i + 1] + 0.11 * d[i + 2]) / 255; d[i + 3] = d[i + 3] * clamp((l - 0.7) / 0.22, 0, 1); }
        lmx.putImageData(id, 0, 0); hpx.drawImage(Lm, 0, 0);
      } else hpx.drawImage(T, 0, 0);
      ctx.save(); ctx.globalCompositeOperation = M.pattern === 'holo' ? 'multiply' : 'soft-light'; ctx.globalAlpha = M.patternA; ctx.drawImage(Hp, ox, oy);
      if (M.pattern === 'holo') { ctx.globalCompositeOperation = 'screen'; ctx.globalAlpha = M.patternA * 0.5; ctx.drawImage(Hp, ox, oy); }
      ctx.restore();
    }
    const [G, gx] = this.scratch('light', tw, th);
    const lin = gx.createLinearGradient(mg, mg, tw - mg, th - mg);
    lin.addColorStop(0, 'rgba(255,255,255,0.12)'); lin.addColorStop(0.5, 'rgba(255,255,255,0)'); lin.addColorStop(1, 'rgba(0,0,0,0.14)');
    gx.fillStyle = lin; gx.fillRect(0, 0, tw, th);
    if (M.sheen > 0) {
      const k = M.sheen, sh = gx.createLinearGradient(mg, mg, tw - mg, th - mg);
      [[0, 0], [0.18, 0], [0.27, 0.34], [0.33, 0.12], [0.46, 0], [0.6, 0], [0.655, 0.13], [0.7, 0]].forEach(([o, a]) => sh.addColorStop(o, `rgba(255,255,255,${(a * k).toFixed(3)})`));
      gx.fillStyle = sh; gx.fillRect(0, 0, tw, th);
      const rg = gx.createRadialGradient(tw * 0.28, th * 0.2, 0, tw * 0.28, th * 0.2, Math.max(tw, th) * 0.55);
      rg.addColorStop(0, `rgba(255,255,255,${0.14 * k})`); rg.addColorStop(1, 'rgba(255,255,255,0)'); gx.fillStyle = rg; gx.fillRect(0, 0, tw, th);
    }
    if (M.led) {                                   // lit from the base: warm light rising through the acrylic
      const lg = gx.createLinearGradient(0, th - mg, 0, mg); lg.addColorStop(0, M.led); lg.addColorStop(0.45, 'rgba(255,200,130,0)'); gx.fillStyle = lg; gx.fillRect(0, 0, tw, th);
    }
    if (mat === 'wood' && v.woodGrain !== false) {     // natural wood shows through the print: faint grain over the whole face
      const [Wg, wgx] = this.scratch('wood', tw, th); wgx.fillStyle = wgx.createPattern(this.woodTile(), 'repeat');
      wgx.setTransform(Math.max(0.5, mmC / 6), 0, 0, Math.max(0.5, mmC / 6), 0, 0); wgx.fillRect(0, 0, tw * 12, th * 12); wgx.setTransform(1, 0, 0, 1, 0, 0);
      wgx.globalCompositeOperation = 'destination-in'; wgx.drawImage(T, 0, 0);
      ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = 0.55; ctx.drawImage(Wg, ox, oy); ctx.restore();
    }
    const grain = v.grain != null ? v.grain : M.grain;
    if (grain > 0) { gx.globalAlpha = Math.min(1, grain / 0.05); gx.fillStyle = gx.createPattern(this.noiseTile(), 'repeat'); gx.fillRect(0, 0, tw, th); gx.globalAlpha = 1; }
    gx.globalCompositeOperation = 'destination-in'; gx.drawImage(T, 0, 0); gx.globalCompositeOperation = 'source-over';
    ctx.drawImage(G, ox, oy);
    const env = v.reflect != null ? v.reflect : M.reflect;
    if (env > 0) {                                // gloss: a soft, mirrored, very blurred reflection of the scene's lights
      if (!base._lsEnv) { const e = makeCanvas(24, 24), x = e.getContext('2d'); x.imageSmoothingQuality = 'high'; x.translate(24, 0); x.scale(-1, 1); x.drawImage(base, 0, 0, 24, 24); base._lsEnv = e; }
      const [R2, rx] = this.scratch('env', tw, th); rx.imageSmoothingEnabled = true; rx.imageSmoothingQuality = 'high';
      rx.drawImage(base._lsEnv, (bx0 - ox) - bw * 0.6 + (vw.x - 0.5) * size, (by0 - oy) - bh * 0.6, bw * 2.2, bh * 2.2);
      rx.globalCompositeOperation = 'destination-in'; rx.drawImage(T, 0, 0);
      ctx.save(); ctx.globalCompositeOperation = 'screen'; ctx.globalAlpha = env; ctx.drawImage(R2, ox, oy); ctx.restore();
    }
    if (M.rimHi) {                                 // polished rim: bright on the lit (top-left) edge, a dark hairline on the other
      const rim = Math.max(0.8, 0.22 * mmC);
      const hi = tint('rimHi', M.rimHi), hx = hi.getContext('2d');
      hx.globalCompositeOperation = 'destination-out'; hx.drawImage(T, ux * rim, uy * rim); hx.globalCompositeOperation = 'source-over';
      const lo = tint('rimLo', M.rimLo), lx = lo.getContext('2d');
      lx.globalCompositeOperation = 'destination-out'; lx.drawImage(T, -ux * rim, -uy * rim); lx.globalCompositeOperation = 'source-over';
      ctx.drawImage(lo, ox, oy); ctx.drawImage(hi, ox, oy);
    }
    if (M.inner) {                                 // soft plush / felt: darker towards the edge
      const [I, ix] = this.scratch('inner', tw, th); ix.fillStyle = '#000'; ix.fillRect(0, 0, tw, th); ix.globalCompositeOperation = 'destination-out'; ix.drawImage(T, 0, 0);
      const [J, jx] = this.scratch('inner2', tw, th); jx.shadowColor = `rgba(30,20,15,${M.inner})`; jx.shadowBlur = (mat === 'felt' ? 1.6 : 5) * mmC; jx.drawImage(I, 0, 0);
      jx.shadowColor = 'transparent'; jx.globalCompositeOperation = 'destination-in'; jx.drawImage(T, 0, 0); ctx.drawImage(J, ox, oy);
    }
    if (H) this.drawHanger(ctx, H, 'front');
    // 5. sprites in front (bells, charms), optionally hung from the bottom of the outline
    for (const e of v.extras || []) {
      const im = e && this.img[e.src]; if (!im || !im.naturalWidth) continue;
      let px, py;
      if (e.anchor === 'bottom') { const b = SG.bbox, [bx, by] = this.viewPoint(v, vw, size, (b.x0 + b.x1) / 2, b.y1); px = bx + (e.x || 0) * size / vw.s; py = by + (e.y || 0) * size / vw.s; }
      else { px = ((e.x || 0) - vw.x) / vw.s * size; py = ((e.y || 0) - vw.y) / vw.s * size; }
      const ew = (e.w || 0.1) * size / vw.s, eh = ew * im.naturalHeight / im.naturalWidth;
      ctx.save(); ctx.shadowColor = 'rgba(18,10,4,0.3)'; ctx.shadowBlur = 2.5 * mmC; ctx.shadowOffsetX = sd.dx * mmC; ctx.shadowOffsetY = sd.dy * mmC;
      ctx.drawImage(im, px - ew / 2, py, ew, eh); ctx.restore();
    }
    const fr = v.front && this.img[v.front];
    if (fr && fr.naturalWidth) ctx.drawImage(fr, vw.x * fr.naturalWidth, vw.y * fr.naturalHeight, vw.s * fr.naturalWidth, vw.s * fr.naturalHeight, 0, 0, size, size);
    this._shapeView = { ox, oy, tw, th, mmC };
  }
  /* average colour of the scene photo (0..1 per channel, normalised to the brightest channel), cached per image */
  sceneAmbient(v, im) {
    if (im._lsAmb) return im._lsAmb;
    try {
      const c = makeCanvas(16, 16), x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0, 16, 16);
      const d = x.getImageData(0, 0, 16, 16).data, a = [0, 0, 0]; for (let i = 0; i < d.length; i += 4) { a[0] += d[i]; a[1] += d[i + 1]; a[2] += d[i + 2]; }
      const m = Math.max(...a) || 1; im._lsAmb = a.map((t) => t / m);
    } catch (e) { im._lsAmb = [1, 1, 1]; }
    return im._lsAmb;
  }
  woodTile() {                  // tileable 256 px grain: soft growth rings with a little waviness (multiply: white = no change)
    if (this._wood) return this._wood;
    const N = 256, c = makeCanvas(N, N), x = c.getContext('2d'), id = x.createImageData(N, N), d = id.data, T2 = Math.PI * 2;
    for (let y = 0; y < N; y++) for (let i = 0; i < N; i++) {
      const w = y + 7 * Math.sin(T2 * i / N) + 3 * Math.sin(T2 * 3 * i / N + 1.3) + 1.5 * Math.sin(T2 * 7 * (i + y) / N);
      const r = 0.5 + 0.5 * Math.sin(T2 * 9 * w / N), fine = 0.5 + 0.5 * Math.sin(T2 * 41 * w / N + Math.sin(T2 * 5 * i / N));
      const v = 1 - 0.13 * r ** 3 - 0.05 * fine, q = (y * N + i) * 4;
      d[q] = 255 * v; d[q + 1] = 255 * (v - 0.02 * r); d[q + 2] = 255 * (v - 0.05 * r); d[q + 3] = 255;
    }
    x.putImageData(id, 0, 0); return (this._wood = c);
  }
  /* procedural 512 px material tiles: hologram 'starburst' / 'broken-glass' (pastel rainbow), 'wavy' (ripple light) */
  patternTile(kind) {
    const C = (this._tiles = this._tiles || {}); if (C[kind]) return C[kind];
    const N = 512, c = makeCanvas(N, N), x = c.getContext('2d'); let seed = 99; const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    const hue = (h, l = 82, a = 1) => `hsla(${Math.round(h) % 360},90%,${l}%,${a})`;
    if (kind === 'wavy') {
      x.fillStyle = '#808080'; x.fillRect(0, 0, N, N);
      for (let i = -2; i < 24; i++) { x.beginPath(); for (let px = 0; px <= N; px += 8) { const py = i * 24 + 10 * Math.sin(px / 46 + i * 0.7); px ? x.lineTo(px, py) : x.moveTo(px, py); }
        x.lineWidth = 9; x.strokeStyle = i % 2 ? 'rgba(255,255,255,0.75)' : 'rgba(40,40,40,0.45)'; x.stroke(); }
    } else if (kind === 'broken-glass') {
      for (let i = 0; i < 70; i++) { const cx = rnd() * N, cy = rnd() * N, r = 30 + rnd() * 90, a0 = rnd() * 6.3; x.beginPath();
        for (let k = 0; k < 3 + (i % 3); k++) { const a = a0 + k * 2.1 + rnd() * 0.6; x.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r); }
        x.closePath(); x.fillStyle = hue(rnd() * 360, 80, 0.85); x.fill(); x.strokeStyle = 'rgba(255,255,255,0.8)'; x.lineWidth = 1.2; x.stroke(); }
    } else {                                   // starburst: thin rainbow rays from the centre
      for (let i = 0; i < 180; i++) { const a = i / 180 * Math.PI * 2, w = 0.012 + rnd() * 0.02; x.beginPath(); x.moveTo(N / 2, N / 2);
        x.arc(N / 2, N / 2, N * 0.75, a, a + w); x.closePath(); x.fillStyle = hue(i * 2 + rnd() * 40, 78 + rnd() * 10, 0.9); x.fill(); }
      const rg = x.createRadialGradient(N / 2, N / 2, 0, N / 2, N / 2, N * 0.7); rg.addColorStop(0, 'rgba(255,255,255,0.6)'); rg.addColorStop(1, 'rgba(255,255,255,0)'); x.fillStyle = rg; x.fillRect(0, 0, N, N);
    }
    return (C[kind] = c);
  }
  noiseTile() {
    if (this._noise) return this._noise;
    const c = makeCanvas(96, 96), x = c.getContext('2d'), id = x.createImageData(96, 96), d = id.data; let seed = 12345;
    for (let i = 0; i < d.length; i += 4) { seed = (seed * 16807) % 2147483647; const r = seed / 2147483647, v = r > 0.5 ? 255 : 0; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = Math.round(Math.abs(r - 0.5) * 2 * 22); }
    x.putImageData(id, 0, 0); return (this._noise = c);
  }
  /* Hanger geometry in canvas px: the hole, the top of its tab, and where the strands go (view.hanger.to). */
  hangerGeom(v, vw, size, SG, mmC) {
    const hg = v.hanger, h = SG.hole || { x: SG.crown.x, y: SG.crown.y + 1.2 * this.shapeParams().mm, r: 0.6 * this.shapeParams().mm, R: 1.2 * this.shapeParams().mm };   // no hole: a sewn loop at the crown
    const [hx, hy] = this.viewPoint(v, vw, size, h.x, h.y), [, ty] = this.viewPoint(v, vw, size, h.x, h.y - h.R);
    const to = hg.to || [0.5, 0], tx = (to[0] - vw.x) / vw.s * size, tyy = (to[1] - vw.y) / vw.s * size;
    const [ex] = this.viewPoint(v, vw, size, h.x + h.r, h.y);
    return { type: hg.type || 'ribbon', color: hg.color || '#b0222c', hx, hy, top: ty, hr: Math.abs(ex - hx), tx, ty: tyy, mmC, width: (hg.width || 4) * mmC, bow: hg.bow };
  }
  drawHanger(ctx, H, layer) {
    const { hx, hy, tx, ty, mmC } = H, len = Math.hypot(tx - hx, ty - hy) || 1;
    const bez = (side, n = 40) => {          // strand from the hole to the knot, bowing a little sideways
      const out = [], sx = side * (H.bow != null ? H.bow : 0.075) * len, c1 = [hx + sx, hy - 0.4 * len], c2 = [tx + sx * 0.35, ty + 0.28 * len];
      for (let i = 0; i <= n; i++) { const u = i / n, a = (1 - u) ** 3, b = 3 * (1 - u) ** 2 * u, c = 3 * (1 - u) * u * u, d = u ** 3; out.push([a * hx + b * c1[0] + c * c2[0] + d * tx, a * hy + b * c1[1] + c * c2[1] + d * ty]); }
      return out;
    };
    const shadow = (on) => { if (on) { ctx.shadowColor = 'rgba(18,10,4,0.32)'; ctx.shadowBlur = 1.6 * mmC; ctx.shadowOffsetX = 0.7 * mmC; ctx.shadowOffsetY = 1.2 * mmC; } else { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = ctx.shadowOffsetY = 0; } };
    const strip = (pts, wAt) => {            // ribbon polygon with a width profile
      const L = [], R = [];
      pts.forEach((p, i) => { const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)], dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy) || 1, w = wAt(i / (pts.length - 1)) / 2;
        L.push([p[0] - dy / l * w, p[1] + dx / l * w]); R.push([p[0] + dy / l * w, p[1] - dx / l * w]); });
      return { L, R };
    };
    const poly = (a) => { ctx.beginPath(); a.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); };
    ctx.save(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (H.type === 'chain') {
      const metal = H.color, lk = 2.4 * mmC, rr = Math.max(H.hr * 1.9, 2 * mmC), cy = H.top + rr * 0.15;
      const ring = (front) => {               // jump ring through the hole, seen almost edge-on
        ctx.lineWidth = Math.max(1, 0.5 * mmC); ctx.strokeStyle = mixRGB(metal, 0.45, [0, 0, 0]);
        ctx.beginPath(); ctx.ellipse(hx, (hy + H.top) / 2 - rr * 0.35, rr * 0.32, (hy - H.top) / 2 + rr * 0.55, 0, front ? Math.PI * 0.5 : -Math.PI * 0.5, front ? Math.PI * 1.5 : Math.PI * 0.5); ctx.stroke();
        ctx.lineWidth = Math.max(0.6, 0.22 * mmC); ctx.strokeStyle = mixRGB(metal, 0.5); ctx.stroke();
      };
      if (layer === 'back') {
        ring(false);
        const sx = hx, sy = H.top - rr * 0.9, n = Math.max(2, Math.floor(Math.hypot(tx - sx, ty - sy) / (lk * 0.82))), ang = Math.atan2(ty - sy, tx - sx);
        shadow(true);
        for (let i = 0; i < n; i++) {
          const u = (i + 0.5) / n, px = sx + (tx - sx) * u, py = sy + (ty - sy) * u;
          ctx.lineWidth = Math.max(1, 0.42 * mmC); ctx.strokeStyle = mixRGB(metal, 0.4, [0, 0, 0]);
          ctx.beginPath(); if (i % 2) { ctx.moveTo(px - Math.cos(ang) * lk * 0.5, py - Math.sin(ang) * lk * 0.5); ctx.lineTo(px + Math.cos(ang) * lk * 0.5, py + Math.sin(ang) * lk * 0.5); } else ctx.ellipse(px, py, lk * 0.55, lk * 0.3, ang, 0, Math.PI * 2);
          ctx.stroke(); shadow(false); ctx.lineWidth = Math.max(0.5, 0.16 * mmC); ctx.strokeStyle = mixRGB(metal, 0.55); ctx.stroke(); shadow(true);
        }
        shadow(false);
      } else ring(true);
    } else {
      const thin = H.type === 'string', w = thin ? Math.max(1.2, 0.7 * mmC) : H.width, pinch = thin ? w : Math.min(w, H.hr * 1.6);
      const wAt = (u) => pinch + (w - pinch) * clamp(u / 0.14, 0, 1) ** 0.7;
      const strand = (side, front) => {
        const pts = bez(side), { L, R } = strip(pts, wAt), col = H.color;
        shadow(true); ctx.fillStyle = mixRGB(col, front ? 0 : 0.18, [0, 0, 0]); poly([...L, ...R.reverse()]); ctx.closePath(); ctx.fill(); shadow(false);
        if (thin) { ctx.setLineDash([0.9 * mmC, 0.7 * mmC]); ctx.lineWidth = w * 0.45; ctx.strokeStyle = mixRGB(col, 0.35); poly(pts); ctx.stroke(); ctx.setLineDash([]); return; }
        R.reverse();
        const mid = strip(pts.map((p, i) => { const l = L[i], r = R[i]; return [l[0] * 0.62 + r[0] * 0.38, l[1] * 0.62 + r[1] * 0.38]; }), (u) => wAt(u) * 0.34);
        const gl = ctx.createLinearGradient(pts[0][0], pts[0][1], pts[pts.length - 1][0], pts[pts.length - 1][1]);   // satin: the light changes along the twist
        [[0, 0.15], [0.18, 0.5], [0.4, 0.2], [0.62, 0.45], [0.85, 0.12], [1, 0.3]].forEach(([o, a]) => gl.addColorStop(o, mixRGB(col, 0.5, [255, 255, 255], a * (front ? 1 : 0.6))));
        ctx.fillStyle = gl; poly([...mid.L, ...mid.R.reverse()]); ctx.closePath(); ctx.fill();
        ctx.lineWidth = Math.max(0.6, 0.18 * mmC); ctx.strokeStyle = mixRGB(col, 0.5, [0, 0, 0], 0.55); poly(L); ctx.stroke(); poly(R); ctx.stroke();
      };
      if (layer === 'back') strand(1, false); else strand(-1, true);
    }
    ctx.restore();
  }

  /* ---------- text only (embroidered names: stockings, knit blankets) ---------- */
  isTextOnly(g = this.sel) { if (!this.cfgText || !g) return false; return !!(Object.prototype.hasOwnProperty.call(g, 'textOnly') ? g.textOnly : this.cfg.textOnly); }
  fontChoice() { const fc = (this.cfg && this.cfg.fontChoices) || []; return fc.find((f) => f.key === this.state.font) || fc[0] || null; }
  threadChoice() {
    const tc = (this.cfg && this.cfg.threadChoices) || [], d = (this.sel && this.sel.defaultThread) || (this.cfg && this.cfg.defaultThread);
    return tc.find((t) => t.key === this.state.thread) || tc.find((t) => t.key === d) || tc[0] || null;
  }
  isScript(f) { return f && f.script != null ? !!f.script : /script|vibes|pacifico|dancing|allura|parisienne|satisfy|cookie|sacramento|lobster/i.test((f && f.family) || ''); }
  textOnlyHTML() {
    if (!this.cfgText) return '';
    if (typeof document !== 'undefined' && !document.getElementById('ls-textonly-css')) {
      const css = document.createElement('style'); css.id = 'ls-textonly-css';
      css.textContent = '.ls-chips{display:flex;flex-wrap:wrap;gap:8px}.ls-pick{display:inline-flex;align-items:center;gap:8px;min-height:44px;padding:6px 14px;border:1px solid rgba(43,20,25,.16);border-radius:999px;background:#fff;color:#2b1419;font:inherit;font-size:13px;cursor:pointer;-webkit-tap-highlight-color:transparent}'
        + '.ls-pick[aria-pressed="true"]{border-color:#2b1419;box-shadow:inset 0 0 0 1px #2b1419}.ls-pick:focus-visible{outline:2px solid #b24e6c;outline-offset:2px}'
        + '.ls-pick-note{display:block;font-size:11.5px;opacity:.7;margin-top:2px}.ls-pick--font span{font-size:19px;line-height:1.1}.ls-dot{width:18px;height:18px;border-radius:50%;flex:none;box-shadow:inset 0 0 0 1px rgba(0,0,0,.18)}';
      document.head.appendChild(css);
      if (this.cfg.fontCss && !document.querySelector(`link[href="${this.cfg.fontCss}"]`)) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = this.cfg.fontCss; document.head.appendChild(l); }
    }
    const fc = this.cfg.fontChoices || [], tc = this.cfg.threadChoices || [];
    const approx = fc.some((x) => x.approx) ? '<small class="ls-pick-note">Font previews are close matches of the stitched fonts.</small>' : '';
    const f = fc.length ? `<div class="ls-step ls-step--font"><div class="ls-label">Font <small class="ls-pick-name"></small></div><div class="ls-chips" role="group" aria-label="Font">${fc.map((x) => `<button type="button" class="ls-pick ls-pick--font" data-font="${escHTML(x.key)}" aria-pressed="false" title="${escHTML(x.label)}"><span style="font-family:'${escHTML(x.family)}';font-weight:${+x.weight || 400}">${escHTML(x.sample || x.label)}</span></button>`).join('')}</div>${approx}</div>` : '';
    const t = tc.length ? `<div class="ls-step ls-step--thread"><div class="ls-label">Thread colour <small class="ls-pick-name"></small></div><div class="ls-chips" role="group" aria-label="Thread colour">${tc.map((x) => `<button type="button" class="ls-pick ls-pick--thread" data-thread="${escHTML(x.key)}" aria-pressed="false"><i class="ls-dot" style="background:${escHTML(x.hex)}"></i>${escHTML(x.label)}</button>`).join('')}</div></div>` : '';
    return f + t;
  }
  syncTextOnlyUI() {
    const r = this.root; if (!r) return;
    const on = this.isTextOnly(), steps = [...r.querySelectorAll('.ls-panel > .ls-step')], tog = $(r, '.ls-text-toggle');
    if (!this._toBound) {
      this._toBound = true;
      r.querySelectorAll('[data-font]').forEach((b) => b.addEventListener('click', () => { this.state.font = b.dataset.font; this.syncTextOnlyUI(); this.requestRender(); this.emit(); }));
      r.querySelectorAll('[data-thread]').forEach((b) => b.addEventListener('click', () => { this.state.thread = b.dataset.thread; this.syncTextOnlyUI(); this.requestRender(); this.emit(); }));
      if (document.fonts) Promise.all((this.cfg.fontChoices || []).map((f) => {      // exact font files (Printway's own fonts) when the config has them
        if (f.src && typeof FontFace === 'function') { try { const ff = new FontFace(f.family, `url(${this.asset(f.src)})`, { weight: String(f.weight || 400) }); document.fonts.add(ff); return ff.load().catch(() => {}); } catch (e) { /* fall back to the CSS font */ } }
        return document.fonts.load(`${+f.weight || 400} 40px "${f.family}"`).catch(() => {});
      })).then(() => this.requestRender());
      this._textStepLabel = tog && tog.closest('.ls-label') ? tog.closest('.ls-label').firstChild.textContent : '';
      r.querySelectorAll('.ls-name, .ls-sub').forEach((i) => i.addEventListener('input', () => { if (this.isTextOnly()) this.showError(''); }));
      const note = $(r, '.ls-note span'); this._noteText = note ? note.textContent : '';
    }
    const textStep = tog && tog.closest('.ls-step');
    steps.forEach((s) => { if (s !== textStep && !s.matches('.ls-step--font, .ls-step--thread')) s.style.display = on ? 'none' : ''; });
    r.querySelectorAll('.ls-step--font, .ls-step--thread').forEach((s) => { s.style.display = on ? '' : 'none'; });
    if (tog) {
      tog.style.display = on ? 'none' : '';
      const lab = tog.closest('.ls-label'); if (lab && lab.firstChild) lab.firstChild.textContent = on ? '1. Name to stitch ' : this._textStepLabel;
      const err = $(r, '.ls-error'); if (on && err && textStep && !textStep.contains(err)) textStep.appendChild(err);   // the photo step is hidden: errors show here
    }
    if (on && !this.state.showText) this.setShowText(true);
    if (on && this.touch && !this._viewChosen && this.state.view !== 'close' && this.view && this.view.closeUp) this.toggleView(true);   // phones: the name readable from the start
    const sub = $(r, '.ls-sub'), sf = sub && sub.closest('.ls-field'); if (sf) sf.style.display = on && !(this.sel && this.sel.line) ? 'none' : '';
    if (on && sub && !(this.sel && this.sel.line) && this.state.subtitle) { sub.value = ''; this.state.subtitle = ''; }
    const note = $(r, '.ls-note span'); if (note) note.textContent = on ? 'We check every name by hand before stitching. What you see is what we stitch.' : this._noteText;
    const fc = this.fontChoice(), tc = this.threadChoice();
    r.querySelectorAll('[data-font]').forEach((b) => b.setAttribute('aria-pressed', String(!!fc && b.dataset.font === fc.key)));
    r.querySelectorAll('[data-thread]').forEach((b) => b.setAttribute('aria-pressed', String(!!tc && b.dataset.thread === tc.key)));
    const fn = $(r, '.ls-step--font .ls-pick-name'), tn = $(r, '.ls-step--thread .ls-pick-name');
    if (fn) fn.textContent = fc ? fc.label : ''; if (tn) tn.textContent = tc ? tc.label : '';
  }
  async fontsReadyText() {
    const specs = this.textSpecs(), want = specs.map((t) => `${t.weight} 100px "${t.family}"`);
    const ok = () => want.every((f) => document.fonts.check(f, 'A'));
    for (let i = 0; i < 3 && !ok(); i++) {
      await Promise.all(specs.map((t) => document.fonts.load(`${t.weight} 100px "${t.family}"`, t.text || 'A'))).catch(() => {});
      if (!ok()) await new Promise((r) => setTimeout(r, 700));
    }
    return ok();
  }
  /* Thread on the product: flat text (stitch = false: the staff print file) or the embroidery look for the preview:
     fine satin-stitch lines + noise, a tiny darker edge and a small raised shadow. No fake 3D. */
  drawTextOnlyDesign(ctx, w, h, { only = null, stitch = true } = {}) {
    if (!this.state.showText || only === 'photo') return;
    const PW = this.PW, PH = this.PH, th = this.threadChoice(), ink = (th && th.hex) || (this.sel && this.sel.ink) || (this.cfg && this.cfg.ink) || INK;
    if (!stitch) { ctx.save(); ctx.scale(w / PW, h / PH); this.drawTextBoxes(ctx, { ink }); ctx.restore(); return; }
    const dev = ctx.getTransform ? Math.abs(ctx.getTransform().a) : 1, W = Math.max(1, Math.ceil(w)), H = Math.max(1, Math.ceil(h));
    const [O, o] = this.scratch('thread', W, H); o.scale(w / PW, h / PH); this.drawTextBoxes(o, { ink });
    const pxmm = (w / PW) * ((this.sel && this.sel.dpi) || 300) / 25.4, sp = clamp(0.42 * pxmm, 1.3, 5);
    const key = ink + '|' + sp.toFixed(2);
    if (!this._stitch || this._stitch.key !== key) {          // tileable 45° satin lines with a little noise
      const n = 8, T = Math.max(8, Math.round(sp * Math.SQRT2 * n)), per = T / n, C = makeCanvas(T, T), x = C.getContext('2d', { willReadFrequently: true });
      x.lineWidth = Math.max(0.6, per * 0.32);
      for (let i = -n; i <= 2 * n; i++) { const c0 = i * per; x.strokeStyle = mixRGB(ink, 0.38, [255, 255, 255], 0.55); x.beginPath(); x.moveTo(c0, 0); x.lineTo(c0 + T, T); x.stroke();
        x.strokeStyle = mixRGB(ink, 0.35, [0, 0, 0], 0.35); x.beginPath(); x.moveTo(c0 + per * 0.5, 0); x.lineTo(c0 + per * 0.5 + T, T); x.stroke(); }
      const id = x.getImageData(0, 0, T, T), d = id.data; let seed = 7;
      for (let i = 0; i < d.length; i += 4) { seed = (seed * 16807) % 2147483647; const nz = (seed / 2147483647 - 0.5) * 40; d[i] = clamp(d[i] + nz, 0, 255); d[i + 1] = clamp(d[i + 1] + nz, 0, 255); d[i + 2] = clamp(d[i + 2] + nz, 0, 255); d[i + 3] = Math.max(d[i + 3], 18); }
      x.putImageData(id, 0, 0); this._stitch = { key, C };
    }
    o.setTransform(1, 0, 0, 1, 0, 0); o.globalCompositeOperation = 'source-atop';
    const pat = o.createPattern(this._stitch.C, 'repeat'); o.fillStyle = pat; o.fillRect(0, 0, W, H);
    o.globalCompositeOperation = 'source-over';
    const e = Math.max(0.6, 0.2 * pxmm), [Ed, ed] = this.scratch('threadEdge', W, H);    // outer outline only (no seams where script letters overlap)
    ed.drawImage(O, 0, 0); ed.globalCompositeOperation = 'destination-in';
    for (const [dx, dy] of [[e, 0], [-e, 0], [0, e], [0, -e]]) ed.drawImage(O, dx, dy);
    const [Rn, rn] = this.scratch('threadRing', W, H); rn.drawImage(O, 0, 0); rn.globalCompositeOperation = 'destination-out'; rn.drawImage(Ed, 0, 0);
    rn.globalCompositeOperation = 'source-in'; rn.fillStyle = mixRGB(ink, 0.45, [0, 0, 0], 0.7); rn.fillRect(0, 0, W, H);
    o.drawImage(Rn, 0, 0);
    ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.34)'; ctx.shadowBlur = Math.max(1, 0.5 * pxmm) * dev; ctx.shadowOffsetX = 0.15 * pxmm * dev; ctx.shadowOffsetY = 0.3 * pxmm * dev;
    ctx.drawImage(O, 0, 0, W, H, 0, 0, W, H); ctx.restore();
  }
  /* Text-only preview: like renderOverlay, but the shade multiplies the thread only (the layer is transparent around it). */
  renderTextOnly(c, size, vw) {
    const ctx = c.getContext('2d'); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, size, size); ctx.imageSmoothingQuality = 'high';
    const v = this.view, base = v && ((vw.s < 1 && v.srcHD && this.img[v.srcHD]) || this.img[v.src]);
    if (!v || !base) { this.renderFlat(ctx, size); return; }
    const nw = base.naturalWidth, nh = base.naturalHeight;
    ctx.drawImage(base, vw.x * nw, vw.y * nh, vw.s * nw, vw.s * nh, 0, 0, size, size);
    const m = this.viewMesh(v), [x0, y0, x1, y1] = m.region, Pc = m.P.map((p) => [(p[0] - vw.x) / vw.s * size, (p[1] - vw.y) / vw.s * size]);
    const xs = Pc.map((p) => p[0]), ys = Pc.map((p) => p[1]), bw = Math.max(...xs) - Math.min(...xs), bh = Math.max(...ys) - Math.min(...ys);
    const rw = (x1 - x0) * this.PW, rh = (y1 - y0) * this.PH, s = Math.min(1600 / Math.max(rw, rh), Math.max(bw, bh) * 1.15 / Math.max(rw, rh));
    const lw = Math.max(2, Math.round(rw * s)), lh = Math.max(2, Math.round(rh * s));
    const layer = this._layer; if (layer.width !== lw || layer.height !== lh) { layer.width = lw; layer.height = lh; }
    const lctx = layer.getContext('2d'); lctx.setTransform(1, 0, 0, 1, 0, 0); lctx.clearRect(0, 0, lw, lh); lctx.imageSmoothingQuality = 'high';
    lctx.translate(-x0 * this.PW * s, -y0 * this.PH * s); this.drawDesign(lctx, this.PW * s, this.PH * s); lctx.setTransform(1, 0, 0, 1, 0, 0);
    const [T, t] = this.scratch('txtwarp', size, size); t.imageSmoothingQuality = 'high';
    const { cols, rows } = m, Lp = (c2, r2) => [c2 / cols * lw, r2 / rows * lh];
    for (let r = 0; r < rows; r++) for (let q = 0; q < cols; q++) {
      const i0 = r * (cols + 1) + q, i1 = i0 + 1, i2 = i0 + cols + 1, i3 = i2 + 1;
      drawTri(t, layer, Lp(q, r), Lp(q + 1, r), Lp(q, r + 1), Pc[i0], Pc[i1], Pc[i2]);
      drawTri(t, layer, Lp(q + 1, r), Lp(q + 1, r + 1), Lp(q, r + 1), Pc[i1], Pc[i3], Pc[i2]);
    }
    const prep = this.prep[this.sel.view] || {};
    if (prep.shade) {
      const [A, a] = this.scratch('txtalpha', size, size); a.drawImage(T, 0, 0);
      t.globalCompositeOperation = 'multiply'; t.drawImage(prep.shade, vw.x * prep.shade.width, vw.y * prep.shade.height, vw.s * prep.shade.width, vw.s * prep.shade.height, 0, 0, size, size);
      t.globalCompositeOperation = 'destination-in'; t.drawImage(A, 0, 0);
    }
    if (prep.mask) { t.globalCompositeOperation = 'destination-in'; t.drawImage(prep.mask, vw.x * prep.mask.width, vw.y * prep.mask.height, vw.s * prep.mask.width, vw.s * prep.mask.height, 0, 0, size, size); }
    t.globalCompositeOperation = 'source-over';
    ctx.drawImage(T, 0, 0);
  }
  async _exportTextPrint(maxBytes = Infinity) {
    if (!(await this.fontsReady()) && this.needsFonts()) throw new Error('font-not-loaded');
    const PW = this.PW, PH = this.PH, cap = Math.min(1, Math.sqrt(MAX_MP / (PW * PH))); let blob = null;
    for (const k of [1, 0.85, 0.7]) {
      const w = Math.round(PW * cap * k), h = Math.round(PH * cap * k), c = makeCanvas(w, h), ctx = c.getContext('2d');
      this.drawDesign(ctx, w, h, { placeholder: false, cut: false });
      blob = await canvasToBlob(c, 'image/png'); this._printPx = [w, h]; c.width = c.height = 0;
      if (blob.size <= maxBytes) break;
    }
    return blob;
  }

  /* ---------- Printway: low-resolution guard + the order spec ---------- */
  effectiveDpi() {                 // photo pixels per printed inch, as placed now
    const geo = this.photoGeom(); if (!geo || !geo.dw) return null;
    return geo.src.width * ((this.sel && this.sel.dpi) || 300) / geo.dw;
  }
  lowResLevel() {
    const st = this.state; if (!this.pw || !st.photo || st.sample) return 'ok';
    const d = this.effectiveDpi(); if (d == null) return 'ok';
    const block = this.cfg.blockDpi != null ? this.cfg.blockDpi : 90, warn = this.cfg.minDpi != null ? this.cfg.minDpi : 150;
    return d < block ? 'block' : d < warn ? 'warn' : 'ok';
  }
  lowResText(level) {
    return level === 'block' ? 'This photo is too small for this size and would print blurry. Please upload a larger original photo.'
      : 'This photo is small for this size, it may print soft. Try a larger original photo.';
  }
  lowResHTML() {
    if (!this.pw) return '';
    if (typeof document !== 'undefined' && !document.getElementById('ls-lowres-css')) {
      const css = document.createElement('style'); css.id = 'ls-lowres-css';
      css.textContent = '.ls-lowres{position:absolute;left:10px;right:10px;bottom:10px;z-index:3;margin:0;padding:8px 12px;border-radius:12px;background:rgba(255,246,228,.97);border:1px solid #e3b25e;color:#5a3a06;font-size:12.5px;line-height:1.4;box-shadow:0 4px 14px rgba(43,20,25,.12);pointer-events:none}'
        + '.ls-lowres[hidden]{display:none}.ls-lowres.is-block{background:rgba(253,234,236,.97);border-color:#d58a97;color:#7a1f2e}.ls-stage.is-lowres .ls-hint{bottom:64px}';
      document.head.appendChild(css);
    }
    return '<p class="ls-lowres" role="status" hidden></p>';
  }
  syncLowRes() {
    const el = this.stage && $(this.stage, '.ls-lowres'); if (!el) return;
    const lv = this.lowResLevel(); if (lv === this._lowSig) return; this._lowSig = lv;
    el.hidden = lv === 'ok'; el.textContent = lv === 'ok' ? '' : this.lowResText(lv);
    el.classList.toggle('is-block', lv === 'block'); this.stage.classList.toggle('is-lowres', lv !== 'ok');
  }
  syncShapeUI() {
    const r = this.root; if (!r) return;
    const sh = this.shapeSpec(), b = $(r, '.ls-bg .ls-toggle-text b'); if (b) b.textContent = sh ? 'Cut to shape' : 'Remove background';
    if (sh && !this.state.photo) $(r, '.ls-bg-sub').textContent = 'Upload a photo: we cut around it';
    const tog = $(r, '.ls-text-toggle'), step = tog && tog.closest('.ls-step'), none = !!(sh && this.shapeParams().text === 'none');
    if (step) step.style.display = none ? 'none' : '';
  }
  printwaySpec(spec) {
    const g = this.sel, cfg = this.cfg, sh = this.cfgShape && this.shapeSpec(), txt = this.cfgText && this.isTextOnly();
    spec.supplier = 'printway'; spec.bp = null; spec.pp = null;
    spec.format = sh ? 'printway-cutmask' : txt ? 'text-only' : 'flat';
    spec.size = (g && g.size) || (g && g.px ? +(g.px[0] / (g.dpi || 300)).toFixed(2) + 'in' : null);
    const Lp = sh ? this.pwLayout() : null;
    spec.target_px = sh ? Lp.px : [this.PW, this.PH];
    if (sh) { spec.cells = Lp.cells; spec.back = Lp.cells.back ? Lp.back : null; }
    spec.file_px = this._printPx || null;
    if (cfg.printway || (g && g.printway)) spec.printway = { ...(cfg.printway || {}), ...((g && g.printway) || {}) };
    if (sh) {
      const SG = this.shapeGeom(), P = this.shapeParams(), PW = this.PW, PH = this.PH, f = (v, d) => +(v / d).toFixed(5);
      spec.shape = { material: P.material, edge: P.edge, base_ink: P.baseInk, border_px: Math.round(P.b), bleed_px: Math.round(P.bleed), outline: this.isCut() ? 'cutout' : 'rounded-rect',
        hole: SG.hole ? { x: f(SG.hole.x, PW), y: f(SG.hole.y, PH), d_px: Math.round(SG.hole.r * 2) } : null,
        bbox: [f(SG.bbox.x0, PW), f(SG.bbox.y0, PH), f(SG.bbox.x1, PW), f(SG.bbox.y1, PH)] };
    }
    if (txt) {
      const fc = this.fontChoice(), tc = this.threadChoice();
      spec.font = fc ? { key: fc.key, label: fc.label, printway: fc.pw || fc.label, family: fc.family, weight: fc.weight || 400, exact: !!fc.src } : null;
      spec.thread = tc ? { key: tc.key, label: tc.label, printway: tc.pw || tc.label, hex: tc.hex } : null;
    }
    if (this.state.photo) spec.dpi = Math.round(this.effectiveDpi() || 0);
    return spec;
  }

  /* --------- outputs --------- */
  async fontsReady() {
    if (!document.fonts) return true;
    if (this.cfgText && this.isTextOnly()) return this.fontsReadyText();
    const T = this.shownText(), ok = () => document.fonts.check('400 100px "Pinyon Script"', 'A') && document.fonts.check('600 100px Montserrat', 'A')
      && [...document.fonts].some((f) => f.family.replace(/["']/g, '') === 'Pinyon Script' && f.status === 'loaded');
    for (let i = 0; i < 3 && !ok(); i++) {
      await Promise.all([document.fonts.load('400 100px "Pinyon Script"', T.name || 'A'),
        document.fonts.load('600 100px Montserrat', T.line || 'A')]).catch(() => {});
      if (!ok()) await new Promise((r) => setTimeout(r, 700));
    }
    return ok();
  }
  needsFonts() { const T = this.shownText(); return this.state.showText && !!(T.name || T.line); }
  /* Flat print PNG of the whole area, capped at ~16 MP (iOS canvas limit) and stepped down under maxBytes.
     Overlay products: this is the staff/manual copy; Printify gets the full-resolution layers (see _Spec). */
  exportPrint(maxBytes = Infinity) {
    return this.printing(() => (this.cfgShape && this.shapeSpec() ? this._exportShapePrint(maxBytes) : this.cfgText && this.isTextOnly() ? this._exportTextPrint(maxBytes) : this._exportPrint(maxBytes)));
  }
  async _exportPrint(maxBytes = Infinity) {
    if (!(await this.fontsReady()) && this.needsFonts()) throw new Error('font-not-loaded');
    const PW = this.PW, PH = this.PH; const cap = Math.min(1, Math.sqrt(MAX_MP / (PW * PH)));
    let overlayImg = null;
    if (this.mode === 'overlay' && this.sel.overlay && cap > 0.55) {       // sharper overlay for the flat copy when the web one is small
      try { overlayImg = await loadImage(this.asset(this.sel.overlay)); } catch { overlayImg = null; }
    }
    let blob = null;
    const steps = this.mode === 'overlay' ? [1, 0.85, 0.7] : (this.state.showText ? [1, 0.88, 0.8] : [0.8, 0.7]);
    for (const k of steps) {
      const w = Math.round(PW * cap * k), h = Math.round(PH * cap * k);
      const c = makeCanvas(w, h); const ctx = c.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      this.drawDesign(ctx, w, h, { placeholder: false, overlayImg });
      blob = await canvasToBlob(c, 'image/png');
      if (this.pw) this._printPx = [w, h];
      c.width = c.height = 0;                     // free GPU/canvas memory right away (mobile Safari)
      if (blob.size <= maxBytes) break;
    }
    return blob;
  }
  /* Overlay products: the photo window (bbox) at the photo's own resolution, and the text at print resolution.
     Printify stacks them with the hosted full-resolution overlay. Returns blobs + Printify placements. */
  exportLayers(maxBytes = Infinity) { return this.printing(() => this._exportLayers(maxBytes)); }
  async _exportLayers(maxBytes = Infinity) {
    if (this.mode !== 'overlay') return null;
    if (this.pw || (this.cfgText && this.isTextOnly())) return null;   // Printway takes the one flat / cut-mask / text print file, no Printify layers
    const dz = this.design(); if (dz && (dz.layout || isPat(dz))) return null;      // designs print as one flat file
    if (!(await this.fontsReady()) && this.needsFonts()) throw new Error('font-not-loaded');
    const PW = this.PW, PH = this.PH, F = this.frame(), geo = this.photoGeom();
    const out = {};
    // photo layer: the window bbox at print resolution (capped at ~16 MP), as JPEG. The overlay covers everything
    // outside the window's shape, so the corners are simply filled; Printify then never sees a low-dpi image.
    let k = Math.min(1, Math.sqrt(MAX_MP / (F.w * F.h)));
    for (let i = 0; i < 3; i++) {
      const w = Math.max(1, Math.round(F.w * k)), h = Math.max(1, Math.round(F.h * k));
      const c = makeCanvas(w, h); const ctx = c.getContext('2d'); ctx.imageSmoothingQuality = 'high';
      ctx.scale(w / F.w, h / F.h); ctx.translate(-F.x, -F.y);
      ctx.fillStyle = this.sel.windowFill || this.sel.bg || '#ffffff'; ctx.fillRect(F.x, F.y, F.w, F.h);
      ctx.save(); ctx.beginPath(); ctx.rect(F.x, F.y, F.w, F.h); ctx.clip();
      if (geo) ctx.drawImage(geo.src, geo.x, geo.y, geo.dw, geo.dh);
      ctx.restore();
      out.photo = await canvasToBlob(c, 'image/jpeg', 0.92); c.width = c.height = 0;
      if (out.photo.size <= maxBytes) break; k *= 0.8;
    }
    out.photoPlace = { x: (F.x + F.w / 2) / PW, y: (F.y + F.h / 2) / PH, scale: F.w / PW, angle: 0 };
    // text layer: union of the text boxes, transparent
    const specs = this.state.showText ? this.textSpecs() : [];
    if (specs.length) {
      const bx0 = Math.min(...specs.map((t) => t.box.x)) * PW, by0 = Math.min(...specs.map((t) => t.box.y)) * PH;
      const bx1 = Math.max(...specs.map((t) => t.box.x + t.box.w)) * PW, by1 = Math.max(...specs.map((t) => t.box.y + t.box.h)) * PH;
      const tw = bx1 - bx0, th = by1 - by0; let tk = Math.min(1, Math.sqrt(MAX_MP / (tw * th)));
      for (let i = 0; i < 3; i++) {
        const w = Math.max(1, Math.round(tw * tk)), h = Math.max(1, Math.round(th * tk));
        const c = makeCanvas(w, h); const ctx = c.getContext('2d'); ctx.imageSmoothingQuality = 'high';
        ctx.scale(w / tw, h / th); ctx.translate(-bx0, -by0); this.drawTextBoxes(ctx);
        out.text = await canvasToBlob(c, 'image/png'); c.width = c.height = 0;
        if (out.text.size <= maxBytes) break; tk *= 0.8;
      }
      out.textPlace = { x: (bx0 + tw / 2) / PW, y: (by0 + th / 2) / PH, scale: tw / PW, angle: 0 };
    }
    return out;
  }
  async exportPreview(px = 1200) {        // the whole product, whatever the on-screen view
    await this.fontsReady();
    const out = makeCanvas(px, px);
    if (this.mode === 'overlay') { const keep = this._layer; this._layer = makeCanvas(1, 1); this.renderOverlay(out, px, this.viewWindow('full')); this._layer = keep; }
    else this.renderTo(out, px, this.viewWindow('full'));
    const blob = await canvasToBlob(out, 'image/jpeg', 0.88);
    out.width = out.height = 0;
    return blob;
  }
  async exportOriginal() {      // downscaled JPEG of the uploaded photo (never the raw camera file)
    const st = this.state; if (!st.photo) return null;
    return canvasToBlob(st.photo, 'image/jpeg', 0.9);
  }
  /* Everything the order needs: files + the Printify spec (how to stack them). */
  exportFiles(maxBytes) { return this.printing(() => this._exportFiles(maxBytes)); }
  async _exportFiles(maxBytes) {
    const print = await this.exportPrint(maxBytes);
    const layers = await this.exportLayers(maxBytes);
    const preview = await this.exportPreview(), original = await this.exportOriginal();
    const stamp = Date.now().toString(36); const id = (this.cfg && this.cfg.id) || 'crew';
    const files = {
      print: new File([print], `lune-${id}-print-${stamp}.png`, { type: 'image/png' }),
      preview: new File([preview], `lune-${id}-preview-${stamp}.jpg`, { type: 'image/jpeg' }),
    };
    if (original) files.original = new File([original], `lune-${id}-photo-${stamp}.jpg`, { type: 'image/jpeg' });
    if (layers) {
      files.photoLayer = new File([layers.photo], `lune-${id}-photo-layer-${stamp}.jpg`, { type: 'image/jpeg' });
      if (layers.text) files.textLayer = new File([layers.text], `lune-${id}-text-layer-${stamp}.png`, { type: 'image/png' });
    }
    files.spec = this.printSpec(layers);
    return files;
  }
  printSpec(layers) {
    const cfg = this.cfg, g = this.sel;
    const spec = { v: 2, cfg: cfg ? cfg.id : 'crew', rev: cfg ? cfg.rev : 0, group: g ? g.key : null,
      bp: cfg && cfg.printify ? cfg.printify.bp : 49, pp: cfg && cfg.printify ? cfg.printify.pp : 99,
      position: (g && g.position) || 'front', px: [this.PW, this.PH], layers: [], statics: {}, flat: { prop: '_Print file' },
      print_details: (cfg && cfg.printDetails) || null, design: this.design() ? this.design().key : null };
    if (this.mode === 'overlay' && layers) {
      spec.layers.push({ prop: '_Photo layer', ...layers.photoPlace });
      if (g.overlay) spec.layers.push({ static: FILES + g.overlay, x: 0.5, y: 0.5, scale: 1, angle: 0 });
      if (layers.text) spec.layers.push({ prop: '_Text layer', ...layers.textPlace });
    } else spec.layers.push({ prop: '_Print file', x: 0.5, y: 0.5, scale: 1, angle: 0 });
    if (g && g.statics) for (const [pos, st] of Object.entries(g.statics)) spec.statics[pos] = FILES + (st.src || st);
    if (this.pw || (this.cfgText && this.isTextOnly())) this.printwaySpec(spec);
    return spec;
  }
  /* Shopify (Horizon): the studio owns add-to-cart for this product. Every add path (main button, sticky bar,
     Enter in the form) is stopped at window capture, before any theme handler, so the theme can never add a bare
     line, play its 'Added' animation for a refused tap, or queue the add during a variant change. We render the
     files, post them with the line to /cart/add ourselves and tell the theme's cart (drawer, count) it changed. */
  attachToProductForm(form, { requirePhoto = true, onBlocked, maxBytes = 19.5 * 1024 * 1024 } = {}) {
    const pfc = form.closest('product-form-component') || form;
    const section = form.closest('.shopify-section') || document;
    const scope = pfc.closest('.shopify-section, dialog, product-card') || document;   // where Horizon's form listens
    const ready = $(this.root, '.ls-ready');
    const BUSY = 'Adding your design…';
    let busy = false, rev = 0, variantPending = false, lastFiles = null;
    this.on(() => { rev++; ready.classList.remove('is-on'); });   // any change means the next add renders fresh files
    scope.addEventListener('variant:selected', () => { variantPending = true; });
    scope.addEventListener('variant:update', () => { variantPending = false; });
    window.addEventListener('pageshow', (e) => { if (e.persisted) { busy = false; variantPending = false; } });

    const isAddButton = (el) => {
      const b = el instanceof Element ? el.closest('button, input[type="submit"]') : null;
      if (!b) return false;
      if (b.form === form && (b.type === 'submit')) return true;
      return !!(b.closest('sticky-add-to-cart') && section.contains(b));
    };
    const mainBtns = () => [...form.querySelectorAll('[type="submit"]')];
    const sticky = () => section.querySelector('sticky-add-to-cart [ref="addToCartButton"], sticky-add-to-cart button');
    const say = (msg, where) => {
      this.showError(msg);
      if (!msg) return;
      const pinned = this.root.contains(this.stage) && getComputedStyle(this.stage).position === 'sticky';
      ($(this.root, where || '.ls-step') || this.root).scrollIntoView({ behavior: 'smooth', block: pinned ? 'end' : 'center' });
    };
    const waitVariant = () => new Promise((resolve, reject) => {
      if (!variantPending) return resolve();
      const t0 = performance.now();
      const tick = () => { if (!variantPending) resolve(); else if (performance.now() - t0 > 8000) { variantPending = false; reject(new Error('variant-timeout')); } else setTimeout(tick, 100); };
      tick();
    });
    const setBusy = (on) => {
      for (const b of mainBtns()) {
        if (on) { b.dataset.lsLabel = b.innerHTML; b.disabled = true; b.textContent = BUSY; }
        else if (b.textContent === BUSY) { b.disabled = false; if (b.dataset.lsLabel) b.innerHTML = b.dataset.lsLabel; }
      }
      const sb = sticky(); if (sb) { if (on) { sb.dataset.lsWas = sb.disabled ? '1' : ''; sb.disabled = true; } else if (sb.dataset.lsWas !== '1') sb.disabled = false; }
      this.root.classList.toggle('is-adding', on);
    };

    const add = async () => {
      if (busy) return;
      this.flushEmit();                             // a change in the last 250 ms must not ship old files
      const st = this.state;
      const textOnly = this.cfgText && this.isTextOnly();
      if (textOnly && !(st.name || '').trim() && !(st.subtitle || '').trim()) { say('Please type the name you want stitched.', '.ls-error'); if (onBlocked) onBlocked(); return; }
      if (requirePhoto && !textOnly && (!st.photo || st.sample)) {
        say(st.photo ? 'That is our sample photo. Please upload your own photo to order.' : 'Please upload your photo first.');
        if (onBlocked) onBlocked(); return;
      }
      if (this.pw && st.photo && !st.sample && this.lowResLevel() === 'block') { say(this.lowResText('block'), '.ls-error'); if (onBlocked) onBlocked(); return; }
      if (this.pw && st.removing) { say('One moment: we are still cutting out your photo.', '.ls-error'); return; }
      if (typeof FormData === 'undefined' || typeof fetch === 'undefined') { say('Please update your browser to add your design.'); return; }
      busy = true; setBusy(true); this.showError(''); ready.classList.remove('is-on');
      try {
        await waitVariant();
        await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));   // let the busy label paint first
        const rev0 = rev;
        const meta = { text: st.showText, name: (st.name || '').trim(), subtitle: (st.subtitle || '').trim(), bg: st.useCut && st.cut ? 'Removed' : 'Original photo', layout: JSON.stringify(this.layout()) };
        const files = await this.exportFiles(maxBytes);
        this.flushEmit();
        const now = this.state;
        if (rev !== rev0 || meta.layout !== JSON.stringify(this.layout()) || meta.name !== (now.name || '').trim() || meta.subtitle !== (now.subtitle || '').trim()) throw new Error('changed-while-preparing');
        if ([files.print, files.preview, files.original].find((f) => f && f.size > maxBytes)) throw new Error('file-too-large');

        const fd = new FormData(form);              // id, quantity, selling plan… exactly as the theme would send
        const id = fd.get('id'); if (!id) throw new Error('no-variant');
        if (!fd.get('quantity')) fd.set('quantity', '1');
        // only what the customer typed; an empty field is left out (both empty: "Text: No text", like text off)
        if (this.cfgText && this.isTextOnly()) {           // text only: Name, Line, Font, Thread (labels the shopper picked)
          ['Text', 'Name', 'Line under name', 'Line', 'Background'].forEach((k) => fd.delete(`properties[${k}]`));
          if (meta.name) fd.set('properties[Name]', meta.name);
          if (meta.subtitle) fd.set('properties[Line]', meta.subtitle.toUpperCase());
          const fc = this.fontChoice(), th = this.threadChoice();
          if (fc) fd.set('properties[Font]', fc.label || fc.key); if (th) fd.set('properties[Thread]', th.label || th.key);
          if (fc && fc.pw) fd.set('properties[_Printway font]', fc.pw); if (th && th.pw) fd.set('properties[_Printway thread]', th.pw);
        } else if (meta.text && (meta.name || meta.subtitle)) {
          fd.delete('properties[Text]');
          if (meta.name) fd.set('properties[Name]', meta.name); else fd.delete('properties[Name]');
          if (meta.subtitle) fd.set('properties[Line under name]', meta.subtitle); else fd.delete('properties[Line under name]');
        } else { fd.delete('properties[Name]'); fd.delete('properties[Line under name]'); fd.set('properties[Text]', (meta.text || this.mode === 'overlay') ? 'No text' : 'No text (full-size photo)'); }
        if (!(this.cfgText && this.isTextOnly())) fd.set('properties[Background]', meta.bg);
        { const dd = this.design(); if (dd) fd.set('properties[Design]', (dd.label || dd.key) + (dd.custom ? ' ' + (this.state.customColor || dd.color || '') : dd.color ? ' ' + dd.color : '')); else fd.delete('properties[Design]'); }
        fd.set('properties[_Print file]', files.print);
        if (files.photoLayer) fd.set('properties[_Photo layer]', files.photoLayer);
        if (files.textLayer) fd.set('properties[_Text layer]', files.textLayer);
        fd.set('properties[_Preview]', files.preview);
        if (files.original) fd.set('properties[_Photo]', files.original);
        fd.set('properties[_Layout]', meta.layout);
        fd.set('properties[_Spec]', JSON.stringify(files.spec));
        fd.set('properties[_Design]', 'Lune Studio 4 · ' + (this.cfg ? this.cfg.id + '@' + this.cfg.rev : 'crew'));
        const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
        if (sections.length) fd.set('sections', [...new Set(sections)].join(','));
        const url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
        const res = await fetch(url, { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' } });
        let data = null; try { data = await res.json(); } catch {}
        if (!res.ok || !data || data.status) { const e = new Error('cart-error'); e.userMessage = data && (data.description || data.message); throw e; }
        lastFiles = files;
        let cart; try { cart = await (await fetch('/cart.js')).json(); } catch {}
        pfc.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: String(id), data: { source: 'product-form-component', itemCount: Number(fd.get('quantity')) || 1, productId: pfc.dataset && pfc.dataset.productId, sections: data.sections } } }));
        ready.classList.add('is-on');
      } catch (err) {
        if (err.message === 'changed-while-preparing') say('Your design changed while we were saving it. Tap the button again to add it.', '.ls-error');
        else if (err.message === 'variant-timeout') say('Please choose your size again, then tap Add to cart.', '.ls-error');
        else if (err.message === 'font-not-loaded') say('The name font did not load. Please check your connection and tap Add to cart again.', '.ls-error');
        else if (err.message === 'cart-error') say(err.userMessage || 'Sorry, we could not add this to your cart. Please try again.', '.ls-error');
        else { console.error(err); say('Sorry, we could not prepare your design. Please try again, or use a smaller photo.', '.ls-error'); }
      } finally {
        setBusy(false); busy = false;
      }
    };

    // Window capture runs before Horizon's document-level click/submit delegation.
    window.addEventListener('click', (e) => {
      if (!isAddButton(e.target)) return;
      e.preventDefault(); e.stopImmediatePropagation();
      add();
    }, true);
    window.addEventListener('submit', (e) => {
      if (e.target !== form) return;
      e.preventDefault(); e.stopImmediatePropagation();
      add();
    }, true);
    this.lastFiles = () => lastFiles;
    { const c = this.o.confirm !== false && $(this.root, '.ls-confirm'); if (c) { c.hidden = false; this.syncConfirm(); } }   // ls6: the "Printing: ..." line belongs next to Add to cart
    return this;
  }
}

export default LuneStudio;
if (typeof window !== 'undefined') window.LuneStudio = LuneStudio;
